import {
  Archive,
  Aperture,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  BookOpen,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Database,
  Download,
  Eye,
  FileBox,
  FolderSearch,
  GripVertical,
  History,
  Image as ImageIcon,
  Images,
  KeyRound,
  LoaderCircle,
  LocateFixed,
  LockKeyhole,
  Link,
  LogOut,
  MapPin,
  MapPinned,
  Menu,
  Music2,
  Navigation,
  Pencil,
  PenTool,
  RefreshCw,
  Save,
  Search,
  Settings2,
  ShieldCheck,
  Trash2,
  Upload,
  UserPlus,
  Users,
  Video,
  X,
} from "lucide-react";
import {
  type CSSProperties,
  Fragment,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  lazy,
  type ReactNode,
  type Ref,
  Suspense,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  type DragEndEvent,
  type DragStartEvent,
  type Modifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { ApiError, api } from "./api";
import { ActionFeedback, type FeedbackTone, useTransientNotice } from "./ActionFeedback";
import type { ImageWorkbenchLightboxProps } from "./ImageWorkbenchLightbox";
import type {
  AmapCandidate,
  AdminMaterial,
  AdminLocation,
  AdminCaseSummary,
  AdminUser,
  ArchiveOrderItem,
  CaseDetailData,
  CaseImage,
  CaseList,
  CaseSummary,
  Community,
  ClientConfig,
  EnterpriseDocument,
  EnterpriseDocumentDetail,
  MapLocation,
  MaterialCaseOption,
  MaterialEnterpriseOption,
  CaseMaterial,
  CaseAccessItem,
  CaseViewLogPage,
  CatalogStats,
  FloorplanFile,
  FloorplanKind,
  FloorplanLibraryData,
  SystemStatus,
  User,
} from "./types";

const DeferredImageWorkbenchLightbox = lazy(() => import("./ImageWorkbenchLightbox").then((module) => ({ default: module.ImageWorkbenchLightbox })));
const DeferredSocialHub = lazy(() => import("./MediaModules").then((module) => ({ default: module.SocialHub })));
const DeferredSocialManagement = lazy(() => import("./MediaModules").then((module) => ({ default: module.SocialManagement })));

function ImageWorkbenchLightbox(props: ImageWorkbenchLightboxProps) {
  return (
    <Suspense fallback={<div className="lightbox-loading" role="status"><LoaderCircle className="spin" aria-hidden="true" />正在打开大图</div>}>
      <DeferredImageWorkbenchLightbox {...props} />
    </Suspense>
  );
}

type View = "archive" | "materials" | "map" | "enterprise" | "floorplans" | "social" | "admin";
type AdminTab = "locations" | "cases" | "materials" | "enterprise" | "floorplans" | "social" | "users" | "views" | "system";
type ArchiveKind = "case" | "material";
const ARCHIVE_COPY = {
  case: {
    singular: "案例", group: "小区", index: "小区图鉴", count: "户", image: "效果图",
    room: "房号", area: "面积", style: "风格", information: "案例信息", attachments: "关联资料",
    emptyScene: "SPACE AESTHETICS", emptySceneSecond: "GALLERY",
    emptyHeadline: "见过万千空间，", emptyHeadlineSecond: "方懂家的尺度。",
  },
  material: {
    singular: "素材", group: "素材库", index: "素材图鉴", count: "种", image: "素材图",
    room: "素材名称", area: "规格", style: "标签", information: "素材信息", attachments: "关联资料",
    emptyScene: "MATERIAL", emptySceneSecond: "ARCHIVE",
    emptyHeadline: "洞见空间细节，", emptyHeadlineSecond: "回应生活所问。",
  },
} as const;
const STORE_MARK_PATH = "M 11.35 3.31 Q 12 2 12.65 3.31 L 14.47 7 Q 14.94 7.95 15.99 8.11 L 20.06 8.7 Q 21.51 8.91 20.46 9.93 L 17.52 12.8 Q 16.76 13.55 16.93 14.59 L 17.63 18.65 Q 17.88 20.09 16.58 19.41 L 12.94 17.49 Q 12 17 11.06 17.49 L 7.42 19.41 Q 6.12 20.09 6.37 18.65 L 7.07 14.59 Q 7.24 13.55 6.48 12.8 L 3.54 9.93 Q 2.49 8.91 3.94 8.7 L 8.01 8.11 Q 9.06 7.95 9.53 7 L 11.35 3.31 Z";

function StoreMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d={STORE_MARK_PATH} />
    </svg>
  );
}

type MapPoint = {
  id: number | string;
  community_id?: number | null;
  name: string;
  lng: number;
  lat: number;
  place_type: "community" | "store";
  case_count?: number;
};

const HAILAR_CENTER = [119.765, 49.211] as const;
const COMMUNITY_NAME_COLLATOR = new Intl.Collator("zh-CN-u-co-pinyin", {
  numeric: true,
  sensitivity: "base",
});
const HAILAR_FOCUS_RADIUS_KM = 60;
const BRAND_NAME = "易高·空间美学馆";
const BRAND_LOGO_SRC = "/yigao-logo.png";
const ARCHIVE_UPLOAD_MAX_FILES = 120;
const CASE_UPLOAD_MAX_FILE_BYTES = 160 * 1024 * 1024;
const CASE_UPLOAD_MAX_TOTAL_BYTES = 1024 * 1024 * 1024;

function archiveImageFileError(files: File[], archiveKind: "case" | "material" | "enterprise"): string {
  const subject = archiveKind === "case" ? "案例" : archiveKind === "material" ? "素材" : "企业资料";
  const imageLabel = archiveKind === "case" ? "效果图" : archiveKind === "material" ? "素材图" : "资料图片";
  if (!files.length) return `请先选择至少一张${imageLabel}。`;
  if (files.length > ARCHIVE_UPLOAD_MAX_FILES) return `单个${subject}最多上传 ${ARCHIVE_UPLOAD_MAX_FILES} 张${imageLabel}。`;
  const oversized = files.find((file) => file.size > CASE_UPLOAD_MAX_FILE_BYTES);
  if (oversized) return `“${oversized.name}”超过单张 160 MB 的上限。`;
  if (files.reduce((sum, file) => sum + file.size, 0) > CASE_UPLOAD_MAX_TOTAL_BYTES) return `单个${subject}的全部图片不能超过 1 GB。`;
  if (files.some((file) => !/\.(?:png|jpe?g|webp)$/i.test(file.name))) return `${subject}${imageLabel}支持 PNG、JPG、JPEG 和 WebP 格式。`;
  return "";
}

function enterpriseFileError(files: File[]): string {
  return archiveImageFileError(files, "enterprise");
}

function isInHailarFocusArea(point: Pick<MapPoint, "lng" | "lat">): boolean {
  const latitudeKm = (point.lat - HAILAR_CENTER[1]) * 111;
  const longitudeKm = (point.lng - HAILAR_CENTER[0]) * 111 * Math.cos((HAILAR_CENTER[1] * Math.PI) / 180);
  return Math.hypot(latitudeKm, longitudeKm) <= HAILAR_FOCUS_RADIUS_KM;
}

interface AMapMarkerLike {
  on: (event: string, handler: () => void) => void;
  setzIndex?: (value: number) => void;
}

interface AMapMapLike {
  add: (markers: AMapMarkerLike[]) => void;
  clearMap: () => void;
  destroy: () => void;
  on?: (event: string, handler: () => void) => void;
  off?: (event: string, handler: () => void) => void;
  resize?: () => void;
  setCenter: (position: [number, number]) => void;
  setZoom: (zoom: number) => void;
  setFitView: (
    overlays?: AMapMarkerLike[],
    immediately?: boolean,
    avoid?: [number, number, number, number],
    maxZoom?: number,
  ) => void;
}

interface AMapNamespace {
  Map: new (
    container: HTMLElement,
    options: Record<string, unknown>,
  ) => AMapMapLike;
  Marker: new (options: Record<string, unknown>) => AMapMarkerLike;
}

declare global {
  interface Window {
    AMap?: AMapNamespace;
    _AMapSecurityConfig?: { serviceHost: string };
  }
}

let amapLoadPromise: Promise<AMapNamespace> | null = null;

function messageFrom(error: unknown): string {
  return error instanceof Error ? error.message : "发生未知错误，请重试。";
}

function confirmDiscardLocationChanges(): boolean {
  return window.confirm(
    "当前地图点位有未保存的修改。\n\n选择“确定”放弃修改并离开；选择“取消”继续编辑。",
  );
}

function formatBytes(value: number): string {
  if (value >= 1024 ** 3) return `${(value / 1024 ** 3).toFixed(1)} GB`;
  if (value >= 1024 ** 2) return `${(value / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(value / 1024))} KB`;
}

function UploadProgress({ value }: { value: number }) {
  const percent = Math.max(0, Math.min(100, Math.round(value)));
  const processing = percent >= 100;
  const progressRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      progressRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      ref={progressRef}
      className="upload-progress"
      data-processing={processing}
      role="progressbar"
      aria-label={processing ? "文件上传完成，服务器处理中" : "文件上传进度"}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
    >
      <div>
        <span>{processing ? "文件已传完，服务器正在处理" : "正在传输文件"}</span>
        <strong>{percent}%</strong>
      </div>
      <span className="upload-progress-track" aria-hidden="true">
        <span style={{ transform: `scaleX(${percent / 100})` }} />
      </span>
    </div>
  );
}

const restrictDragToContainer: Modifier = ({ transform, draggingNodeRect, containerNodeRect }) => {
  if (!draggingNodeRect || !containerNodeRect) return transform;
  const x = Math.min(
    containerNodeRect.right - draggingNodeRect.right,
    Math.max(containerNodeRect.left - draggingNodeRect.left, transform.x),
  );
  const y = Math.min(
    containerNodeRect.bottom - draggingNodeRect.bottom,
    Math.max(containerNodeRect.top - draggingNodeRect.top, transform.y),
  );
  return { ...transform, x, y };
};

const restrictDragToVerticalContainer: Modifier[] = [
  ({ transform }) => ({ ...transform, x: 0 }),
  restrictDragToContainer,
];
const restrictDragToGrid: Modifier[] = [restrictDragToContainer];

const uploadFileIds = new WeakMap<File, string>();
let uploadFileSequence = 0;

function uploadFileId(file: File): string {
  const current = uploadFileIds.get(file);
  if (current) return current;
  uploadFileSequence += 1;
  const next = `upload-file-${uploadFileSequence}`;
  uploadFileIds.set(file, next);
  return next;
}

function naturalSortFiles(files: File[]): File[] {
  return [...files].sort((left, right) => COMMUNITY_NAME_COLLATOR.compare(left.name, right.name));
}

function SortableUploadImage({
  file,
  previewUrl,
  index,
  cover,
  disabled,
  onPreview,
  onSetCover,
}: {
  file: File;
  previewUrl: string;
  index: number;
  cover: boolean;
  disabled: boolean;
  onPreview: (trigger: HTMLButtonElement) => void;
  onSetCover?: () => void;
}) {
  const id = uploadFileId(file);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({
    id, disabled, transition: { duration: 180, easing: "cubic-bezier(.16, 1, .3, 1)" },
  });
  return (
    <article ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className={`upload-image-card${cover ? " cover" : ""}${isDragging ? " dragging" : ""}${isOver && !isDragging ? " drop-target" : ""}`}>
      <button className="upload-image-preview" type="button" onClick={(event) => onPreview(event.currentTarget)} aria-label={`预览第 ${index + 1} 张：${file.name}`}><img src={previewUrl} alt="" /><span><Eye aria-hidden="true" />预览</span></button>
      <div><span>第 {String(index + 1).padStart(2, "0")} 张</span><strong title={file.name}>{file.name}</strong><small>{formatBytes(file.size)}</small></div>
      <div className="upload-image-actions">{cover ? <span><Check aria-hidden="true" />封面</span> : onSetCover ? <button type="button" disabled={disabled} onClick={onSetCover}>设为封面</button> : null}<button type="button" disabled={disabled} {...attributes} {...listeners} aria-label={`拖动调整第 ${index + 1} 张：${file.name}`}><GripVertical aria-hidden="true" />拖动</button></div>
    </article>
  );
}

function UploadImageOrganizer({
  files,
  onChange,
  disabled,
  allowCover = true,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  disabled: boolean;
  allowCover?: boolean;
}) {
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [previews, setPreviews] = useState<Map<File, string>>(new Map());
  const organizerRef = useRef<HTMLElement>(null);
  const previousFileCountRef = useRef(0);
  const previewTriggerRef = useRef<HTMLButtonElement>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 140, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    const next = new Map(files.map((file) => [file, URL.createObjectURL(file)]));
    setPreviews(next);
    setPreviewIndex((value) => value === null || value < files.length ? value : null);
    const revealFrame = files.length > 0 && previousFileCountRef.current === 0
      ? window.requestAnimationFrame(() => organizerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }))
      : null;
    previousFileCountRef.current = files.length;
    return () => {
      if (revealFrame !== null) window.cancelAnimationFrame(revealFrame);
      next.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [files]);

  if (!files.length) return null;
  const previewFile = previewIndex === null ? null : files[previewIndex];
  function closePreview() {
    setPreviewIndex(null);
  }
  return (
    <section ref={organizerRef} className="upload-image-organizer" aria-label="上传前图片顺序">
      <header><div><strong>上传前检查</strong><span>{files.length} 张 · {allowCover ? "首张作为初始封面" : "仅调整待新增图片顺序"} · 拖到朱砂框位置</span></div><button className="secondary-action" type="button" disabled={disabled} onClick={() => onChange(naturalSortFiles(files))}><RefreshCw aria-hidden="true" />按文件名排序</button></header>
      <DndContext sensors={sensors} modifiers={restrictDragToGrid} collisionDetection={closestCenter} onDragEnd={(event) => { if (!event.over || event.active.id === event.over.id) return; const from = files.findIndex((file) => uploadFileId(file) === event.active.id); const to = files.findIndex((file) => uploadFileId(file) === event.over?.id); if (from >= 0 && to >= 0) onChange(arrayMove(files, from, to)); }}>
        <SortableContext items={files.map(uploadFileId)} strategy={rectSortingStrategy}><div className="upload-image-grid">{files.map((file, index) => <SortableUploadImage key={uploadFileId(file)} file={file} previewUrl={previews.get(file) ?? ""} index={index} cover={allowCover && index === 0} disabled={disabled} onPreview={(trigger) => { previewTriggerRef.current = trigger; setPreviewIndex(index); }} onSetCover={allowCover && index > 0 ? () => onChange(arrayMove(files, index, 0)) : undefined} />)}</div></SortableContext>
      </DndContext>
      {previewFile && <ImageWorkbenchLightbox src={previews.get(previewFile) ?? ""} fileName={previewFile.name} caption={`${previewFile.name} · ${(previewIndex ?? 0) + 1}/${files.length} · 上传前预览`} dialogLabel="上传图片大图预览" previousDisabled={previewIndex === 0} nextDisabled={previewIndex === files.length - 1} onPrevious={() => setPreviewIndex((value) => value === null ? null : Math.max(0, value - 1))} onNext={() => setPreviewIndex((value) => value === null ? null : Math.min(files.length - 1, value + 1))} onClose={closePreview} returnFocusRef={previewTriggerRef} />}
    </section>
  );
}

function ArchiveDeleteAction({
  label,
  imageCount,
  description,
  busy,
  onConfirm,
}: {
  label: string;
  imageCount: number;
  description?: string;
  busy: boolean;
  onConfirm: () => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  function closeConfirm() {
    if (busy) return;
    setConfirming(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  }
  useEffect(() => {
    if (!confirming) return;
    confirmRef.current?.focus();
    function keydown(event: KeyboardEvent) {
      if (event.key === "Escape" && !busy) {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeConfirm();
      }
    }
    window.addEventListener("keydown", keydown, true);
    return () => window.removeEventListener("keydown", keydown, true);
  }, [busy, confirming]);
  return (
    <>
      <button ref={triggerRef} className="danger-action danger-trigger" type="button" disabled={busy} onClick={() => setConfirming(true)}><Trash2 aria-hidden="true" />永久删除</button>
      {confirming && createPortal(<div className="archive-delete-dialog" role="alertdialog" aria-modal="true" aria-labelledby="archive-delete-title" aria-describedby="archive-delete-description"><div className="archive-delete-backdrop" onClick={closeConfirm} /><section><span className="case-delete-mark"><Trash2 aria-hidden="true" /></span><div><h2 id="archive-delete-title">确认永久删除“{label}”？</h2><p id="archive-delete-description">{description ?? `将永久删除资料记录及其 ${imageCount} 张原图、缩略图和预览缓存，操作无法恢复。`}</p></div><footer><button className="secondary-action" type="button" disabled={busy} onClick={closeConfirm}>取消</button><button ref={confirmRef} className="danger-action" type="button" disabled={busy} onClick={() => void onConfirm()}>{busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <Trash2 aria-hidden="true" />}{busy ? "正在永久删除" : "确认永久删除"}</button></footer></section></div>, document.body)}
    </>
  );
}

function formatArea(value: string | null): string {
  if (!value) return "未登记";
  return /(?:㎡|m²|平)$/i.test(value) ? value : `${value}㎡`;
}

function formatViewTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

function isResearchCase(item: Pick<CaseSummary, "source_folder" | "room">): boolean {
  return item.source_folder.includes("研发") || Boolean(item.room?.includes("研发"));
}

function caseDisplayTitle(
  item: Pick<CaseSummary, "source_folder" | "community_name" | "room" | "area" | "style">,
  archiveKind: ArchiveKind = "case",
): string {
  const parts = [item.community_name];
  if (item.room && !isResearchCase(item)) parts.push(item.room);
  if (item.area) parts.push(archiveKind === "case" ? formatArea(item.area) : item.area);
  if (item.style && archiveKind !== "case") parts.push(item.style);
  return parts.filter(Boolean).join("_");
}

function materialKindLabel(kind: CaseMaterial["kind"]): string {
  return {
    cad: "附件",
    cad_archive: "压缩附件",
    douyin: "抖音视频",
    panorama: "全景图",
    link: "网页链接",
  }[kind];
}

function MaterialKindIcon({ kind }: { kind: CaseMaterial["kind"] }) {
  if (kind === "panorama") return <Eye aria-hidden="true" />;
  if (kind === "douyin") return <Video aria-hidden="true" />;
  if (kind === "link") return <Link aria-hidden="true" />;
  return <FileBox aria-hidden="true" />;
}

type MaterialDownloadPrompt = {
  material: CaseMaterial;
  returnTarget: HTMLElement;
};

function MaterialDownloadDialog({
  prompt,
  onClose,
}: {
  prompt: MaterialDownloadPrompt;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = `material-download-title-${prompt.material.id}`;
  const descriptionId = `material-download-description-${prompt.material.id}`;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cancelRef.current?.focus();
    function keydown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const controls = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>("button:not([disabled]), a[href]"),
      );
      const first = controls[0];
      const last = controls.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    window.addEventListener("keydown", keydown);
    return () => {
      window.removeEventListener("keydown", keydown);
      document.body.style.overflow = previousOverflow;
      window.requestAnimationFrame(() => prompt.returnTarget.focus({ preventScroll: true }));
    };
  }, [onClose, prompt.returnTarget]);

  return (
    <div className="material-download-backdrop">
      <section
        ref={dialogRef}
        className="material-download-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <div className="material-download-mark"><Download aria-hidden="true" /></div>
        <div className="material-download-copy">
          <h2 id={titleId}>确认下载这份资料？</h2>
          <strong>{prompt.material.label}</strong>
          <p id={descriptionId}>
            {prompt.material.file_name ?? materialKindLabel(prompt.material.kind)}
            {prompt.material.size_bytes ? ` · ${formatBytes(prompt.material.size_bytes)}` : ""}
            <br />下载只会保存到当前设备，不会修改案例信息或原始效果图资料。
          </p>
        </div>
        <div className="material-download-actions">
          <button ref={cancelRef} className="secondary-action" type="button" onClick={onClose}>取消</button>
          <a
            className="primary-action"
            href={prompt.material.open_url}
            target="_blank"
            rel="noreferrer"
            onClick={onClose}
          >
            <Download aria-hidden="true" />确认下载
          </a>
        </div>
      </section>
    </div>
  );
}

function LoadingState({ label = "正在读取案例" }: { label?: string }) {
  return (
    <div className="state-block" role="status">
      <LoaderCircle className="spin" aria-hidden="true" />
      <p>{label}</p>
    </div>
  );
}

function ErrorState({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <div className="state-block state-error" role="alert">
      <CircleAlert aria-hidden="true" />
      <div>
        <strong>这里暂时没有加载成功</strong>
        <p>{message}</p>
      </div>
      {retry && (
        <button className="text-action" type="button" onClick={retry}>
          <RefreshCw aria-hidden="true" />
          重新加载
        </button>
      )}
    </div>
  );
}

function SearchField({
  label,
  value,
  onChange,
  placeholder,
  clearLabel,
  className = "",
  inputRef,
  onEnter,
  action,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  clearLabel?: string;
  className?: string;
  inputRef?: Ref<HTMLInputElement>;
  onEnter?: () => void;
  action?: ReactNode;
}) {
  const inputId = useId();

  return (
    <div className={`ui-search ${className}`.trim()}>
      <label htmlFor={inputId}>{label}</label>
      <div className="ui-search-control">
        <Search aria-hidden="true" />
        <input
          ref={inputRef}
          id={inputId}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && onEnter) {
              event.preventDefault();
              onEnter();
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
        />
        {action ?? (value && clearLabel ? (
          <button className="ui-search-clear" type="button" onClick={() => onChange("")} aria-label={clearLabel}>
            <X aria-hidden="true" />
          </button>
        ) : null)}
      </div>
    </div>
  );
}

function SuggestionInput({
  id,
  inputRef,
  name,
  suggestions,
  placeholder,
  required = false,
  suggestionLabel,
}: {
  id?: string;
  inputRef?: Ref<HTMLInputElement>;
  name: string;
  suggestions: string[];
  placeholder: string;
  required?: boolean;
  suggestionLabel: string;
}) {
  const generatedInputId = useId();
  const inputId = id ?? generatedInputId;
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const internalInputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const filteredSuggestions = useMemo(() => {
    const query = value.trim().toLocaleLowerCase("zh-CN");
    if (!query) return suggestions;
    return suggestions.filter((item) => item.toLocaleLowerCase("zh-CN").includes(query));
  }, [suggestions, value]);

  useEffect(() => {
    function closeOnOutsidePointer(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    }
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePointer);
  }, []);

  function assignInputRef(node: HTMLInputElement | null) {
    internalInputRef.current = node;
    if (typeof inputRef === "function") inputRef(node);
    else if (inputRef) (inputRef as { current: HTMLInputElement | null }).current = node;
  }

  function chooseSuggestion(suggestion: string) {
    setValue(suggestion);
    setOpen(false);
    setActiveIndex(-1);
    window.requestAnimationFrame(() => internalInputRef.current?.focus());
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!filteredSuggestions.length) return;
      setOpen(true);
      setActiveIndex((current) => {
        if (event.key === "ArrowDown") return current >= filteredSuggestions.length - 1 ? 0 : current + 1;
        return current <= 0 ? filteredSuggestions.length - 1 : current - 1;
      });
      return;
    }
    if (event.key === "Enter" && open && activeIndex >= 0) {
      event.preventDefault();
      chooseSuggestion(filteredSuggestions[activeIndex]);
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (event.key === "Tab") {
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  const hasSuggestions = filteredSuggestions.length > 0;
  const expanded = open && hasSuggestions;

  return (
    <div ref={rootRef} className="ui-combobox">
      <input
        ref={assignInputRef}
        id={inputId}
        name={name}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        required={required}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={listboxId}
        aria-activedescendant={expanded && activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined}
        onChange={(event) => {
          setValue(event.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onClick={() => setOpen(true)}
        onKeyDown={handleKeyDown}
      />
      <button
        type="button"
        className="ui-combobox-toggle"
        aria-label={`${open ? "收起" : "展开"}${suggestionLabel}`}
        aria-expanded={expanded}
        aria-controls={listboxId}
        onClick={() => {
          setOpen((current) => !current);
          setActiveIndex(-1);
          if (!open) window.requestAnimationFrame(() => internalInputRef.current?.focus());
        }}
      >
        <ChevronDown aria-hidden="true" />
      </button>
      {expanded && (
        <div id={listboxId} className="ui-combobox-list" role="listbox" aria-label={suggestionLabel}>
          {filteredSuggestions.map((suggestion, index) => (
            <button
              id={`${listboxId}-${index}`}
              key={suggestion}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              className={index === activeIndex ? "active" : ""}
              onPointerDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => chooseSuggestion(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Login({ onAuthenticated }: { onAuthenticated: (user: User) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.login(username, password);
      onAuthenticated(await api.me());
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-intro" aria-label="系统介绍">
        <div className="login-statement">
          <p className="login-gallery-en">SPACE AESTHETICS GALLERY</p>
          <h1>空间美学馆</h1>
          <span className="login-statement-rule" aria-hidden="true" />
          <p className="login-statement-slogan">见过万千空间，方懂家的尺度。</p>
          <p className="login-statement-description">典藏空间设计成果<br />沉淀居住美学探索</p>
          <p className="login-statement-english">Understanding the true measure of home<br />comes from experiencing countless spaces.</p>
        </div>
        <ul className="login-pillars" aria-label="空间美学馆特色">
          <li><Images aria-hidden="true" /><b>真实案例</b><small>REAL CASE</small></li>
          <li><Aperture aria-hidden="true" /><b>空间美学</b><small>AESTHETICS</small></li>
          <li><PenTool aria-hidden="true" /><b>设计灵感</b><small>INSPIRATION</small></li>
          <li><BadgeCheck aria-hidden="true" /><b>工艺品质</b><small>CRAFTSMANSHIP</small></li>
        </ul>
      </section>
      <section className="login-panel">
        <div className="brand-lockup brand-lockup-large login-brand">
          <img className="brand-logo brand-logo-large" src={BRAND_LOGO_SRC} alt="" />
          <span>
            <b>{BRAND_NAME}</b>
            <small>空间设计案例库</small>
          </span>
        </div>
        <form className="login-form" onSubmit={submit}>
          <LockKeyhole aria-hidden="true" />
          <h2>登录工作台</h2>
          <label>
            <span>账号</span>
            <input
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              disabled={busy}
              required
            />
          </label>
          <label>
            <span>密码</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={busy}
              required
            />
          </label>
          {error && <div className="form-error" role="alert">{error}</div>}
          <button className="primary-action" type="submit" disabled={busy}>
            {busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <ArrowRight aria-hidden="true" />}
            {busy ? "正在登录" : "进入空间美学馆"}
          </button>
        </form>
        <p className="login-help">连续输错多次会暂时锁定，请联系管理员核对账号。</p>
      </section>
    </main>
  );
}

function ImageSurface({
  src,
  alt,
  className = "",
  eager = false,
  draggable = true,
}: {
  src: string | null;
  alt: string;
  className?: string;
  eager?: boolean;
  draggable?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className={`image-fallback ${className}`} role="img" aria-label={`${alt}，预览图尚未就绪`}>
        <ImageIcon aria-hidden="true" />
        <span>预览图尚未就绪</span>
      </div>
    );
  }
  return (
    <img
      className={className}
      src={src}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      draggable={draggable}
      onError={() => setFailed(true)}
    />
  );
}

type DetailGalleryImage = Pick<CaseImage, "id" | "file_name" | "thumb_url" | "preview_url">;

function DetailGallery({
  images,
  activeImage,
  coverImageId,
  fallbackAlt,
  galleryLabel,
  previewButtonRef,
  meta,
  onOpen,
  onSelect,
}: {
  images: DetailGalleryImage[];
  activeImage: number;
  coverImageId: number | null;
  fallbackAlt: string;
  galleryLabel: string;
  previewButtonRef: Ref<HTMLButtonElement>;
  meta: ReactNode;
  onOpen: () => void;
  onSelect: (index: number) => void;
}) {
  const image = images[activeImage];
  return (
    <div className="detail-gallery">
      <section className="detail-stage">
        <button
          ref={previewButtonRef}
          className="detail-preview"
          type="button"
          onClick={onOpen}
          aria-label={`放大查看第 ${activeImage + 1} 张`}
        >
          <ImageSurface src={image?.preview_url ?? null} alt={image?.file_name ?? fallbackAlt} eager />
          <span>点击进入大图查看</span>
        </button>
        {meta}
      </section>

      <div className="proof-strip" aria-label={galleryLabel}>
        {images.map((item, index) => (
          <button
            type="button"
            key={item.id}
            className={index === activeImage ? "active" : ""}
            onClick={() => onSelect(index)}
            aria-label={`查看第 ${index + 1} 张：${item.file_name}${item.id === coverImageId ? "，当前封面" : ""}`}
          >
            <ImageSurface src={item.thumb_url} alt="" />
            <span className="proof-number">{String(index + 1).padStart(2, "0")}</span>
            {item.id === coverImageId && <span className="proof-cover">封面</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function ArchiveImageDragPreview({ item, index, isCover, metaSuffix = "" }: { item: Pick<CaseImage, "thumb_url" | "file_name" | "size_bytes">; index: number; isCover: boolean; metaSuffix?: string }) {
  return (
    <article className={`case-image-drag-overlay${isCover ? " cover" : ""}`} aria-hidden="true">
      <ImageSurface src={item.thumb_url} alt="" draggable={false} eager />
      <div>
        <span>第 {String(index + 1).padStart(2, "0")} 张</span>
        <strong>{item.file_name}</strong>
        <small>{formatBytes(item.size_bytes)}{metaSuffix ? ` · ${metaSuffix}` : ""}</small>
      </div>
      <GripVertical aria-hidden="true" />
    </article>
  );
}

function SortableArchiveImageCard({
  item,
  index,
  coverImageId,
  savedCoverImageId,
  disabled,
  canDelete,
  deleteConfirming,
  onSetCover,
  onPreview,
  onDelete,
  onCancelDelete,
  onConfirmDelete,
  idPrefix = "case",
  helpId = "case-image-sort-help",
  metaSuffix = "",
}: {
  item: Pick<CaseImage, "id" | "file_name" | "size_bytes" | "thumb_url">;
  index: number;
  coverImageId: number | null;
  savedCoverImageId: number | null;
  disabled: boolean;
  canDelete: boolean;
  deleteConfirming: boolean;
  onSetCover: () => void;
  onPreview: (trigger: HTMLButtonElement) => void;
  onDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  idPrefix?: string;
  helpId?: string;
  metaSuffix?: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: item.id,
    disabled,
    transition: {
      duration: 180,
      easing: "cubic-bezier(.16, 1, .3, 1)",
    },
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const isCover = item.id === coverImageId;

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={[isCover ? "cover" : "", isDragging ? "drag-placeholder" : "", isOver && !isDragging ? "drop-target" : ""].filter(Boolean).join(" ")}
      data-image-order-id={item.id}
      aria-roledescription="可排序图片"
    >
      <button className="case-image-preview-action" type="button" onClick={(event) => onPreview(event.currentTarget)} aria-label={`预览第 ${index + 1} 张图片：${item.file_name}`}><ImageSurface src={item.thumb_url} alt="" draggable={false} /><span><Eye aria-hidden="true" />预览大图</span></button>
      <div>
        <span>第 {String(index + 1).padStart(2, "0")} 张</span>
        <strong title={item.file_name}>{item.file_name}</strong>
        <small>{formatBytes(item.size_bytes)}{metaSuffix ? ` · ${metaSuffix}` : ""}</small>
      </div>
      {!deleteConfirming && <div className="case-image-row-actions">
        {isCover ? (
          <span><Check aria-hidden="true" />{item.id === savedCoverImageId ? "当前封面" : "待保存封面"}</span>
        ) : (
          <button
            className="case-cover-action"
            type="button"
            disabled={disabled}
            aria-label={`将第 ${index + 1} 张图片设为封面：${item.file_name}`}
            onClick={onSetCover}
          >设为封面</button>
        )}
        <div className="case-image-row-buttons">
          <button
            id={`${idPrefix}-image-delete-${item.id}`}
            className="case-image-delete-action"
            type="button"
            aria-label={`将第 ${index + 1} 张图片标记为待删除：${item.file_name}`}
            disabled={!canDelete || disabled}
            onClick={onDelete}
          ><Trash2 aria-hidden="true" />移除</button>
          <button
            id={`${idPrefix}-image-sort-handle-${item.id}`}
            className="case-image-sort-handle"
            type="button"
            disabled={disabled}
            {...attributes}
            {...listeners}
            aria-label={`拖动调整第 ${index + 1} 张图片顺序：${item.file_name}`}
            aria-describedby={helpId}
          ><GripVertical aria-hidden="true" /><span>拖动</span></button>
        </div>
      </div>}
      {deleteConfirming && (
        <div className="case-image-card-confirm" role="alert">
          <span><Trash2 aria-hidden="true" /></span>
          <p><strong>确认将这张图片移入待删除？</strong><small>现在不会删除，保存全部更改后才会永久删除原图与缓存。</small></p>
          <div>
            <button className="secondary-action" type="button" disabled={disabled} onClick={onCancelDelete}>取消</button>
            <button id={`${idPrefix}-image-confirm-${item.id}`} className="danger-action" type="button" disabled={disabled} onClick={onConfirmDelete}><Trash2 aria-hidden="true" />确认移除</button>
          </div>
        </div>
      )}
    </article>
  );
}

type EnterpriseSortableImage = EnterpriseDocumentDetail["images"][number];

function EnterpriseImageDragPreview({
  item,
  index,
  isCover,
}: {
  item: EnterpriseSortableImage;
  index: number;
  isCover: boolean;
}) {
  return <ArchiveImageDragPreview item={item} index={index} isCover={isCover} metaSuffix="企业资料" />;
}

function SortableEnterpriseImageCard({
  item,
  index,
  coverImageId,
  savedCoverImageId,
  disabled,
  canDelete,
  deleteConfirming,
  onSetCover,
  onPreview,
  onDelete,
  onCancelDelete,
  onConfirmDelete,
}: {
  item: EnterpriseSortableImage;
  index: number;
  coverImageId: number | null;
  savedCoverImageId: number | null;
  disabled: boolean;
  canDelete: boolean;
  deleteConfirming: boolean;
  onSetCover: () => void;
  onPreview: (trigger: HTMLButtonElement) => void;
  onDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
}) {
  return (
    <SortableArchiveImageCard
      item={item}
      index={index}
      coverImageId={coverImageId}
      savedCoverImageId={savedCoverImageId}
      disabled={disabled}
      canDelete={canDelete}
      deleteConfirming={deleteConfirming}
      onSetCover={onSetCover}
      onPreview={onPreview}
      onDelete={onDelete}
      onCancelDelete={onCancelDelete}
      onConfirmDelete={onConfirmDelete}
      idPrefix="enterprise"
      helpId="enterprise-image-sort-help"
      metaSuffix="企业资料"
    />
  );
}

function ArchiveIndexNavigation({
  items,
  groupLabel,
  countLabel,
  selectedId,
  recent,
  recentTotal,
  allTotal,
  onAll,
  onRecent,
  onSelect,
  indexRef,
}: {
  items: Community[];
  groupLabel: string;
  countLabel: string;
  selectedId?: number;
  recent: boolean;
  recentTotal: number | null;
  allTotal?: number;
  onAll: () => void;
  onRecent: () => void;
  onSelect: (item: Community) => void;
  indexRef?: Ref<HTMLDivElement>;
}) {
  const total = allTotal ?? items.reduce((sum, item) => sum + item.case_count, 0);
  const allActive = !recent && selectedId === undefined;
  return (
    <div ref={indexRef} className="community-index" aria-label={`${groupLabel}导航`}>
      <div className="community-index-pinned">
        <button
          type="button"
          className={allActive ? "active" : ""}
          aria-pressed={allActive}
          aria-label={`全部${groupLabel}，${total} ${countLabel}`}
          onClick={onAll}
        >
          <span className="recent-index-label"><Archive aria-hidden="true" />全部</span>
          <b>{total}</b>
        </button>
        <button
          type="button"
          className={`recent-index-button ${recent ? "active" : ""}`.trim()}
          aria-pressed={recent}
          aria-label={`最近浏览，${recentTotal ?? 0} ${countLabel}`}
          onClick={onRecent}
        >
          <span className="recent-index-label"><History aria-hidden="true" />最近浏览</span>
          <b>{recentTotal ?? "—"}</b>
        </button>
      </div>
      <div className="community-index-list">
        {items.map((item) => (
          <button
            type="button"
            className={!recent && selectedId === item.id ? "active" : ""}
            aria-pressed={!recent && selectedId === item.id}
            onClick={() => onSelect(item)}
            key={item.id}
          >
            <span>{item.name}</span><b>{item.case_count}</b>
          </button>
        ))}
      </div>
    </div>
  );
}

function SortableArchiveOrderRow({
  item,
  index,
  disabled,
}: {
  item: ArchiveOrderItem;
  index: number;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({
    id: item.id,
    disabled,
    transition: { duration: 180, easing: "cubic-bezier(.16, 1, .3, 1)" },
  });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className={`archive-order-row${isDragging ? " dragging" : ""}${isOver && !isDragging ? " drop-target" : ""}`}>
      <span>{String(index + 1).padStart(2, "0")}</span>
      <strong>{item.name}</strong>
      <button type="button" disabled={disabled} {...attributes} {...listeners} aria-label={`拖动调整 ${item.name} 的顺序`}><GripVertical aria-hidden="true" />拖动</button>
    </div>
  );
}

function ArchiveOrderManager({
  archiveKind,
  categoryLabel,
  itemLabel,
  onSaved,
}: {
  archiveKind: "case" | "material" | "enterprise" | "floorplan";
  categoryLabel: string;
  itemLabel: string;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState<ArchiveOrderItem[] | null>(null);
  const [items, setItems] = useState<ArchiveOrderItem[] | null>(null);
  const [mode, setMode] = useState<"alphabetical" | "custom">("alphabetical");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useTransientNotice();
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 140, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (!open) return;
    let active = true;
    setError("");
    const loadOrder = archiveKind === "floorplan" ? api.floorplanOrder : (level: "categories" | "items") => api.archiveOrder(archiveKind, level);
    Promise.all([loadOrder("categories"), loadOrder("items")])
      .then(([categoryState, itemState]) => {
        if (!active) return;
        setCategories(categoryState.items);
        setItems(itemState.items);
        setMode(categoryState.mode);
      })
      .catch((reason) => active && setError(messageFrom(reason)));
    return () => { active = false; };
  }, [archiveKind, open]);

  function finishSort(level: "categories" | "items", event: DragEndEvent) {
    if (!event.over || event.active.id === event.over.id) return;
    const current = level === "categories" ? categories : items;
    if (!current) return;
    const from = current.findIndex((item) => String(item.id) === String(event.active.id));
    const to = current.findIndex((item) => String(item.id) === String(event.over?.id));
    if (from < 0 || to < 0) return;
    const next = arrayMove(current, from, to);
    if (level === "categories") setCategories(next); else setItems(next);
  }

  async function save() {
    if (!categories || !items || busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (archiveKind === "floorplan") {
        await api.updateFloorplanOrder("categories", categories.map((item) => String(item.id)), mode);
        await api.updateFloorplanOrder("items", items.map((item) => String(item.id)));
      } else {
        await api.updateArchiveOrder(archiveKind, "categories", categories.map((item) => Number(item.id)), mode);
        await api.updateArchiveOrder(archiveKind, "items", items.map((item) => Number(item.id)));
      }
      setNotice(`${categoryLabel}和${itemLabel}顺序已保存。`);
      onSaved();
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={`archive-order-manager${open ? " open" : ""}`}>
      <button className="secondary-action archive-order-toggle" type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)}><Settings2 aria-hidden="true" />{open ? "收起排序设置" : "排序设置"}</button>
      {open && <div className="archive-order-workbench">
        <header><div><h3>浏览顺序</h3><p>前台不显示排序规则；这里的设置同时作用于电脑和手机。</p></div><button className="primary-action" type="button" disabled={busy || !categories || !items} onClick={() => void save()}>{busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <Save aria-hidden="true" />}{busy ? "正在保存" : "保存排序"}</button></header>
        {error && <div className="form-error" role="alert"><CircleAlert aria-hidden="true" />{error}</div>}
        {notice && <ActionFeedback message={notice} onDismiss={() => setNotice("")} />}
        {!categories || !items ? <LoadingState label="正在读取排序设置" /> : <div className="archive-order-columns">
          <section><header><div><h4>{categoryLabel}</h4><p>选择自动排序或自定义；拖动时朱砂框表示放置位置。</p></div><div className="archive-order-mode" role="group" aria-label={`${categoryLabel}排序方式`}><button type="button" className={mode === "alphabetical" ? "active" : ""} aria-pressed={mode === "alphabetical"} onClick={() => setMode("alphabetical")}>A–Z</button><button type="button" className={mode === "custom" ? "active" : ""} aria-pressed={mode === "custom"} onClick={() => setMode("custom")}>自定义</button></div></header>{mode === "custom" ? <DndContext sensors={sensors} modifiers={restrictDragToVerticalContainer} collisionDetection={closestCenter} onDragEnd={(event) => finishSort("categories", event)}><SortableContext items={categories.map((item) => item.id)} strategy={verticalListSortingStrategy}><div className="archive-order-list">{categories.map((item, index) => <SortableArchiveOrderRow key={item.id} item={item} index={index} disabled={busy} />)}</div></SortableContext></DndContext> : <div className="archive-order-auto"><Archive aria-hidden="true" /><strong>自动按名称排列</strong><p>切回自定义时，之前保存的顺序仍会保留。</p></div>}</section>
          <section><header><div><h4>{itemLabel}</h4><p>拖动调整默认展示顺序；朱砂框表示放置位置。</p></div></header><DndContext sensors={sensors} modifiers={restrictDragToVerticalContainer} collisionDetection={closestCenter} onDragEnd={(event) => finishSort("items", event)}><SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}><div className="archive-order-list">{items.map((item, index) => <SortableArchiveOrderRow key={item.id} item={item} index={index} disabled={busy} />)}</div></SortableContext></DndContext></section>
        </div>}
      </div>}
    </section>
  );
}

function ArchiveView({
  archiveKind = "case",
  initialCommunity,
  initialScope = "all",
  isAdmin,
  isRestricted = false,
  stats,
  onStatsChanged,
}: {
  archiveKind?: ArchiveKind;
  initialCommunity?: number;
  initialScope?: "all" | "landed";
  isAdmin: boolean;
  isRestricted?: boolean;
  stats: CatalogStats | null | undefined;
  onStatsChanged: () => void;
}) {
  const copy = ARCHIVE_COPY[archiveKind];
  const [communities, setCommunities] = useState<Community[]>([]);
  const [cases, setCases] = useState<CaseList | null>(null);
  const [query, setQuery] = useState("");
  const [settledQuery, setSettledQuery] = useState("");
  const [community, setCommunity] = useState<number | undefined>(initialCommunity);
  const [recent, setRecent] = useState(false);
  const [recentTotal, setRecentTotal] = useState<number | null>(null);
  const [recentRefreshKey, setRecentRefreshKey] = useState(0);
  const [page, setPage] = useState(1);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [folioPanoramaPreview, setFolioPanoramaPreview] = useState<CaseMaterial | null>(null);
  const [folioPanoramaOpeningId, setFolioPanoramaOpeningId] = useState<number | null>(null);
  const [folioVideoOpeningId, setFolioVideoOpeningId] = useState<number | null>(null);
  const [folioPanoramaError, setFolioPanoramaError] = useTransientNotice();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const folioRef = useRef<HTMLDivElement>(null);
  const folioCloseRef = useRef<HTMLButtonElement>(null);
  const lastCaseTriggerRef = useRef<HTMLButtonElement>(null);
  const folioPanoramaDialogRef = useRef<HTMLDivElement>(null);
  const folioPanoramaCloseRef = useRef<HTMLButtonElement>(null);
  const folioPanoramaReturnRef = useRef<HTMLButtonElement | null>(null);
  const studioRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<HTMLElement>(null);
  const communityIndexRef = useRef<HTMLDivElement>(null);
  const returnPositionRef = useRef({
    studio: 0,
    board: 0,
    communityIndex: 0,
    communityIndexLeft: 0,
    caseId: null as number | null,
  });
  const shouldRestorePositionRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setSettledQuery(query.trim()), 280);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => setPage(1), [settledQuery, community, recent]);

  useEffect(() => setPreviewId(null), [settledQuery, community, page, recent]);

  useLayoutEffect(() => {
    boardRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [settledQuery, community, page, recent]);

  useLayoutEffect(() => {
    if (selectedId !== null || !shouldRestorePositionRef.current) return;
    shouldRestorePositionRef.current = false;
    const position = returnPositionRef.current;
    studioRef.current?.scrollTo({ top: position.studio, left: 0, behavior: "auto" });
    boardRef.current?.scrollTo({ top: position.board, left: 0, behavior: "auto" });
    communityIndexRef.current?.scrollTo({
      top: position.communityIndex,
      left: position.communityIndexLeft,
      behavior: "auto",
    });
    const frame = window.requestAnimationFrame(() => {
      studioRef.current?.scrollTo({ top: position.studio, left: 0, behavior: "auto" });
      boardRef.current?.scrollTo({ top: position.board, left: 0, behavior: "auto" });
      communityIndexRef.current?.scrollTo({
        top: position.communityIndex,
        left: position.communityIndexLeft,
        behavior: "auto",
      });
      if (position.caseId !== null) {
        boardRef.current
          ?.querySelector<HTMLButtonElement>(`[data-case-id="${position.caseId}"]`)
          ?.focus({ preventScroll: true });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selectedId]);

  useEffect(() => {
    if (previewId === null) return;
    const frame = window.requestAnimationFrame(() => {
      if (window.matchMedia("(max-width: 920px)").matches) {
        folioCloseRef.current?.focus({ preventScroll: true });
      } else {
        folioRef.current?.focus({ preventScroll: true });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [previewId]);

  useEffect(() => {
    if (previewId === null) return;
    function keydown(event: KeyboardEvent) {
      if (document.querySelector(".panorama-dialog")) return;
      if (event.key === "Escape" && window.matchMedia("(max-width: 920px)").matches) {
        event.preventDefault();
        closeFolio();
      }
    }
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [previewId]);

  function closeFolio() {
    const trigger = lastCaseTriggerRef.current;
    setPreviewId(null);
    window.requestAnimationFrame(() => trigger?.focus({ preventScroll: true }));
  }

  function openCaseDetail(caseId: number) {
    returnPositionRef.current = {
      studio: studioRef.current?.scrollTop ?? 0,
      board: boardRef.current?.scrollTop ?? 0,
      communityIndex: communityIndexRef.current?.scrollTop ?? 0,
      communityIndexLeft: communityIndexRef.current?.scrollLeft ?? 0,
      caseId,
    };
    setSelectedId(caseId);
  }

  function returnToArchive() {
    shouldRestorePositionRef.current = true;
    setSelectedId(null);
  }

  async function openFolioPanorama(caseId: number, returnTarget: HTMLButtonElement) {
    folioPanoramaReturnRef.current = returnTarget;
    setFolioPanoramaError("");
    setFolioPanoramaOpeningId(caseId);
    try {
      const detail = archiveKind === "case" ? await api.openCase(caseId) : await api.openMaterialCase(caseId);
      const panorama = detail.materials.find((item) => item.kind === "panorama");
      if (!panorama) throw new Error("当前案例没有可查看的全景图。");
      setFolioPanoramaPreview(panorama);
      setRecentRefreshKey((value) => value + 1);
    } catch (reason) {
      setFolioPanoramaError(messageFrom(reason));
    } finally {
      setFolioPanoramaOpeningId(null);
    }
  }

  async function openFolioVideo(caseId: number) {
    const target = window.open("about:blank", "_blank");
    if (!target) {
      setFolioPanoramaError("浏览器阻止了新窗口，请允许弹出窗口后重试。");
      return;
    }
    target.opener = null;
    setFolioPanoramaError("");
    setFolioVideoOpeningId(caseId);
    try {
      const detail = archiveKind === "case" ? await api.openCase(caseId) : await api.openMaterialCase(caseId);
      const videos = detail.materials.filter((item) => item.kind === "douyin");
      if (videos.length === 0) throw new Error("当前案例没有可查看的视频。");
      if (videos.length > 1) {
        target.close();
        openCaseDetail(caseId);
        return;
      }
      target.location.replace(new URL(videos[0].open_url, window.location.href).href);
      setRecentRefreshKey((value) => value + 1);
    } catch (reason) {
      target.close();
      setFolioPanoramaError(messageFrom(reason));
    } finally {
      setFolioVideoOpeningId(null);
    }
  }

  useEffect(() => {
    if (!folioPanoramaPreview) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    folioPanoramaCloseRef.current?.focus();
    function keydown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setFolioPanoramaPreview(null);
        return;
      }
      if (event.key !== "Tab" || !folioPanoramaDialogRef.current) return;
      const controls = Array.from(
        folioPanoramaDialogRef.current.querySelectorAll<HTMLElement>("button:not([disabled]), a[href]"),
      );
      const first = controls[0];
      const last = controls.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    window.addEventListener("keydown", keydown);
    return () => {
      window.removeEventListener("keydown", keydown);
      document.body.style.overflow = previousOverflow;
      window.requestAnimationFrame(() => folioPanoramaReturnRef.current?.focus({ preventScroll: true }));
    };
  }, [folioPanoramaPreview]);

  useEffect(() => {
    let active = true;
    (archiveKind === "case" ? api.communities() : api.materialLibraries())
      .then((items) => active && setCommunities(items)).catch(() => undefined);
    return () => {
      active = false;
    };
  }, [archiveKind]);

  useEffect(() => {
    let active = true;
    (archiveKind === "case" ? api.recentCases({ pageSize: 1 }) : api.recentMaterialCases({ pageSize: 1 }))
      .then((payload) => active && setRecentTotal(payload.total))
      .catch(() => active && setRecentTotal(null));
    return () => {
      active = false;
    };
  }, [archiveKind, recentRefreshKey]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const request = recent
      ? archiveKind === "case" ? api.recentCases({ q: settledQuery, page }) : api.recentMaterialCases({ q: settledQuery, page })
      : archiveKind === "case" ? api.cases({ q: settledQuery, community, page, scope: initialScope }) : api.materialCases({ q: settledQuery, community, page });
    request
      .then((payload) => active && setCases(payload))
      .catch((reason) => active && setError(messageFrom(reason)))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [archiveKind, settledQuery, community, page, reloadKey, initialScope, recent, recentRefreshKey]);

  useEffect(() => {
    if (previewId !== null && !cases?.items.some((item) => item.id === previewId)) {
      setPreviewId(null);
    }
  }, [cases, previewId]);

  if (selectedId !== null) {
    return (
      <CaseDetail
        caseId={selectedId}
        archiveKind={archiveKind}
        isAdmin={isAdmin}
        onBack={returnToArchive}
        onDeleted={() => { setSelectedId(null); setPreviewId(null); setReloadKey((value) => value + 1); }}
        onViewed={() => setRecentRefreshKey((value) => value + 1)}
        onChanged={() => {
          setReloadKey((value) => value + 1);
          onStatsChanged();
        }}
      />
    );
  }

  const preview = cases?.items.find((item) => item.id === previewId) ?? null;
  const archiveTotal = communities.reduce((total, item) => total + item.case_count, 0);
  const materialImageTotal = communities.reduce((total, item) => total + (item.image_count ?? 0), 0);
  const selectedCommunity = communities.find((item) => item.id === community);
  const pageCount = cases ? Math.max(1, Math.ceil(cases.total / cases.page_size)) : 1;

  return (
    <div ref={studioRef} className="archive-studio">
      <aside className="project-index">
        <div className="index-heading">
          <h1>{copy.index}</h1>
          <p>
            <span>{archiveTotal} {copy.count}</span>
            <span>
              {archiveKind === "material"
                ? `${materialImageTotal} 张素材图`
                : stats === undefined
                  ? "正在核对效果图"
                  : stats === null
                    ? "效果图统计暂不可用"
                    : `${stats.images} 张${isRestricted ? "授权效果图" : "效果图"}`}
            </span>
          </p>
        </div>

        <SearchField
          className="index-search"
          label={`搜索${copy.singular}`}
          value={query}
          onChange={setQuery}
          placeholder={`搜索${copy.group}、${copy.room}或${copy.style}`}
          clearLabel={`清空${copy.singular}搜索`}
        />

        <label className="community-picker">
          <span>选择{copy.group}</span>
          <select
            value={community ?? ""}
            onChange={(event) => {
              setRecent(false);
              setCommunity(event.target.value ? Number(event.target.value) : undefined);
            }}
          >
            <option value="">全部</option>
            {communities.map((item) => (
              <option value={item.id} key={item.id}>{item.name} · {item.case_count}</option>
            ))}
          </select>
        </label>

        <ArchiveIndexNavigation
          items={communities}
          groupLabel={copy.group}
          countLabel={copy.count}
          selectedId={community}
          recent={recent}
          recentTotal={recentTotal}
          indexRef={communityIndexRef}
          onAll={() => { setCommunity(undefined); setRecent(false); }}
          onRecent={() => { setCommunity(undefined); setRecent(true); }}
          onSelect={(item) => { setRecent(false); setCommunity(item.id); }}
        />
      </aside>

      <main ref={boardRef} className="project-board" aria-busy={loading}>
        <header className="board-heading">
          <div>
            <h2>{recent ? "最近浏览" : selectedCommunity?.name ?? (settledQuery ? "搜索结果" : `全部${copy.singular}`)}</h2>
            <p>{recent ? `按最近打开时间排序，选择${copy.singular}继续浏览` : `选择${copy.singular}，右侧查看完整图片`}</p>
          </div>
          <p aria-live="polite">当前结果 <strong>{cases?.total ?? 0}</strong> {copy.count}</p>
        </header>

        {loading && <LoadingState label={`正在整理${copy.singular}墙`} />}
        {error && <ErrorState message={error} retry={() => setReloadKey((value) => value + 1)} />}
        {!loading && !error && cases?.items.length === 0 && (
          <div className="empty-archive">
            <FolderSearch aria-hidden="true" />
            <h2>
              {recent
                ? settledQuery ? `最近浏览中没有匹配${copy.singular}` : "还没有最近浏览"
                : isRestricted && archiveTotal === 0 && !settledQuery
                  ? "尚未授权任何案例"
                  : `没有找到对应${copy.singular}`}
            </h2>
            <p>
              {recent
                ? settledQuery
                  ? "试试减少关键词，或清除搜索后查看全部最近记录。"
                  : `成功打开${copy.singular}详情后，会按时间顺序显示在这里。`
                : isRestricted && archiveTotal === 0 && !settledQuery
                  ? "请联系管理员在员工账号中配置可查看的案例。"
                  : `试试减少关键词，或切换到“全部${copy.group}”。`}
            </p>
            {(recent || !isRestricted || archiveTotal > 0 || Boolean(settledQuery)) && (
              <button
                type="button"
                className="secondary-action"
                onClick={() => {
                  if (recent && settledQuery) {
                    setQuery("");
                  } else {
                    setQuery("");
                    setCommunity(undefined);
                    setRecent(false);
                  }
                }}
              >
                {recent && settledQuery ? "清除搜索" : recent ? `返回全部${copy.singular}` : "清除筛选"}
              </button>
            )}
          </div>
        )}

        {!loading && !error && cases?.items.length ? (
          <>
            <section
              className="project-grid"
              aria-label={`${copy.singular}墙`}
              key={`${recent ? "recent" : "all"}-${settledQuery}-${community ?? "all"}-${initialScope}-${page}`}
            >
              {cases.items.map((item, index) => (
                <CaseTile
                  key={item.id}
                  item={item}
                  index={(page - 1) * cases.page_size + index + 1}
                  selected={previewId === item.id}
                  animationOrder={Math.min(index, 8)}
                  archiveKind={archiveKind}
                  onSelect={(trigger) => {
                    lastCaseTriggerRef.current = trigger;
                    setPreviewId(item.id);
                  }}
                />
              ))}
            </section>
            <nav className="pagination studio-pagination" aria-label={`${copy.singular}分页`}>
              <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
                <ChevronLeft aria-hidden="true" /> 上一页
              </button>
              <span>{page} / {pageCount}</span>
              <button type="button" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>
                下一页 <ChevronRight aria-hidden="true" />
              </button>
            </nav>
          </>
        ) : null}
      </main>

      <aside className={`project-folio ${preview ? "ready" : "waiting"}`} aria-label={`当前${copy.singular}图册`}>
        <div className="folio-bar">
          <span>{archiveKind === "case" ? "CASE FOLIO" : "MATERIAL FOLIO"}</span>
          <span>{preview ? `已选择${copy.singular}` : "等待选择"}</span>
          {preview && (
            <button
              ref={folioCloseRef}
              className="folio-close"
              type="button"
              onClick={closeFolio}
              aria-label={`关闭当前${copy.singular}`}
            >
              <X aria-hidden="true" />
            </button>
          )}
        </div>
        {preview ? (
          <div
            ref={folioRef}
            className={`folio-content ${Boolean(preview.panorama_count) && Boolean(preview.video_count) ? "folio-content-three-actions" : ""}`}
            key={preview.id}
            tabIndex={-1}
            aria-labelledby="current-folio-title"
          >
            <button className="folio-preview" type="button" onClick={() => openCaseDetail(preview.id)}>
              <ImageSurface src={preview.cover_url} alt={`${caseDisplayTitle(preview, archiveKind)}${copy.image}`} eager />
              <span>打开{copy.singular} <ArrowUpRight aria-hidden="true" /></span>
            </button>
            <div className="folio-index"><span>当前{copy.singular}</span><span>{archiveKind === "case" ? "CASE" : "MATERIAL"} {String(preview.id).padStart(4, "0")}</span></div>
            <h2 id="current-folio-title">{caseDisplayTitle(preview, archiveKind)}</h2>
            <dl>
              <div><dt>{copy.group}</dt><dd>{preview.community_name}</dd></div>
              <div><dt>{copy.room}</dt><dd>{preview.room || "未命名素材"}</dd></div>
              <div><dt>{copy.area}</dt><dd>{archiveKind === "case" ? formatArea(preview.area) : preview.area || "未登记"}</dd></div>
              <div><dt>图片</dt><dd>{preview.image_count} 张</dd></div>
              <div><dt>{copy.style}</dt><dd>{preview.style || "未登记"}</dd></div>
              {Boolean(preview.panorama_count) && (
                <div className="folio-panorama-row">
                  <dt>全景</dt>
                  <dd>
                    <button
                      className="folio-panorama-link"
                      type="button"
                      disabled={folioPanoramaOpeningId === preview.id}
                      onClick={(event) => void openFolioPanorama(preview.id, event.currentTarget)}
                    >
                      <Aperture aria-hidden="true" /> {folioPanoramaOpeningId === preview.id ? "正在打开" : "查看"}
                    </button>
                  </dd>
                </div>
              )}
              {Boolean(preview.video_count) && (
                <div className="folio-video-row">
                  <dt>视频</dt>
                  <dd>
                    <button
                      className="folio-video-link"
                      type="button"
                      disabled={folioVideoOpeningId === preview.id}
                      onClick={() => void openFolioVideo(preview.id)}
                    >
                      <Video aria-hidden="true" /> {folioVideoOpeningId === preview.id ? "正在打开" : "查看"}
                    </button>
                  </dd>
                </div>
              )}
            </dl>
            {folioPanoramaError && (
              <ActionFeedback
                className="folio-panorama-feedback"
                message={folioPanoramaError}
                tone="warning"
                onDismiss={() => setFolioPanoramaError("")}
              />
            )}
            <div className={`folio-actions ${Boolean(preview.panorama_count) && Boolean(preview.video_count) ? "folio-actions-three" : ""}`}>
              <button className="primary-action folio-action" type="button" onClick={() => openCaseDetail(preview.id)}>
                {copy.image} <ArrowUpRight aria-hidden="true" />
              </button>
              {Boolean(preview.panorama_count) && (
                <button
                  className="folio-panorama-compact"
                  type="button"
                  disabled={folioPanoramaOpeningId === preview.id}
                  onClick={(event) => void openFolioPanorama(preview.id, event.currentTarget)}
                >
                  <Aperture aria-hidden="true" /> {folioPanoramaOpeningId === preview.id ? "正在打开" : "全景"}
                </button>
              )}
              {Boolean(preview.video_count) && (
                <button
                  className="folio-video-compact"
                  type="button"
                  disabled={folioVideoOpeningId === preview.id}
                  onClick={() => void openFolioVideo(preview.id)}
                >
                  <Video aria-hidden="true" /> {folioVideoOpeningId === preview.id ? "正在打开" : "视频"}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="folio-empty">
            <div className="folio-empty-scene" aria-hidden="true">
              <span>{copy.emptyScene}<br />{copy.emptySceneSecond}</span>
            </div>
            <div className="folio-empty-copy">
              <span aria-hidden="true" />
              <h2>{copy.emptyHeadline}<br />{copy.emptyHeadlineSecond}</h2>
              <p>从左侧{copy.singular}墙选择一{copy.count}，完整图册将在这里展开。</p>
            </div>
            <ul className="folio-empty-pillars" aria-label="空间美学馆内容">
              <li><Images aria-hidden="true" /><span><b>{archiveKind === "case" ? "真实案例" : "精选实景"}</b><small>{archiveKind === "case" ? "CASES" : "SCENES"}</small></span></li>
              <li><Aperture aria-hidden="true" /><span><b>{archiveKind === "case" ? "空间美学" : "空间参考"}</b><small>SPACE</small></span></li>
              <li>{archiveKind === "case" ? <PenTool aria-hidden="true" /> : <Search aria-hidden="true" />}<span><b>{archiveKind === "case" ? "设计灵感" : "常见问题"}</b><small>{archiveKind === "case" ? "IDEAS" : "ANSWERS"}</small></span></li>
              <li>{archiveKind === "case" ? <BadgeCheck aria-hidden="true" /> : <PenTool aria-hidden="true" />}<span><b>{archiveKind === "case" ? "工艺品质" : "设计灵感"}</b><small>{archiveKind === "case" ? "CRAFT" : "IDEAS"}</small></span></li>
            </ul>
          </div>
        )}
      </aside>

      {folioPanoramaPreview && (
        <div
          ref={folioPanoramaDialogRef}
          className="panorama-dialog"
          role="dialog"
          aria-modal="true"
          aria-label={`预览全景图：${folioPanoramaPreview.label}`}
        >
          <header>
            <div><span>全景图预览</span><h2>{folioPanoramaPreview.label}</h2></div>
            <div>
              <a className="secondary-action" href={folioPanoramaPreview.open_url} target="_blank" rel="noreferrer">
                新窗口打开 <ArrowUpRight aria-hidden="true" />
              </a>
              <button
                ref={folioPanoramaCloseRef}
                type="button"
                onClick={() => setFolioPanoramaPreview(null)}
                aria-label="关闭全景图预览"
              >
                <X aria-hidden="true" />
              </button>
            </div>
          </header>
          <div className="panorama-frame">
            <iframe
              src={folioPanoramaPreview.preview_url ?? folioPanoramaPreview.open_url}
              title={folioPanoramaPreview.label}
              sandbox="allow-forms allow-popups allow-same-origin allow-scripts"
              referrerPolicy="no-referrer"
              allow="fullscreen; gyroscope; accelerometer; xr-spatial-tracking"
            />
            <p>若预览方禁止嵌入，请使用上方“新窗口打开”。</p>
          </div>
        </div>
      )}

    </div>
  );
}

function CaseTile({
  archiveKind = "case",
  item,
  index,
  selected,
  animationOrder,
  onSelect,
}: {
  archiveKind?: ArchiveKind;
  item: CaseSummary;
  index: number;
  selected: boolean;
  animationOrder: number;
  onSelect: (trigger: HTMLButtonElement) => void;
}) {
  const copy = ARCHIVE_COPY[archiveKind];
  return (
    <article
      className="project-card"
      data-selected={selected}
      style={{ "--card-order": animationOrder } as CSSProperties}
    >
      <button
        className="project-select"
        type="button"
        data-case-id={item.id}
        aria-pressed={selected}
        aria-label={`选择 ${caseDisplayTitle(item, archiveKind)} 作为当前${copy.singular}`}
        onClick={(event) => onSelect(event.currentTarget)}
      >
        <span className="project-image-wrap">
          <ImageSurface src={item.cover_url} alt={`${caseDisplayTitle(item, archiveKind)}${copy.image}`} className="project-image" />
          {selected && <span className="project-current">当前{copy.singular}</span>}
        </span>
        <span className="project-copy">
          <span>
            <b>{caseDisplayTitle(item, archiveKind)}</b>
            <span className="project-facts">
              <em>{item.image_count} 张</em>
              {Boolean(item.panorama_count) && (
                <span className="project-panorama" aria-label="有配套全景图" title="有配套全景图"><Aperture aria-hidden="true" /></span>
              )}
              {Boolean(item.video_count) && (
                <span className="project-video" aria-label="有配套视频" title="有配套视频"><Video aria-hidden="true" /></span>
              )}
            </span>
          </span>
          <small>
            {String(index).padStart(3, "0")} · {item.room || "研发"}
            {item.area ? ` · ${archiveKind === "case" ? formatArea(item.area) : item.area}` : ""}
            {item.style ? ` · ${item.style}` : ""}
          </small>
        </span>
      </button>
    </article>
  );
}

type PendingMaterialCreate =
  | { tempId: string; mode: "upload"; label: string; file: File }
  | {
    tempId: string;
    mode: "link";
    label: string;
    kind: "panorama" | "douyin" | "link";
    url: string;
    previewUrl: string | null;
  };

function CaseDetail({
  caseId,
  archiveKind = "case",
  isAdmin,
  onBack,
  onChanged,
  onViewed,
  onDeleted,
  backLabel = "",
  externalNotice = "",
  externalNoticeTone = "success",
  onDismissExternalNotice,
}: {
  caseId: number;
  archiveKind?: ArchiveKind;
  isAdmin: boolean;
  onBack: () => void;
  onChanged: () => void;
  onViewed?: () => void;
  onDeleted?: () => void;
  backLabel?: string;
  externalNotice?: string;
  externalNoticeTone?: FeedbackTone;
  onDismissExternalNotice?: () => void;
}) {
  const copy = ARCHIVE_COPY[archiveKind];
  const [detail, setDetail] = useState<CaseDetailData | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [editorPreviewImage, setEditorPreviewImage] = useState<CaseImage | null>(null);
  const [panoramaPreview, setPanoramaPreview] = useState<CaseMaterial | null>(null);
  const [downloadPrompt, setDownloadPrompt] = useState<MaterialDownloadPrompt | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveStage, setSaveStage] = useState("");
  const [editError, setEditError] = useState("");
  const [editNotice, setEditNotice] = useTransientNotice();
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageOrderIds, setImageOrderIds] = useState<number[]>([]);
  const [activeDragImageId, setActiveDragImageId] = useState<number | null>(null);
  const [imageUploadProgress, setImageUploadProgress] = useState<number | null>(null);
  const [imageError, setImageError] = useState("");
  const [imageNotice, setImageNotice] = useTransientNotice();
  const [imageDeleteConfirm, setImageDeleteConfirm] = useState<CaseImage | null>(null);
  const [pendingImageDeletes, setPendingImageDeletes] = useState<CaseImage[]>([]);
  const [editorMaterials, setEditorMaterials] = useState<AdminMaterial[] | null>(null);
  const [materialError, setMaterialError] = useState("");
  const [materialNotice, setMaterialNotice] = useTransientNotice();
  const [materialCreateMode, setMaterialCreateMode] = useState<"upload" | "link">("upload");
  const [materialDraft, setMaterialDraft] = useState({
    label: "",
    kind: "link" as "panorama" | "douyin" | "link",
    url: "",
    previewUrl: "",
    file: null as File | null,
  });
  const [pendingMaterialCreates, setPendingMaterialCreates] = useState<PendingMaterialCreate[]>([]);
  const [pendingMaterialRemoveIds, setPendingMaterialRemoveIds] = useState<number[]>([]);
  const [draft, setDraft] = useState({
    community: "",
    room: "",
    area: "",
    style: "",
    remark: "",
    coverImageId: null as number | null,
  });
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const previewButtonRef = useRef<HTMLButtonElement>(null);
  const editorPreviewButtonRef = useRef<HTMLButtonElement>(null);
  const panoramaDialogRef = useRef<HTMLDivElement>(null);
  const panoramaCloseRef = useRef<HTMLButtonElement>(null);
  const panoramaReturnRef = useRef<HTMLElement | null>(null);
  const panoramaHeaderRef = useRef<HTMLButtonElement>(null);
  const panoramaSummaryRef = useRef<HTMLElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);
  const materialFileInputRef = useRef<HTMLInputElement>(null);
  const editorFirstFieldRef = useRef<HTMLInputElement>(null);
  const imageOrderIdsRef = useRef<number[]>([]);
  const openedCaseRef = useRef<number | null>(null);
  const openRequestRef = useRef<{ caseId: number; request: Promise<CaseDetailData> } | null>(null);
  const onViewedRef = useRef(onViewed);
  const imageSortSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 140, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    onViewedRef.current = onViewed;
  }, [onViewed]);

  useEffect(() => {
    imageOrderIdsRef.current = imageOrderIds;
  }, [imageOrderIds]);

  useEffect(() => {
    if (!editing) return;
    let active = true;
    setEditorMaterials(null);
    setMaterialError("");
    api.adminMaterials("", archiveKind)
      .then((items) => {
        if (!active) return;
        setEditorMaterials(items.filter((item) => (archiveKind === "case" ? item.cases : item.material_cases).some((candidate) => candidate.id === caseId)));
      })
      .catch((reason) => {
        if (active) setMaterialError(`附件读取失败：${messageFrom(reason)} 请稍后重试。`);
      });
    return () => { active = false; };
  }, [archiveKind, caseId, editing]);

  useEffect(() => {
    let active = true;
    setError("");
    setDetail(null);
    let request: Promise<CaseDetailData>;
    let recordsView = false;
    if (openedCaseRef.current === caseId) {
      request = archiveKind === "case" ? api.caseDetail(caseId) : api.materialCaseDetail(caseId);
    } else if (openRequestRef.current?.caseId === caseId) {
      request = openRequestRef.current.request;
    } else {
      request = archiveKind === "case" ? api.openCase(caseId) : api.openMaterialCase(caseId);
      openRequestRef.current = { caseId, request };
      recordsView = true;
    }
    request
      .then((payload) => {
        if (!active) return;
        openedCaseRef.current = caseId;
        if (openRequestRef.current?.request === request) openRequestRef.current = null;
        setDetail(payload);
        setImageOrderIds(payload.images.map((item) => item.id));
        if (recordsView) onViewedRef.current?.();
      })
      .catch((reason) => {
        if (openRequestRef.current?.request === request) openRequestRef.current = null;
        if (active) setError(messageFrom(reason));
      });
    return () => {
      active = false;
    };
  }, [archiveKind, caseId, reloadKey]);

  useEffect(() => {
    if (!detail || lightbox || editing || panoramaPreview || imageDeleteConfirm) {
      return;
    }
    const lastImageIndex = Math.max(0, detail.images.length - 1);
    function keydown(event: KeyboardEvent) {
      if (
        event.defaultPrevented
        || event.altKey
        || event.ctrlKey
        || event.metaKey
        || event.shiftKey
      ) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target?.closest("input, textarea, select, [contenteditable='true']")) return;
      if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        event.preventDefault();
        setActiveImage((value) => Math.max(0, value - 1));
      } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        event.preventDefault();
        setActiveImage((value) => Math.min(lastImageIndex, value + 1));
      }
    }
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [detail, editing, imageDeleteConfirm, lightbox, panoramaPreview]);

  useEffect(() => {
    if (!panoramaPreview) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panoramaCloseRef.current?.focus();
    function keydown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setPanoramaPreview(null);
        return;
      }
      if (event.key !== "Tab" || !panoramaDialogRef.current) return;
      const controls = Array.from(
        panoramaDialogRef.current.querySelectorAll<HTMLElement>("button:not([disabled]), a[href]"),
      );
      const first = controls[0];
      const last = controls.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    window.addEventListener("keydown", keydown);
    return () => {
      window.removeEventListener("keydown", keydown);
      document.body.style.overflow = previousOverflow;
      window.requestAnimationFrame(() => panoramaReturnRef.current?.focus());
    };
  }, [panoramaPreview]);

  useEffect(() => {
    if (!editing) return;
    const confirmedImageId = imageDeleteConfirm?.id;
    const frame = confirmedImageId
      ? window.requestAnimationFrame(() => document.getElementById(`case-image-confirm-${confirmedImageId}`)?.focus())
      : 0;
    function keydown(event: KeyboardEvent) {
      if (event.defaultPrevented || document.querySelector(".image-workbench")) return;
      if (event.key !== "Escape" || saving) return;
      event.preventDefault();
      if (confirmedImageId) {
        setImageDeleteConfirm(null);
        window.requestAnimationFrame(() => document.getElementById(`case-image-delete-${confirmedImageId}`)?.focus());
        return;
      }
      closeEditor();
    }
    window.addEventListener("keydown", keydown);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("keydown", keydown);
    };
  }, [editing, imageDeleteConfirm, saving]);

  function openEditor() {
    if (!detail) return;
    setImageDeleteConfirm(null);
    setImageFiles([]);
    setImageUploadProgress(null);
    setImageError("");
    setImageNotice("");
    setPendingImageDeletes([]);
    setEditorMaterials(null);
    setMaterialError("");
    setMaterialNotice("");
    setMaterialCreateMode("upload");
    setMaterialDraft({ label: "", kind: "link", url: "", previewUrl: "", file: null });
    setPendingMaterialCreates([]);
    setPendingMaterialRemoveIds([]);
    const initialImageOrder = detail.images.map((item) => item.id);
    imageOrderIdsRef.current = initialImageOrder;
    setImageOrderIds(initialImageOrder);
    setActiveDragImageId(null);
    setDraft({
      community: detail.community_name,
      room: detail.room ?? "",
      area: detail.area ?? "",
      style: detail.style ?? "",
      remark: detail.remark ?? "",
      coverImageId: detail.cover_image_id ?? detail.images[0]?.id ?? null,
    });
    setEditError("");
    setEditNotice("");
    setEditing(true);
    window.requestAnimationFrame(() => editorFirstFieldRef.current?.focus({ preventScroll: true }));
  }

  function closeEditor() {
    if (saving || deleting) return;
    setEditing(false);
    setEditError("");
    setImageDeleteConfirm(null);
    setImageFiles([]);
    setPendingImageDeletes([]);
    setImageUploadProgress(null);
    setImageError("");
    setImageNotice("");
    setEditorMaterials(null);
    setMaterialError("");
    setMaterialNotice("");
    setMaterialCreateMode("upload");
    setMaterialDraft({ label: "", kind: "link", url: "", previewUrl: "", file: null });
    setPendingMaterialCreates([]);
    setPendingMaterialRemoveIds([]);
    setActiveDragImageId(null);
    if (imageFileInputRef.current) imageFileInputRef.current.value = "";
    if (materialFileInputRef.current) materialFileInputRef.current.value = "";
    window.requestAnimationFrame(() => editButtonRef.current?.focus());
  }

  async function saveCase(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!detail || saving) return;
    if (materialDraft.label.trim() || materialDraft.url.trim() || materialDraft.previewUrl.trim() || materialDraft.file) {
      setMaterialError("附件新增区还有尚未加入待保存的内容，请先点击“加入待保存”或清空输入。");
      document.getElementById("case-attachment-editor-title")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const fileError = imageFiles.length ? archiveImageFileError(imageFiles, archiveKind) : "";
    if (fileError) {
      setImageError(fileError);
      return;
    }
    if (imageOrderIds.length + imageFiles.length < 1) {
      setImageError(`${copy.singular}至少需要保留一张图片，请先追加新图片或撤销待删除项。`);
      return;
    }
    if (imageOrderIds.length + imageFiles.length > ARCHIVE_UPLOAD_MAX_FILES) {
      setImageError(`每个${copy.singular}最多保留 ${ARCHIVE_UPLOAD_MAX_FILES} 张图片。`);
      return;
    }
    setSaving(true);
    setSaveStage("正在整理待保存更改");
    setImageUploadProgress(null);
    setEditError("");
    setEditNotice("");
    setImageError("");
    setMaterialError("");
    let serverMutated = false;
    let remainingImageDeletes = [...pendingImageDeletes];
    let remainingMaterialRemoveIds = [...pendingMaterialRemoveIds];
    let remainingMaterialCreates = [...pendingMaterialCreates];
    try {
      let version = detail.version;
      let addedImageIds: number[] = [];

      if (imageFiles.length) {
        setSaveStage("正在上传待新增图片");
        setImageUploadProgress(0);
        const uploadResult = archiveKind === "case"
          ? await api.addCaseImages(caseId, version, imageFiles, setImageUploadProgress)
          : await api.addMaterialCaseImages(caseId, version, imageFiles, setImageUploadProgress);
        version = uploadResult.version;
        addedImageIds = uploadResult.image_ids;
        serverMutated = true;
        setImageFiles([]);
        if (imageFileInputRef.current) imageFileInputRef.current.value = "";
      }

      for (const item of pendingImageDeletes) {
        setSaveStage(`正在删除图片：${item.file_name}`);
        const result = archiveKind === "case"
          ? await api.deleteCaseImage(caseId, item.id, version)
          : await api.deleteMaterialCaseImage(caseId, item.id, version);
        version = result.version;
        serverMutated = true;
        remainingImageDeletes = remainingImageDeletes.filter((candidate) => candidate.id !== item.id);
        setPendingImageDeletes(remainingImageDeletes);
      }

      const finalImageIds = [...imageOrderIds, ...addedImageIds];
      const finalCoverImageId = draft.coverImageId && finalImageIds.includes(draft.coverImageId)
        ? draft.coverImageId
        : finalImageIds[0] ?? null;
      setSaveStage("正在保存基本信息、封面与顺序");
      const updateBody = {
        version,
        room: draft.room.trim() || null,
        area: draft.area.trim() || null,
        style: draft.style.trim() || null,
        remark: draft.remark.trim() || null,
        cover_image_id: finalCoverImageId,
        image_ids: finalImageIds,
      };
      const updateResult = archiveKind === "case"
        ? await api.updateCase(caseId, updateBody)
        : await api.updateMaterialCase(caseId, { ...updateBody, community: draft.community.trim() || null });
      version = updateResult.version;
      serverMutated = true;

      for (const materialId of pendingMaterialRemoveIds) {
        const item = editorMaterials?.find((candidate) => candidate.id === materialId);
        if (!item) continue;
        const associations = archiveKind === "case" ? item.cases : item.material_cases;
        setSaveStage(`${associations.length > 1 ? "正在解除附件关联" : "正在删除附件"}：${item.label}`);
        if (associations.length > 1) {
          await api.updateMaterial(item.id, {
            version: item.version,
            label: item.label,
            target_url: item.target_url,
            preview_url: item.preview_url,
            case_ids: archiveKind === "case" ? associations.filter((candidate) => candidate.id !== caseId).map((candidate) => candidate.id) : [],
            material_case_ids: archiveKind === "material" ? associations.filter((candidate) => candidate.id !== caseId).map((candidate) => candidate.id) : [],
            published: Boolean(item.published),
          });
        } else {
          await api.deleteMaterial(item.id);
        }
        serverMutated = true;
        remainingMaterialRemoveIds = remainingMaterialRemoveIds.filter((id) => id !== materialId);
        setPendingMaterialRemoveIds(remainingMaterialRemoveIds);
      }

      for (const item of pendingMaterialCreates) {
        setSaveStage(`${item.mode === "upload" ? "正在上传附件" : "正在登记链接"}：${item.label}`);
        if (item.mode === "upload") {
          await api.uploadMaterial(
            item.file, item.label, archiveKind === "case" ? [caseId] : [], undefined,
            archiveKind, [], archiveKind === "material" ? [caseId] : [],
          );
        } else {
          await api.createLinkMaterial({
            scope: archiveKind,
            kind: item.kind,
            label: item.label,
            url: item.url,
            preview_url: item.previewUrl,
            case_ids: archiveKind === "case" ? [caseId] : [],
            material_case_ids: archiveKind === "material" ? [caseId] : [],
          });
        }
        serverMutated = true;
        remainingMaterialCreates = remainingMaterialCreates.filter((candidate) => candidate.tempId !== item.tempId);
        setPendingMaterialCreates(remainingMaterialCreates);
      }

      setSaveStage(`正在刷新${copy.singular}`);
      const refreshed = archiveKind === "case" ? await api.caseDetail(caseId) : await api.materialCaseDetail(caseId);
      setDetail(refreshed);
      const refreshedImageOrder = refreshed.images.map((item) => item.id);
      imageOrderIdsRef.current = refreshedImageOrder;
      setImageOrderIds(refreshedImageOrder);
      const coverIndex = refreshed.images.findIndex((item) => item.id === refreshed.cover_image_id);
      if (coverIndex >= 0) setActiveImage(coverIndex);
      setEditing(false);
      setActiveDragImageId(null);
      setImageFiles([]);
      setPendingImageDeletes([]);
      setPendingMaterialCreates([]);
      setPendingMaterialRemoveIds([]);
      setEditorMaterials(null);
      setImageError("");
      setImageNotice("");
      setMaterialError("");
      setMaterialNotice("");
      setEditNotice("基本信息、图片和附件已统一保存。");
      onChanged();
      window.requestAnimationFrame(() => editButtonRef.current?.focus());
    } catch (reason) {
      if (serverMutated) {
        try {
          const refreshed = archiveKind === "case" ? await api.caseDetail(caseId) : await api.materialCaseDetail(caseId);
          setDetail(refreshed);
          const refreshedIds = refreshed.images.map((item) => item.id);
          imageOrderIdsRef.current = refreshedIds;
          setImageOrderIds(refreshedIds);
          setPendingImageDeletes([]);
          setImageFiles([]);
          if (imageFileInputRef.current) imageFileInputRef.current.value = "";
          const materials = await api.adminMaterials("", archiveKind);
          setEditorMaterials(materials.filter((item) => (archiveKind === "case" ? item.cases : item.material_cases).some((candidate) => candidate.id === caseId)));
        } catch {
          // The original failure is the useful message; a manual retry will reload current state.
        }
      }
      setEditError(
        serverMutated
          ? `保存未完整完成，已重新读取成功写入的部分。请检查后继续保存：${messageFrom(reason)}`
          : `保存失败，所有待保存内容仍保留在当前页面：${messageFrom(reason)}`,
      );
    } finally {
      setSaving(false);
      setSaveStage("");
      setImageUploadProgress(null);
    }
  }

  function closeImageDeleteConfirm() {
    if (!imageDeleteConfirm || saving) return;
    const imageId = imageDeleteConfirm.id;
    setImageDeleteConfirm(null);
    window.requestAnimationFrame(() => document.getElementById(`case-image-delete-${imageId}`)?.focus());
  }

  function beginImageSort(event: DragStartEvent) {
    setActiveDragImageId(Number(event.active.id));
    setImageDeleteConfirm(null);
    setImageError("");
  }

  function finishImageSort(event: DragEndEvent) {
    const imageId = Number(event.active.id);
    const targetId = event.over ? Number(event.over.id) : null;
    setActiveDragImageId(null);
    if (targetId !== null && targetId !== imageId) {
      const current = imageOrderIdsRef.current;
      const from = current.indexOf(imageId);
      const to = current.indexOf(targetId);
      if (from >= 0 && to >= 0) {
        const next = arrayMove(current, from, to);
        imageOrderIdsRef.current = next;
        setImageOrderIds(next);
        setImageNotice(`已移到第 ${to + 1} 张，保存案例后生效。`);
      }
    }
    window.requestAnimationFrame(() => document.getElementById(`case-image-sort-handle-${imageId}`)?.focus());
  }

  function cancelImageSort() {
    const imageId = activeDragImageId;
    setActiveDragImageId(null);
    setImageNotice("已取消本次拖动，图片顺序未改变。");
    if (imageId !== null) {
      window.requestAnimationFrame(() => document.getElementById(`case-image-sort-handle-${imageId}`)?.focus());
    }
  }

  function stageImageDelete(item: CaseImage) {
    if (imageOrderIds.length + imageFiles.length <= 1) {
      setImageError("案例至少需要保留一张图片，请先追加新图片再移除当前图片。");
      return;
    }
    const nextOrder = imageOrderIds.filter((id) => id !== item.id);
    imageOrderIdsRef.current = nextOrder;
    setImageOrderIds(nextOrder);
    setPendingImageDeletes((items) => [...items, item]);
    setDraft((value) => value.coverImageId === item.id
      ? { ...value, coverImageId: nextOrder[0] ?? null }
      : value);
    setImageDeleteConfirm(null);
    setImageError("");
    setImageNotice(`“${item.file_name}”已加入待删除，取消编辑不会删除。`);
  }

  function undoImageDelete(item: CaseImage) {
    const originalIndex = detail?.images.findIndex((candidate) => candidate.id === item.id) ?? -1;
    const nextOrder = [...imageOrderIds];
    nextOrder.splice(Math.max(0, Math.min(originalIndex, nextOrder.length)), 0, item.id);
    imageOrderIdsRef.current = nextOrder;
    setImageOrderIds(nextOrder);
    setPendingImageDeletes((items) => items.filter((candidate) => candidate.id !== item.id));
    setImageNotice(`已撤销删除“${item.file_name}”。`);
  }

  function stageMaterialCreate() {
    const label = materialDraft.label.trim();
    setMaterialError("");
    setMaterialNotice("");
    if (!label) {
      setMaterialError("请填写资料名称。");
      return;
    }
    const tempId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    if (materialCreateMode === "upload") {
      const file = materialDraft.file;
      if (!file?.name) {
        setMaterialError("请选择要上传的附件。");
        return;
      }
      if (file.size > 1024 * 1024 * 1024) {
        setMaterialError("单个附件不能超过 1 GB。");
        return;
      }
      setPendingMaterialCreates((items) => [...items, { tempId, mode: "upload", label, file }]);
    } else {
      const url = materialDraft.url.trim();
      const previewUrl = materialDraft.previewUrl.trim();
      try {
        const parsed = new URL(url);
        if (!/^https?:$/.test(parsed.protocol)) throw new Error();
        if (previewUrl) {
          const preview = new URL(previewUrl);
          if (!/^https?:$/.test(preview.protocol)) throw new Error();
        }
      } catch {
        setMaterialError("请输入以 http:// 或 https:// 开头的有效链接。");
        return;
      }
      setPendingMaterialCreates((items) => [...items, {
        tempId,
        mode: "link",
        label,
        kind: materialDraft.kind,
        url,
        previewUrl: materialDraft.kind === "panorama" ? previewUrl || null : null,
      }]);
    }
    setMaterialDraft((value) => ({ ...value, label: "", url: "", previewUrl: "", file: null }));
    if (materialFileInputRef.current) materialFileInputRef.current.value = "";
    setMaterialNotice(`“${label}”已加入待新增，保存全部更改后上传。`);
  }

  if (error) return <ErrorState message={error} retry={() => setReloadKey((value) => value + 1)} />;
  if (!detail) return <LoadingState label={`正在打开${copy.singular}`} />;
  const image = detail.images[activeImage];
  const orderedEditorImages = imageOrderIds
    .map((id) => detail.images.find((item) => item.id === id))
    .filter((item): item is CaseImage => Boolean(item));
  const editorPreviewIndex = editorPreviewImage
    ? orderedEditorImages.findIndex((item) => item.id === editorPreviewImage.id)
    : -1;
  const imageOrderChanged = detail.images.length !== imageOrderIds.length
    || detail.images.some((item, index) => imageOrderIds[index] !== item.id);
  const basicInfoChanged = draft.community !== detail.community_name
    || draft.room !== (detail.room ?? "")
    || draft.area !== (detail.area ?? "")
    || draft.style !== (detail.style ?? "")
    || draft.remark !== (detail.remark ?? "");
  const coverChanged = draft.coverImageId !== detail.cover_image_id;
  const pendingChangeCount = Number(basicInfoChanged)
    + Number(imageOrderChanged || coverChanged || imageFiles.length > 0 || pendingImageDeletes.length > 0)
    + Number(pendingMaterialCreates.length > 0 || pendingMaterialRemoveIds.length > 0);
  const activeDragImage = activeDragImageId === null
    ? null
    : orderedEditorImages.find((item) => item.id === activeDragImageId) ?? null;
  const activeDragIndex = activeDragImageId === null ? -1 : imageOrderIds.indexOf(activeDragImageId);
  const panoramaMaterials = archiveKind === "case"
    ? detail.materials.filter((item) => item.kind === "panorama")
    : [];
  const videoMaterials = archiveKind === "case"
    ? detail.materials.filter((item) => item.kind === "douyin")
    : [];
  const listedMaterials = archiveKind === "case"
    ? detail.materials.filter((item) => item.kind !== "panorama" && item.kind !== "douyin")
    : detail.materials;
  const currentDetail = detail;

  async function deleteCurrentRecord() {
    if (deleting) return;
    setDeleting(true);
    setEditError("");
    try {
      if (archiveKind === "case") await api.deleteCase(currentDetail.id, currentDetail.version);
      else await api.deleteMaterialCase(currentDetail.id, currentDetail.version);
      onChanged();
      onDeleted?.();
    } catch (reason) {
      setEditError(messageFrom(reason));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="case-detail">
      <header className="detail-head">
        <button className="back-action" type="button" disabled={editing && (saving || deleting)} onClick={editing ? closeEditor : onBack}>
          <ArrowLeft aria-hidden="true" /> {editing ? `返回${copy.singular}详情` : backLabel || `返回${copy.singular}`}
        </button>
        <div>
          <span>{detail.community_name}</span>
          <h1>{caseDisplayTitle(detail, archiveKind)}</h1>
        </div>
        <div className="detail-tools">
          <span className="detail-position">{activeImage + 1} / {detail.images.length}</span>
          {isAdmin && (editing
            ? <button className="detail-edit-action detail-edit-placeholder" type="button" disabled tabIndex={-1} aria-hidden="true"><Pencil aria-hidden="true" /> 编辑{copy.singular}</button>
            : (
              <button
                ref={editButtonRef}
                className="detail-edit-action"
                type="button"
                onClick={openEditor}
                disabled={saving}
                aria-expanded="false"
                aria-controls="case-editor-workspace"
              >
                <Pencil aria-hidden="true" /> 编辑{copy.singular}
              </button>
            ))}
        </div>
      </header>

      {(editNotice || externalNotice) && (
        <ActionFeedback
          className="detail-update-notice"
          message={editNotice || externalNotice}
          tone={editNotice ? "success" : externalNoticeTone}
          onDismiss={editNotice ? () => setEditNotice("") : () => onDismissExternalNotice?.()}
        />
      )}

      {editError && !editing && <div className="form-error detail-action-error" role="alert"><CircleAlert aria-hidden="true" /> {editError}</div>}

      {isAdmin && editing && (
        <div id="case-editor-workspace" className="case-editor-workspace">
          <form className="case-editor" onSubmit={saveCase}>
            <header className="case-editor-head">
              <div>
                <h2>编辑{copy.singular}</h2>
                <p>基本信息、图片与附件共用同一份草稿；取消不会写入服务器，保存后统一生效。</p>
              </div>
              <span className={pendingChangeCount ? "pending" : "saved"}>{pendingChangeCount ? `${pendingChangeCount} 类待保存` : "尚未修改"}</span>
            </header>

            {editError && <div className="form-error" role="alert"><CircleAlert aria-hidden="true" /> {editError}</div>}

            <section className="case-editor-section case-basic-editor" aria-labelledby="case-basic-editor-title">
              <header>
                <div>
                  <span className="case-editor-section-mark"><Pencil aria-hidden="true" /></span>
                  <div><h3 id="case-basic-editor-title">基本信息</h3><p>维护{copy.group}、{copy.room}、{copy.area}、{copy.style}和内部备注，不改变已上传文件名。</p></div>
                </div>
                <strong>{basicInfoChanged ? "待保存" : "未修改"}</strong>
              </header>
              <div className="case-editor-fields">
                {archiveKind === "material" && <label><span>{copy.group}</span><input ref={editorFirstFieldRef} value={draft.community} onChange={(event) => setDraft((value) => ({ ...value, community: event.target.value }))} required autoComplete="off" /></label>}
                <label><span>{copy.room}</span><input ref={archiveKind === "case" ? editorFirstFieldRef : undefined} value={draft.room} onChange={(event) => setDraft((value) => ({ ...value, room: event.target.value }))} placeholder={archiveKind === "case" ? "研发案例可留空" : "请输入素材名称"} required={archiveKind === "material"} autoComplete="off" /></label>
                <label><span>{copy.area}</span><input value={draft.area} onChange={(event) => setDraft((value) => ({ ...value, area: event.target.value }))} placeholder={archiveKind === "case" ? "例如 128㎡" : "可留空"} autoComplete="off" /></label>
                <label><span>{copy.style}</span><input value={draft.style} onChange={(event) => setDraft((value) => ({ ...value, style: event.target.value }))} placeholder="可留空" autoComplete="off" /></label>
                <label className="case-editor-remark"><span>备注</span><textarea value={draft.remark} onChange={(event) => setDraft((value) => ({ ...value, remark: event.target.value }))} placeholder={`记录需要长期保留的${copy.singular}说明`} /></label>
              </div>
            </section>

            <section className="case-editor-section case-image-manager" aria-labelledby="case-image-editor-title">
              <header>
                <div>
                  <span className="case-editor-section-mark"><Images aria-hidden="true" /></span>
                  <div><h3 id="case-image-editor-title">图片编辑</h3><p>追加图片、调整顺序、设置封面；删除只先标记，保存全部更改后执行。</p></div>
                </div>
                <strong>{imageOrderIds.length + imageFiles.length} 张{pendingImageDeletes.length ? ` · 待删除 ${pendingImageDeletes.length}` : ""}</strong>
              </header>

              <div id="case-image-sort-help" className="case-image-sort-guide">
                <GripVertical aria-hidden="true" />
                <div><strong>图片卡片可直接拖动排序</strong><p>电脑拖动手柄；手机长按后移动，朱砂框表示放置位置。键盘按空格抓取、方向键移动，再按空格放下。</p></div>
                {imageOrderChanged && <span className="pending">顺序待保存</span>}
              </div>

              <div className="case-image-upload">
                <label><span>追加{copy.image}</span><input ref={imageFileInputRef} type="file" accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" multiple disabled={saving} onChange={(event) => { const selectedFiles = naturalSortFiles(Array.from(event.currentTarget.files ?? [])); setImageFiles(selectedFiles); setImageUploadProgress(null); setImageError(selectedFiles.length ? archiveImageFileError(selectedFiles, archiveKind) : ""); setImageNotice(selectedFiles.length ? `已选择 ${selectedFiles.length} 张，可在上传前预览和排序。` : ""); }} /><small>支持 PNG、JPG、JPEG、WebP；最多 {ARCHIVE_UPLOAD_MAX_FILES} 张，可在上传前检查顺序。</small></label>
                <div>
                  <span>{imageFiles.length ? `${imageFiles.length} 张待新增 · ${formatBytes(imageFiles.reduce((sum, file) => sum + file.size, 0))}` : "尚未选择新图片"}</span>
                  {imageFiles.length > 0 && <button className="secondary-action" type="button" disabled={saving} onClick={() => { setImageFiles([]); setImageError(""); setImageNotice("已撤销待新增图片。"); if (imageFileInputRef.current) imageFileInputRef.current.value = ""; }}><X aria-hidden="true" />撤销待新增</button>}
                </div>
                <UploadImageOrganizer files={imageFiles} onChange={setImageFiles} disabled={saving} allowCover={false} />
                {imageUploadProgress !== null && <UploadProgress value={imageUploadProgress} />}
              </div>

              {imageError && <div className="form-error" role="alert"><CircleAlert aria-hidden="true" /> {imageError}</div>}
              {imageNotice && <div className="form-notice order-pending" role="status"><Check aria-hidden="true" />{imageNotice}</div>}

              {pendingImageDeletes.length > 0 && <div className="case-pending-list" aria-label="待删除图片"><strong>待删除图片</strong>{pendingImageDeletes.map((item) => <div key={item.id}><span>{item.file_name}</span><button className="text-action" type="button" disabled={saving} onClick={() => undoImageDelete(item)}>撤销</button></div>)}</div>}

              <DndContext sensors={imageSortSensors} modifiers={restrictDragToGrid} collisionDetection={closestCenter} autoScroll={{ threshold: { x: 0.12, y: 0.12 }, acceleration: 10, interval: 5 }} onDragStart={beginImageSort} onDragEnd={finishImageSort} onDragCancel={cancelImageSort} accessibility={{ screenReaderInstructions: { draggable: "按空格抓取图片，使用方向键调整位置，再按空格放下；按 Esc 取消。" } }}>
                <SortableContext items={imageOrderIds} strategy={rectSortingStrategy}><div className="case-image-grid is-direct-sorting" aria-label={`${copy.singular}图片、封面与排序列表`}>{orderedEditorImages.map((item, index) => <SortableArchiveImageCard key={item.id} item={item} index={index} coverImageId={draft.coverImageId} savedCoverImageId={detail.cover_image_id} disabled={saving} canDelete={imageOrderIds.length + imageFiles.length > 1} deleteConfirming={imageDeleteConfirm?.id === item.id} onPreview={(trigger) => { editorPreviewButtonRef.current = trigger; setEditorPreviewImage(item); }} onSetCover={() => { setDraft((value) => ({ ...value, coverImageId: item.id })); setImageError(""); setImageNotice(`已选择第 ${index + 1} 张作为封面，保存全部更改后生效。`); }} onDelete={() => { setImageDeleteConfirm(item); setImageError(""); setImageNotice(""); }} onCancelDelete={closeImageDeleteConfirm} onConfirmDelete={() => stageImageDelete(item)} />)}</div></SortableContext>
                {createPortal(<DragOverlay modifiers={restrictDragToGrid} dropAnimation={{ duration: 180, easing: "cubic-bezier(.16, 1, .3, 1)" }}>{activeDragImage && activeDragIndex >= 0 ? <ArchiveImageDragPreview item={activeDragImage} index={activeDragIndex} isCover={activeDragImage.id === draft.coverImageId} /> : null}</DragOverlay>, document.body)}
              </DndContext>
              {imageOrderIds.length + imageFiles.length <= 1 && <p className="case-image-minimum"><ShieldCheck aria-hidden="true" />{copy.singular}至少保留一张图片。</p>}
            </section>

            <section className="case-editor-section case-attachment-editor" aria-labelledby="case-attachment-editor-title">
              <header>
                <div><span className="case-editor-section-mark"><FileBox aria-hidden="true" /></span><div><h3 id="case-attachment-editor-title">附件编辑</h3><p>上传文件或登记全景图、视频和网页链接；所有操作与上方内容一起保存或取消。</p></div></div>
                <strong>{(editorMaterials?.length ?? 0) - pendingMaterialRemoveIds.length + pendingMaterialCreates.length} 项</strong>
              </header>
              {materialError && <div className="form-error" role="alert"><CircleAlert aria-hidden="true" /> {materialError}</div>}
              {materialNotice && <div className="form-notice order-pending" role="status"><Check aria-hidden="true" /> {materialNotice}</div>}
              <div className="case-attachment-editor-body">
                <div className="case-attachment-create" onKeyDown={(event) => { if (event.key === "Enter" && !(event.target instanceof HTMLTextAreaElement)) { event.preventDefault(); stageMaterialCreate(); } }}>
                  <div className="material-create-mode" role="group" aria-label="新增附件方式"><button type="button" disabled={saving} className={materialCreateMode === "upload" ? "active" : ""} aria-pressed={materialCreateMode === "upload"} onClick={() => setMaterialCreateMode("upload")}><Upload aria-hidden="true" />上传附件</button><button type="button" disabled={saving} className={materialCreateMode === "link" ? "active" : ""} aria-pressed={materialCreateMode === "link"} onClick={() => setMaterialCreateMode("link")}><Link aria-hidden="true" />登记链接</button></div>
                  <label><span>资料名称</span><input value={materialDraft.label} maxLength={160} disabled={saving} onChange={(event) => setMaterialDraft((value) => ({ ...value, label: event.target.value }))} placeholder={materialCreateMode === "upload" ? "例如：平面方案附件" : "例如：客厅全景展示"} /></label>
                  {materialCreateMode === "upload" ? <label className="material-file-field"><span>选择文件</span><input ref={materialFileInputRef} type="file" disabled={saving} onChange={(event) => setMaterialDraft((value) => ({ ...value, file: event.currentTarget.files?.[0] ?? null }))} /><small>文件写入应用资料区，不会写入原始效果图库；单文件上限 1 GB。</small></label> : <><label><span>链接类型</span><select value={materialDraft.kind} disabled={saving} onChange={(event) => setMaterialDraft((value) => ({ ...value, kind: event.target.value as typeof value.kind }))}><option value="link">普通网页</option><option value="panorama">全景图</option><option value="douyin">抖音视频</option></select></label><label><span>打开链接</span><input type="url" value={materialDraft.url} disabled={saving} onChange={(event) => setMaterialDraft((value) => ({ ...value, url: event.target.value }))} placeholder="https://" /></label>{materialDraft.kind === "panorama" && <label><span>嵌入预览链接（可留空）</span><input type="url" value={materialDraft.previewUrl} disabled={saving} onChange={(event) => setMaterialDraft((value) => ({ ...value, previewUrl: event.target.value }))} placeholder="留空则尝试使用打开链接预览" /></label>}</>}
                  <button className="secondary-action" type="button" disabled={saving} onClick={stageMaterialCreate}>{materialCreateMode === "upload" ? <Upload aria-hidden="true" /> : <Link aria-hidden="true" />}加入待保存</button>
                </div>

                <div className="case-attachment-ledger" aria-live="polite">
                  {!editorMaterials && !materialError ? <LoadingState label={`正在读取当前${copy.singular}附件`} /> : <>
                    {editorMaterials?.map((item) => { const linked = archiveKind === "case" ? item.cases : item.material_cases; const pendingRemoval = pendingMaterialRemoveIds.includes(item.id); const shared = linked.length > 1; return <article key={item.id} className={pendingRemoval ? "pending-remove" : ""}><MaterialKindIcon kind={item.kind} /><div><span>{pendingRemoval ? (shared ? "待解除关联" : "待永久删除") : materialKindLabel(item.kind)}{item.published ? "" : " · 员工端隐藏"}</span><strong>{item.label}</strong><small>{item.file_name || item.target_url}{shared ? ` · 共 ${linked.length} 个${copy.singular}使用` : ""}</small></div><div className="case-attachment-row-actions">{!pendingRemoval && <a className="secondary-action" href={item.open_url} target="_blank" rel="noreferrer">{item.file_name ? "下载" : "打开"}<ArrowUpRight aria-hidden="true" /></a>}<button className={pendingRemoval ? "secondary-action" : "case-material-remove-action"} type="button" disabled={saving} onClick={() => setPendingMaterialRemoveIds((ids) => pendingRemoval ? ids.filter((id) => id !== item.id) : [...ids, item.id])}>{pendingRemoval ? <RefreshCw aria-hidden="true" /> : shared ? <Link aria-hidden="true" /> : <Trash2 aria-hidden="true" />}{pendingRemoval ? "撤销" : shared ? "标记解除" : "标记删除"}</button></div></article>; })}
                    {pendingMaterialCreates.map((item) => <article key={item.tempId} className="pending-new">{item.mode === "upload" ? <FileBox aria-hidden="true" /> : <MaterialKindIcon kind={item.kind} />}<div><span>待新增 · {item.mode === "upload" ? "附件" : materialKindLabel(item.kind)}</span><strong>{item.label}</strong><small>{item.mode === "upload" ? `${item.file.name} · ${formatBytes(item.file.size)}` : item.url}</small></div><div className="case-attachment-row-actions"><button className="secondary-action" type="button" disabled={saving} onClick={() => setPendingMaterialCreates((items) => items.filter((candidate) => candidate.tempId !== item.tempId))}><X aria-hidden="true" />撤销</button></div></article>)}
                    {!editorMaterials?.length && !pendingMaterialCreates.length && <div className="case-attachment-empty"><FileBox aria-hidden="true" /><strong>当前{copy.singular}还没有附件</strong><p>在左侧选择文件或填写链接，再加入待保存。</p></div>}
                  </>}
                </div>
              </div>
            </section>

            <footer className="case-editor-actions">
              <p aria-live="polite">{saving ? saveStage : pendingChangeCount ? `共有 ${pendingChangeCount} 类更改等待统一保存` : "修改基本信息、图片或附件后，可在这里一次保存"}</p>
              <div><ArchiveDeleteAction label={caseDisplayTitle(detail, archiveKind)} imageCount={detail.image_count} busy={deleting} onConfirm={deleteCurrentRecord} /><button className="secondary-action" type="button" onClick={closeEditor} disabled={saving || deleting}>取消编辑</button><button className="primary-action" type="submit" disabled={saving || deleting || !pendingChangeCount}>{saving ? <LoaderCircle className="spin" aria-hidden="true" /> : <Save aria-hidden="true" />}{saving ? "正在保存" : "保存全部更改"}</button></div>
            </footer>
          </form>
        </div>
      )}

      {!editing && <>
      <DetailGallery
        images={detail.images}
        activeImage={activeImage}
        coverImageId={detail.cover_image_id}
        fallbackAlt={detail.source_folder}
        galleryLabel={`本${copy.singular}全部图片`}
        previewButtonRef={previewButtonRef}
        onOpen={() => setLightbox(true)}
        onSelect={setActiveImage}
        meta={<aside className="detail-meta">
          <div>
            <span>当前{copy.singular}</span>
            <strong>{image?.file_name}</strong>
            <small>{image ? formatBytes(image.size_bytes) : ""}</small>
          </div>
          <dl>
            <div><dt>{copy.group}</dt><dd>{detail.community_name}</dd></div>
            <div><dt>{copy.room}</dt><dd>{detail.room || (archiveKind === "case" ? "研发案例，无房号" : "未命名")}</dd></div>
            <div><dt>{copy.area}</dt><dd>{archiveKind === "case" ? formatArea(detail.area) : detail.area || "未登记"}</dd></div>
            <div><dt>{copy.style}</dt><dd>{detail.style || "未登记"}</dd></div>
            {archiveKind === "case" && panoramaMaterials.length > 0 && (
              <div className="detail-panorama-row">
                <dt>全景</dt>
                <dd>
                  {panoramaMaterials.length === 1 ? (
                    <button
                      ref={panoramaHeaderRef}
                      className="detail-panorama-link"
                      type="button"
                      onClick={(event) => {
                        panoramaReturnRef.current = event.currentTarget;
                        setPanoramaPreview(panoramaMaterials[0]);
                      }}
                    >
                      <Aperture aria-hidden="true" /> 查看
                    </button>
                  ) : (
                    <details className="detail-panorama-menu" onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        event.preventDefault();
                        event.currentTarget.removeAttribute("open");
                        event.currentTarget.querySelector("summary")?.focus();
                      }
                    }}>
                      <summary ref={panoramaSummaryRef}><Aperture aria-hidden="true" /> 选择查看 <ChevronDown aria-hidden="true" /></summary>
                      <div>
                        {panoramaMaterials.map((item) => (
                          <button
                            type="button"
                            key={item.id}
                            onClick={(event) => {
                              const menu = event.currentTarget.closest("details");
                              panoramaReturnRef.current = menu?.querySelector<HTMLElement>("summary") ?? null;
                              menu?.removeAttribute("open");
                              setPanoramaPreview(item);
                            }}
                          >
                            <span>{item.label}</span><ArrowUpRight aria-hidden="true" />
                          </button>
                        ))}
                      </div>
                    </details>
                  )}
                </dd>
              </div>
            )}
            {archiveKind === "case" && videoMaterials.length > 0 && (
              <div className="detail-video-row">
                <dt>视频</dt>
                <dd>
                  {videoMaterials.length === 1 ? (
                    <a className="detail-video-link" href={videoMaterials[0].open_url} target="_blank" rel="noreferrer">
                      <Video aria-hidden="true" /> 查看
                    </a>
                  ) : (
                    <details className="detail-video-menu" onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        event.preventDefault();
                        event.currentTarget.removeAttribute("open");
                        event.currentTarget.querySelector("summary")?.focus();
                      }
                    }}>
                      <summary><Video aria-hidden="true" /> {videoMaterials.length}项 <ChevronDown aria-hidden="true" /></summary>
                      <div>
                        {videoMaterials.map((item) => (
                          <a href={item.open_url} target="_blank" rel="noreferrer" key={item.id}>
                            <span>{item.label}</span><ArrowUpRight aria-hidden="true" />
                          </a>
                        ))}
                      </div>
                    </details>
                  )}
                </dd>
              </div>
            )}
            {detail.remark && <div><dt>备注</dt><dd>{detail.remark}</dd></div>}
          </dl>
        </aside>}
      />

      {listedMaterials.length > 0 && (
        <section className="attachments-section case-materials-section">
          <div>
            <h2>{copy.singular}资料</h2>
          </div>
          <div className="attachment-list material-list">
              {listedMaterials.map((item) => {
                const meta = [
                  materialKindLabel(item.kind),
                  item.file_name ? item.file_name.split(".").at(-1)?.toUpperCase() : null,
                  item.size_bytes ? formatBytes(item.size_bytes) : null,
                  item.association_count > 1 ? `关联 ${item.association_count} 个${copy.singular}` : null,
                ].filter(Boolean).join(" · ");
                if (item.kind === "cad" || item.kind === "cad_archive") {
                  return (
                    <button
                      className="material-card"
                      type="button"
                      key={item.id}
                      onClick={(event) => setDownloadPrompt({ material: item, returnTarget: event.currentTarget })}
                    >
                      <MaterialKindIcon kind={item.kind} />
                      <span><b>{item.label}</b><small>{meta}</small></span>
                      <span>下载 <Download aria-hidden="true" /></span>
                    </button>
                  );
                }
                return (
                  <a href={item.open_url} target="_blank" rel="noreferrer" key={item.id}>
                    <MaterialKindIcon kind={item.kind} />
                    <span><b>{item.label}</b><small>{meta}</small></span>
                    <span>打开 <ArrowUpRight aria-hidden="true" /></span>
                  </a>
                );
              })}
            </div>
        </section>
      )}
      </>}

      {downloadPrompt && (
        <MaterialDownloadDialog prompt={downloadPrompt} onClose={() => setDownloadPrompt(null)} />
      )}

      {panoramaPreview && (
        <div ref={panoramaDialogRef} className="panorama-dialog" role="dialog" aria-modal="true" aria-label={`预览全景图：${panoramaPreview.label}`}>
          <header>
            <div><span>全景图预览</span><h2>{panoramaPreview.label}</h2></div>
            <div>
              <a className="secondary-action" href={panoramaPreview.open_url} target="_blank" rel="noreferrer">新窗口打开 <ArrowUpRight aria-hidden="true" /></a>
              <button ref={panoramaCloseRef} type="button" onClick={() => setPanoramaPreview(null)} aria-label="关闭全景图预览"><X aria-hidden="true" /></button>
            </div>
          </header>
          <div className="panorama-frame">
            <iframe
              src={panoramaPreview.preview_url ?? panoramaPreview.open_url}
              title={panoramaPreview.label}
              sandbox="allow-forms allow-popups allow-same-origin allow-scripts"
              referrerPolicy="no-referrer"
              allow="fullscreen; gyroscope; accelerometer; xr-spatial-tracking"
            />
            <p>若预览方禁止嵌入，请使用上方“新窗口打开”。</p>
          </div>
        </div>
      )}

      {lightbox && image && (
        <ImageWorkbenchLightbox
          src={image.preview_url}
          originalSrc={image.original_url}
          fileName={image.file_name}
          caption={`${image.file_name} · ${activeImage + 1}/${detail.images.length}`}
          dialogLabel="大图查看与标注"
          previousDisabled={activeImage === 0}
          nextDisabled={activeImage === detail.images.length - 1}
          onPrevious={() => setActiveImage((value) => Math.max(0, value - 1))}
          onNext={() => setActiveImage((value) => Math.min(detail.images.length - 1, value + 1))}
          onClose={() => setLightbox(false)}
          returnFocusRef={previewButtonRef}
        />
      )}
      {editorPreviewImage && (
        <ImageWorkbenchLightbox
          src={editorPreviewImage.preview_url}
          originalSrc={editorPreviewImage.original_url}
          fileName={editorPreviewImage.file_name}
          caption={`${editorPreviewImage.file_name} · ${editorPreviewIndex + 1}/${orderedEditorImages.length} · 封面选择预览`}
          dialogLabel="封面大图预览"
          previousDisabled={editorPreviewIndex <= 0}
          nextDisabled={editorPreviewIndex < 0 || editorPreviewIndex >= orderedEditorImages.length - 1}
          onPrevious={() => setEditorPreviewImage(orderedEditorImages[Math.max(0, editorPreviewIndex - 1)] ?? null)}
          onNext={() => setEditorPreviewImage(orderedEditorImages[Math.min(orderedEditorImages.length - 1, editorPreviewIndex + 1)] ?? null)}
          onClose={() => setEditorPreviewImage(null)}
          returnFocusRef={editorPreviewButtonRef}
        />
      )}
    </div>
  );
}

function amapMarkerUrl(point: Pick<MapPoint, "name" | "lng" | "lat">): string {
  return `https://uri.amap.com/marker?position=${point.lng},${point.lat}&name=${encodeURIComponent(point.name)}`;
}

function loadAmap(config: ClientConfig["amap"]): Promise<AMapNamespace> {
  if (window.AMap) return Promise.resolve(window.AMap);
  if (amapLoadPromise) return amapLoadPromise;
  window._AMapSecurityConfig = {
    serviceHost: new URL(config.service_host, window.location.origin).toString().replace(/\/$/, ""),
  };
  const pending = new Promise<AMapNamespace>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-effect-amap]");
    const script = existing ?? document.createElement("script");
    if (!existing) {
      script.dataset.effectAmap = "true";
      script.src = `https://webapi.amap.com/maps?v=2.0&key=${encodeURIComponent(config.js_key)}`;
      script.async = true;
      document.head.append(script);
    }
    script.addEventListener("load", () => {
      if (window.AMap) resolve(window.AMap);
      else reject(new Error("高德地图脚本没有正确初始化。"));
    }, { once: true });
    script.addEventListener("error", () => reject(new Error("高德地图脚本加载失败。")), {
      once: true,
    });
  });
  const guarded = pending.catch((error) => {
    amapLoadPromise = null;
    throw error;
  });
  amapLoadPromise = guarded;
  return guarded;
}

function MapCanvas({
  config,
  points,
  selectedId,
  onSelect,
  label,
}: {
  config: ClientConfig["amap"] | null;
  points: MapPoint[];
  selectedId: number | string | null;
  onSelect: (id: number | string) => void;
  label: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<AMapMapLike | null>(null);
  const namespaceRef = useRef<AMapNamespace | null>(null);
  const onSelectRef = useRef(onSelect);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    if (!config?.enabled || !containerRef.current) return;
    let active = true;
    let readyTimer: number | undefined;
    let handleReady: (() => void) | null = null;
    setError("");
    loadAmap(config)
      .then((AMap) => {
        if (!active || !containerRef.current) return;
        namespaceRef.current = AMap;
        const map = new AMap.Map(containerRef.current, {
          viewMode: "2D",
          zoom: 12,
          center: HAILAR_CENTER,
          resizeEnable: true,
          showIndoorMap: false,
        });
        mapRef.current = map;
        handleReady = () => {
          if (!active) return;
          map.resize?.();
          setReady(true);
        };
        map.on?.("complete", handleReady);
        readyTimer = window.setTimeout(handleReady, 900);
      })
      .catch((reason) => active && setError(messageFrom(reason)));
    return () => {
      active = false;
      if (readyTimer !== undefined) window.clearTimeout(readyTimer);
      if (handleReady) mapRef.current?.off?.("complete", handleReady);
      setReady(false);
      mapRef.current?.destroy();
      mapRef.current = null;
      namespaceRef.current = null;
    };
  }, [config]);

  useEffect(() => {
    const container = containerRef.current;
    if (!config?.enabled || !container || typeof ResizeObserver === "undefined") return;
    let frame: number | undefined;
    const observer = new ResizeObserver(() => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => mapRef.current?.resize?.());
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
      if (frame !== undefined) window.cancelAnimationFrame(frame);
    };
  }, [config?.enabled]);

  useEffect(() => {
    const map = mapRef.current;
    const AMap = namespaceRef.current;
    if (!ready || !map || !AMap) return;
    const frame = window.requestAnimationFrame(() => {
      map.resize?.();
      map.clearMap();
      const markerEntries = points.map((point) => {
        const selected = String(point.id) === String(selectedId);
        const hasCaseCount = typeof point.case_count === "number";
        const markerContent = document.createElement("button");
        const markerIndex = document.createElement("span");
        markerContent.type = "button";
        markerContent.className = `amap-site-marker ${point.place_type === "store" ? "store" : "community"} ${selected ? "selected" : ""}`;
        markerContent.setAttribute("role", "button");
        markerContent.setAttribute(
          "aria-label",
          point.place_type === "store"
            ? `${point.name}，易高门店${selected ? "，当前选中" : ""}`
            : hasCaseCount
              ? `${point.name}，${point.case_count} 户案例${selected ? "，当前选中" : ""}`
              : `${point.name}，地图点位${selected ? "，当前选中" : ""}`,
        );
        if (point.place_type === "store") {
          markerIndex.className = "store-star";
          const star = document.createElementNS("http://www.w3.org/2000/svg", "svg");
          const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
          star.setAttribute("viewBox", "0 0 24 24");
          star.setAttribute("aria-hidden", "true");
          path.setAttribute("d", STORE_MARK_PATH);
          star.append(path);
          markerIndex.append(star);
        } else if (hasCaseCount) {
          markerIndex.textContent = String(point.case_count);
        } else {
          markerIndex.className = "location-dot";
        }
        markerIndex.setAttribute("aria-hidden", "true");
        markerContent.append(markerIndex);
        markerContent.addEventListener("click", () => onSelectRef.current(point.id));
        const marker = new AMap.Marker({
          position: [point.lng, point.lat],
          anchor: "bottom-center",
          title: point.name,
          content: markerContent,
          zIndex: selected ? 200 : 100,
        });
        return { marker, point };
      });
      const markers = markerEntries.map((entry) => entry.marker);
      map.add(markers);
      const selected = points.find((point) => String(point.id) === String(selectedId));
      if (selected) {
        map.setCenter([selected.lng, selected.lat]);
        map.setZoom(16);
      } else {
        const hailarMarkers = markerEntries
          .filter((entry) => isInHailarFocusArea(entry.point))
          .map((entry) => entry.marker);
        if (hailarMarkers.length) {
          map.setFitView(hailarMarkers, false, [70, 70, 70, 70], 15);
        } else {
          map.setCenter([...HAILAR_CENTER]);
          map.setZoom(12);
        }
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [points, ready, selectedId]);

  if (!config?.enabled) {
    return (
      <div className="map-canvas map-canvas-disabled" role="status">
        <MapPinned aria-hidden="true" />
        <strong>高德地图尚未连接</strong>
        <p>地点台账仍可使用；在本机私密配置中填写 JS Key 和安全密钥后，这里会自动启用地图。</p>
      </div>
    );
  }

  return (
    <div className="map-canvas-shell">
      <div ref={containerRef} className="map-canvas" role="img" aria-label={label} />
      {!ready && !error && <span className="map-loading"><LoaderCircle className="spin" /> 正在铺开地图</span>}
      {error && <span className="map-load-error"><CircleAlert /> {error}</span>}
    </div>
  );
}

function EnterpriseDetailView({
  detail,
  backLabel,
  onBack,
  onEdit,
  initialActiveImage = 0,
  initialScrollTop = 0,
  notice = "",
  onDismissNotice,
}: {
  detail: EnterpriseDocumentDetail;
  backLabel: string;
  onBack: () => void;
  onEdit?: (state: { activeImage: number; scrollTop: number }) => void;
  initialActiveImage?: number;
  initialScrollTop?: number;
  notice?: string;
  onDismissNotice?: () => void;
}) {
  const [activeImage, setActiveImage] = useState(() => Math.min(Math.max(0, initialActiveImage), Math.max(0, detail.images.length - 1)));
  const [lightbox, setLightbox] = useState(false);
  const [panoramaPreview, setPanoramaPreview] = useState<CaseMaterial | null>(null);
  const [downloadPrompt, setDownloadPrompt] = useState<MaterialDownloadPrompt | null>(null);
  const previewButtonRef = useRef<HTMLButtonElement>(null);
  const panoramaDialogRef = useRef<HTMLDivElement>(null);
  const panoramaCloseRef = useRef<HTMLButtonElement>(null);
  const panoramaReturnRef = useRef<HTMLButtonElement | null>(null);
  const detailSurfaceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveImage(Math.min(Math.max(0, initialActiveImage), Math.max(0, detail.images.length - 1)));
    setLightbox(false);
    setPanoramaPreview(null);
    setDownloadPrompt(null);
  }, [detail.id, detail.images.length, initialActiveImage]);

  useLayoutEffect(() => {
    detailSurfaceRef.current?.scrollTo({ top: initialScrollTop, left: 0, behavior: "auto" });
  }, [detail.id, initialScrollTop]);

  useEffect(() => {
    if (lightbox || panoramaPreview) return;
    const lastImageIndex = Math.max(0, detail.images.length - 1);
    function keydown(event: KeyboardEvent) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target?.closest("input, textarea, select, [contenteditable='true']")) return;
      if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        event.preventDefault();
        setActiveImage((value) => Math.max(0, value - 1));
      } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        event.preventDefault();
        setActiveImage((value) => Math.min(lastImageIndex, value + 1));
      }
    }
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [detail.images.length, lightbox, panoramaPreview]);

  useEffect(() => {
    if (!panoramaPreview) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panoramaCloseRef.current?.focus();
    function keydown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setPanoramaPreview(null);
        return;
      }
      if (event.key !== "Tab" || !panoramaDialogRef.current) return;
      const controls = Array.from(panoramaDialogRef.current.querySelectorAll<HTMLElement>("button:not([disabled]), a[href]"));
      const first = controls[0];
      const last = controls.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    window.addEventListener("keydown", keydown);
    return () => {
      window.removeEventListener("keydown", keydown);
      document.body.style.overflow = previousOverflow;
      window.requestAnimationFrame(() => panoramaReturnRef.current?.focus());
    };
  }, [panoramaPreview]);

  const image = detail.images[activeImage];
  return (
    <div ref={detailSurfaceRef} className="case-detail">
      <header className="detail-head">
        <button className="back-action" type="button" onClick={onBack}><ArrowLeft aria-hidden="true" />{backLabel}</button>
        <div><span>{detail.category || "企业资料"}</span><h1>{detail.title}</h1></div>
        <div className="detail-tools">
          <span className="detail-position">{activeImage + 1} / {detail.images.length}</span>
          {onEdit && <button id="enterprise-detail-edit" className="detail-edit-action" type="button" onClick={() => onEdit({ activeImage, scrollTop: detailSurfaceRef.current?.scrollTop ?? 0 })}><Pencil aria-hidden="true" />编辑资料</button>}
        </div>
      </header>
      {notice && <ActionFeedback className="detail-update-notice" message={notice} onDismiss={() => onDismissNotice?.()} />}
      <DetailGallery
        images={detail.images}
        activeImage={activeImage}
        coverImageId={detail.cover_image_id}
        fallbackAlt={detail.title}
        galleryLabel="本资料全部图片"
        previewButtonRef={previewButtonRef}
        onOpen={() => setLightbox(true)}
        onSelect={setActiveImage}
        meta={<aside className="detail-meta">
          <div><span>当前资料</span><strong>{image?.file_name || detail.title}</strong><small>{image ? formatBytes(image.size_bytes) : ""}</small></div>
          <dl>
            <div><dt>名称</dt><dd>{detail.title}</dd></div>
            <div><dt>分类</dt><dd>{detail.category || "未分类"}</dd></div>
            <div><dt>图片</dt><dd>{detail.image_count} 张</dd></div>
            {detail.description && <div><dt>说明</dt><dd>{detail.description}</dd></div>}
          </dl>
        </aside>}
      />
      {detail.materials.length > 0 && <section className="attachments-section case-materials-section"><div><h2>企业资料附件</h2></div><div className="attachment-list material-list">{detail.materials.map((item) => {
        const meta = [materialKindLabel(item.kind), item.file_name ? item.file_name.split(".").at(-1)?.toUpperCase() : null, item.size_bytes ? formatBytes(item.size_bytes) : null, item.association_count > 1 ? `关联 ${item.association_count} 份企业资料` : null].filter(Boolean).join(" · ");
        if (item.kind === "panorama" && item.preview_url) return <button className="material-card" type="button" key={item.id} onClick={(event) => { panoramaReturnRef.current = event.currentTarget; setPanoramaPreview(item); }}><MaterialKindIcon kind={item.kind} /><span><b>{item.label}</b><small>{meta}</small></span><span>预览 <Eye aria-hidden="true" /></span></button>;
        if (item.kind === "cad" || item.kind === "cad_archive") return <button className="material-card" type="button" key={item.id} onClick={(event) => setDownloadPrompt({ material: item, returnTarget: event.currentTarget })}><MaterialKindIcon kind={item.kind} /><span><b>{item.label}</b><small>{meta}</small></span><span>下载 <Download aria-hidden="true" /></span></button>;
        return <a href={item.open_url} target="_blank" rel="noreferrer" key={item.id}><MaterialKindIcon kind={item.kind} /><span><b>{item.label}</b><small>{meta}</small></span><span>打开 <ArrowUpRight aria-hidden="true" /></span></a>;
      })}</div></section>}
      {downloadPrompt && <MaterialDownloadDialog prompt={downloadPrompt} onClose={() => setDownloadPrompt(null)} />}
      {panoramaPreview && <div ref={panoramaDialogRef} className="panorama-dialog" role="dialog" aria-modal="true" aria-label={`预览全景图：${panoramaPreview.label}`}><header><div><span>全景图预览</span><h2>{panoramaPreview.label}</h2></div><div><a className="secondary-action" href={panoramaPreview.open_url} target="_blank" rel="noreferrer">新窗口打开 <ArrowUpRight aria-hidden="true" /></a><button ref={panoramaCloseRef} type="button" onClick={() => setPanoramaPreview(null)} aria-label="关闭全景图预览"><X aria-hidden="true" /></button></div></header><div className="panorama-frame"><iframe src={panoramaPreview.preview_url ?? panoramaPreview.open_url} title={panoramaPreview.label} sandbox="allow-forms allow-popups allow-same-origin allow-scripts" referrerPolicy="no-referrer" allow="fullscreen; gyroscope; accelerometer; xr-spatial-tracking" /><p>若预览方禁止嵌入，请使用上方“新窗口打开”。</p></div></div>}
      {lightbox && image && (
        <ImageWorkbenchLightbox
          src={image.preview_url}
          originalSrc={image.original_url}
          fileName={image.file_name}
          caption={`${detail.title} · ${activeImage + 1}/${detail.images.length}`}
          dialogLabel="企业资料大图查看与标注"
          previousDisabled={activeImage === 0}
          nextDisabled={activeImage === detail.images.length - 1}
          onPrevious={() => setActiveImage((value) => Math.max(0, value - 1))}
          onNext={() => setActiveImage((value) => Math.min(detail.images.length - 1, value + 1))}
          onClose={() => setLightbox(false)}
          returnFocusRef={previewButtonRef}
        />
      )}
    </div>
  );
}

type FloorplanMode = "drawing" | "other";

function FloorplanLibrary() {
  const [data, setData] = useState<FloorplanLibraryData | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [query, setQuery] = useState("");
  const [communityId, setCommunityId] = useState<number | undefined>();
  const [mode, setMode] = useState<FloorplanMode>("drawing");
  const [recent, setRecent] = useState(false);
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<FloorplanFile | null>(null);
  const lastTriggerRef = useRef<HTMLButtonElement | null>(null);
  const imagePreviewButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.floorplanLibrary()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((reason) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : "户型资料读取失败");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const communities = useMemo<Community[]>(() => (data?.communities ?? []).map((item, index) => ({
    id: index + 1,
    name: item.name,
    case_count: item.file_count,
  })), [data]);
  const selectedCommunity = communities.find((item) => item.id === communityId);
  const items = useMemo(() => {
    if (!data) return [];
    const term = query.trim().toLocaleLowerCase("zh-CN");
    const source = recent
      ? recentIds.map((id) => data.items.find((item) => item.id === id)).filter((item): item is FloorplanFile => Boolean(item))
      : data.items;
    return source.filter((item) => {
      if (!recent && selectedCommunity && item.community !== selectedCommunity.name) return false;
      if (item.category !== mode) return false;
      if (!term) return true;
      return [item.title, item.file_name, item.community, item.summary, item.area, item.cad_version, item.building, item.remark, ...item.household_tags]
        .filter(Boolean).some((value) => String(value).toLocaleLowerCase("zh-CN").includes(term));
    });
  }, [data, mode, query, recent, recentIds, selectedCommunity]);

  const selected = data?.items.find((item) => item.id === selectedId) ?? null;
  const modeLabels: Record<FloorplanMode, string> = { drawing: "图纸文件", other: "其他资料" };

  function selectItem(item: FloorplanFile, trigger: HTMLButtonElement) {
    lastTriggerRef.current = trigger;
    setSelectedId(item.id);
    setRecentIds((ids) => [item.id, ...ids.filter((id) => id !== item.id)].slice(0, 30));
  }

  function closeFolio() {
    setImagePreview(null);
    setSelectedId(null);
    window.requestAnimationFrame(() => lastTriggerRef.current?.focus({ preventScroll: true }));
  }

  useEffect(() => {
    if (!selectedId || imagePreview) return;
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") closeFolio();
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [imagePreview, selectedId]);

  return <div className="archive-studio floorplan-archive-studio" aria-label="户型库">
    <aside className="project-index">
      <div className="index-heading"><h1>户型库</h1><p><span>{communities.length} 个小区</span><span>{data?.items.filter((item) => item.category === "drawing").length ?? 0} 个 DWG</span></p></div>
      <SearchField className="index-search" label="搜索户型" value={query} onChange={setQuery} placeholder="搜索小区、面积或文件名" clearLabel="清空户型搜索" />
      <label className="community-picker"><span>选择小区</span><select value={communityId ?? ""} onChange={(event) => { setRecent(false); setCommunityId(event.target.value ? Number(event.target.value) : undefined); }}><option value="">全部</option>{communities.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.case_count}</option>)}</select></label>
      <ArchiveIndexNavigation items={communities} groupLabel="小区" countLabel="份" selectedId={communityId} recent={recent} recentTotal={recentIds.length} onAll={() => { setCommunityId(undefined); setRecent(false); }} onRecent={() => { setCommunityId(undefined); setRecent(true); }} onSelect={(item) => { setRecent(false); setCommunityId(item.id); }} />
      <p className="floorplan-readonly"><LockKeyhole aria-hidden="true" />原始资料只读</p>
    </aside>
    <main className="project-board">
      <header className="board-heading"><div><h2>{recent ? "最近浏览" : selectedCommunity?.name ?? (query.trim() ? "搜索结果" : "全部户型")}</h2><p>选择资料，右侧查看{mode === "drawing" ? "图纸" : "文件"}信息</p></div><p>当前结果 <strong>{items.length}</strong> 份</p></header>
      <div className="floorplan-mode-tabs" role="tablist" aria-label="资料类型">{(Object.keys(modeLabels) as FloorplanMode[]).map((key) => <button key={key} type="button" role="tab" aria-selected={mode === key} className={mode === key ? "active" : ""} onClick={() => setMode(key)}>{modeLabels[key]}</button>)}</div>
      {loading && <LoadingState label="正在整理户型墙" />}
      {!loading && error && <ErrorState message={error} retry={() => setReloadKey((value) => value + 1)} />}
      {!loading && !error && (items.length ? <section className="project-grid" aria-label="户型墙">{items.map((item, index) => <FloorplanCard key={item.id} item={item} index={index + 1} selected={item.id === selectedId} onSelect={(trigger) => selectItem(item, trigger)} />)}</section> : <div className="empty-archive"><FolderSearch aria-hidden="true" /><h2>{recent ? "还没有最近浏览" : "没有找到对应户型"}</h2><p>{recent ? "打开一份户型资料后，会显示在这里。" : "试试减少关键词，或切换到全部小区。"}</p></div>)}
    </main>
    <aside className={`project-folio ${selected ? "ready" : "waiting"}`} aria-label="当前户型资料">
      <div className="folio-bar"><span>{selected?.category === "other" ? "FILE FOLIO" : "CAD FOLIO"}</span><span>{selected ? "已选择资料" : "等待选择"}</span>{selected && <button className="folio-close" type="button" onClick={closeFolio} aria-label="关闭当前资料"><X aria-hidden="true" /></button>}</div>
      {selected ? <div className="folio-content floorplan-folio-content" tabIndex={-1}>
        {selected.cover_url ? <button ref={imagePreviewButtonRef} className="folio-preview floorplan-folio-preview" type="button" onClick={() => setImagePreview(selected)} aria-label={`查看大图：${selected.title}`}><ImageSurface src={selected.cover_url} alt={`${selected.community}小区图`} eager /><span>查看大图 <Eye aria-hidden="true" /></span></button> : <div className="folio-preview floorplan-folio-preview"><CadPlaceholder item={selected} /></div>}
        <div className="folio-index"><span>当前资料</span><span>{selected.category === "drawing" ? "DWG FILE" : `${selected.file_type} FILE`}</span></div>
        <h2>{selected.title}</h2>
        <dl><div><dt>小区</dt><dd>{selected.community}</dd></div><div><dt>资料</dt><dd>{selected.category === "drawing" ? "图纸文件" : "其他资料"}</dd></div>{selected.category === "drawing" && <div><dt>内容</dt><dd>{floorplanKindText(selected.kinds)}</dd></div>}{selected.building && <div><dt>楼栋</dt><dd>{selected.building}</dd></div>}{selected.household_tags.length > 0 && <div><dt>户型</dt><dd>{selected.household_tags.join("、")}</dd></div>}<div><dt>格式</dt><dd>{selected.file_type}</dd></div>{selected.cad_version && <div><dt>版本</dt><dd>{selected.cad_version}</dd></div>}<div><dt>大小</dt><dd>{formatBytes(selected.size_bytes)}</dd></div><div><dt>文件</dt><dd title={selected.file_name}>{selected.file_name}</dd></div></dl>
        <div className="folio-actions"><a className="primary-action folio-action" href={selected.download_url}>下载{selected.category === "drawing" ? " DWG" : "文件"} <Download aria-hidden="true" /></a></div>
      </div> : <div className="folio-empty"><div className="folio-empty-scene"><span>CAD PLAN<br />ARCHIVE</span></div><div className="folio-empty-copy"><span /><h2>纵览小区户型，<br />预见空间新生。</h2><p>从左侧户型墙选择一份资料，文件信息将在这里展开。</p></div><ul className="folio-empty-pillars" aria-label="户型库内容"><li><Archive aria-hidden="true" /><span><b>小区归档</b><small>ESTATE</small></span></li><li><FileBox aria-hidden="true" /><span><b>户型图纸</b><small>PLANS</small></span></li><li><Aperture aria-hidden="true" /><span><b>空间尺度</b><small>SCALE</small></span></li><li><FolderSearch aria-hidden="true" /><span><b>相关资料</b><small>FILES</small></span></li></ul></div>}
    </aside>
    {imagePreview?.cover_url && <ImageWorkbenchLightbox src={imagePreview.cover_url} originalSrc={imagePreview.download_url} fileName={imagePreview.file_name} caption={`${imagePreview.community} · ${imagePreview.title}`} dialogLabel="户型库图片大图查看与标注" previousDisabled nextDisabled onPrevious={() => undefined} onNext={() => undefined} onClose={() => setImagePreview(null)} returnFocusRef={imagePreviewButtonRef} />}
  </div>;
}

function CadPlaceholder({ item }: { item: FloorplanFile }) {
  return <div className="cad-placeholder" aria-hidden="true"><span>{item.file_type}</span><b>{item.area || item.community}</b><small>{item.cad_version || item.summary}</small></div>;
}

function FloorplanCard({ item, index, selected, onSelect }: { item: FloorplanFile; index: number; selected: boolean; onSelect: (trigger: HTMLButtonElement) => void }) {
  return <article className="project-card" data-selected={selected}><button className="project-select" type="button" aria-pressed={selected} aria-label={`选择 ${item.title} 作为当前户型资料`} onClick={(event) => onSelect(event.currentTarget)}><span className="project-image-wrap">{item.cover_url ? <ImageSurface src={item.cover_url} alt={`${item.community}${item.summary}`} className="project-image" /> : <CadPlaceholder item={item} />}{selected && <span className="project-current">当前资料</span>}</span><span className="project-copy"><span><b>{item.title}</b><span className="project-facts"><em>{formatBytes(item.size_bytes)}</em></span></span><small>{String(index).padStart(3, "0")} · {item.community} · {item.file_type}{item.cad_version ? ` · ${item.cad_version}` : ""}</small></span></button></article>;
}

const FLOORPLAN_KIND_OPTIONS: ReadonlyArray<{ id: FloorplanKind; label: string }> = [
  { id: "floorplan", label: "户型图" },
  { id: "building", label: "建筑图" },
  { id: "measurement", label: "实测图" },
  { id: "other", label: "其他图纸" },
];

function floorplanKindText(kinds: FloorplanKind[]) {
  return FLOORPLAN_KIND_OPTIONS.filter((item) => kinds.includes(item.id)).map((item) => item.label).join("、") || "未标注";
}

function parseFloorplanTags(value: string) {
  return Array.from(new Set(value.replaceAll("，", ",").split(",").map((item) => item.trim()).filter(Boolean)));
}

function FloorplanManagementAdmin() {
  return (
    <ArchiveManagementModule
      idPrefix="floorplan"
      title="户型库管理"
      description="在同一处维护图纸文件、其他资料和检索信息。"
      informationLabel="图纸文件"
      informationIcon={FileBox}
      information={<FloorplanFilesAdmin category="drawing" />}
      materialsLabel="其他资料"
      materialsIcon={FileBox}
      materials={<FloorplanFilesAdmin category="other" />}
      className="archive-management-admin"
    />
  );
}

function FloorplanFilesAdmin({ category }: { category: "drawing" | "other" }) {
  const [data, setData] = useState<FloorplanLibraryData | null>(null);
  const [query, setQuery] = useState("");
  const [settledQuery, setSettledQuery] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editDirty, setEditDirty] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useTransientNotice();
  const [reloadKey, setReloadKey] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<FloorplanFile | null>(null);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const uploadTriggerRef = useRef<HTMLButtonElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const detailPreviewButtonRef = useRef<HTMLButtonElement>(null);
  const firstEditFieldRef = useRef<HTMLInputElement>(null);
  const filesAdminRef = useRef<HTMLElement>(null);
  const returnPositionRef = useRef({ scrollTop: 0, fileId: null as string | null });
  const shouldRestorePositionRef = useRef(false);
  const deleteFocusRef = useRef({ active: false, fileId: null as string | null });
  const categoryLabel = category === "drawing" ? "图纸文件" : "其他资料";

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSettledQuery(query.trim());
      setPage(1);
    }, 220);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let active = true;
    setError("");
    api.adminFloorplanLibrary()
      .then((result) => active && setData(result))
      .catch((reason) => active && setError(messageFrom(reason)));
    return () => { active = false; };
  }, [reloadKey]);

  const selected = data?.items.find((item) => item.id === selectedId) ?? null;
  const filteredItems = useMemo(() => {
    const term = settledQuery.toLocaleLowerCase("zh-CN");
    const categoryItems = data?.items.filter((item) => item.category === category) ?? [];
    if (!term) return categoryItems;
    return categoryItems.filter((item) => [
      item.title, item.file_name, item.community, item.building, item.remark,
      ...item.household_tags, floorplanKindText(item.kinds),
    ].filter(Boolean).some((value) => String(value).toLocaleLowerCase("zh-CN").includes(term)));
  }, [category, data, settledQuery]);
  const pageCount = Math.max(1, Math.ceil(filteredItems.length / 24));
  const items = filteredItems.slice((page - 1) * 24, page * 24);

  function getAdminScroller() {
    return filesAdminRef.current
      ?.closest<HTMLElement>(".admin-view")
      ?.querySelector<HTMLElement>(":scope > section[role='tabpanel']") ?? null;
  }

  function openDetail(fileId: string) {
    const scroller = getAdminScroller();
    returnPositionRef.current = { scrollTop: scroller?.scrollTop ?? 0, fileId };
    setSelectedId(fileId);
    setImagePreviewOpen(false);
    setEditing(false);
    setEditDirty(false);
    setNotice("");
  }

  function closeDetail() {
    shouldRestorePositionRef.current = true;
    setImagePreviewOpen(false);
    setSelectedId(null);
    setEditing(false);
    setEditDirty(false);
    setReloadKey((value) => value + 1);
  }

  useLayoutEffect(() => {
    if (selectedId !== null || !shouldRestorePositionRef.current) return;
    shouldRestorePositionRef.current = false;
    const position = returnPositionRef.current;
    const restore = () => getAdminScroller()?.scrollTo({ top: position.scrollTop, left: 0, behavior: "auto" });
    restore();
    const frame = window.requestAnimationFrame(() => {
      restore();
      if (position.fileId) document.getElementById(`floorplan-admin-open-${position.fileId}`)?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selectedId]);

  useLayoutEffect(() => {
    if (!deleteFocusRef.current.active || !data || selectedId !== null) return;
    deleteFocusRef.current.active = false;
    const targetId = deleteFocusRef.current.fileId;
    const frame = window.requestAnimationFrame(() => {
      const target = targetId ? document.getElementById(`floorplan-admin-open-${targetId}`) : null;
      (target ?? uploadTriggerRef.current)?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [data, selectedId]);

  useEffect(() => {
    if (!showUpload && !deleteConfirm) return;
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape" || busy || document.querySelector(".image-workbench")) return;
      event.preventDefault();
      if (deleteConfirm) {
        const fileId = deleteConfirm.id;
        setDeleteConfirm(null);
        window.requestAnimationFrame(() => document.getElementById(`floorplan-delete-trigger-${fileId}`)?.focus({ preventScroll: true }));
      } else if (showUpload) {
        setShowUpload(false);
        window.requestAnimationFrame(() => uploadTriggerRef.current?.focus());
      }
    }
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [busy, deleteConfirm, showUpload]);

  useEffect(() => {
    if (!editing) return;
    const frame = window.requestAnimationFrame(() => firstEditFieldRef.current?.focus({ preventScroll: true }));
    function keydown(event: KeyboardEvent) {
      if (event.key !== "Escape" || busy || document.querySelector(".image-workbench")) return;
      event.preventDefault();
      setEditing(false);
      setEditDirty(false);
      window.requestAnimationFrame(() => editButtonRef.current?.focus());
    }
    window.addEventListener("keydown", keydown);
    return () => { window.cancelAnimationFrame(frame); window.removeEventListener("keydown", keydown); };
  }, [busy, editing]);

  async function uploadFile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const form = event.currentTarget;
    const values = new FormData(form);
    const file = values.get("file");
    if (!(file instanceof File) || !file.name) {
      setError("请选择一份需要加入户型库的文件。");
      return;
    }
    const kinds = FLOORPLAN_KIND_OPTIONS.filter((item) => values.getAll("kinds").includes(item.id)).map((item) => item.id);
    setBusy(true);
    setError("");
    try {
      const result = await api.uploadFloorplan({
        file,
        title: String(values.get("title") ?? ""),
        community: String(values.get("community") ?? ""),
        kinds,
        householdTags: String(values.get("household_tags") ?? ""),
        building: String(values.get("building") ?? ""),
        remark: String(values.get("remark") ?? ""),
        onProgress: setUploadProgress,
      });
      setNotice(`“${result.title}”已加入户型库。`);
      setShowUpload(false);
      setReloadKey((value) => value + 1);
      setSelectedId(result.id);
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
      setUploadProgress(null);
    }
  }

  async function saveFile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected || busy) return;
    const values = new FormData(event.currentTarget);
    const kinds = FLOORPLAN_KIND_OPTIONS.filter((item) => values.getAll("kinds").includes(item.id)).map((item) => item.id);
    setBusy(true);
    setError("");
    try {
      const updated = await api.updateFloorplan(selected.id, {
        version: selected.version,
        title: String(values.get("title") ?? ""),
        community: String(values.get("community") ?? ""),
        category: selected.category,
        kinds,
        household_tags: parseFloorplanTags(String(values.get("household_tags") ?? "")),
        building: String(values.get("building") ?? "").trim() || null,
        remark: String(values.get("remark") ?? "").trim() || null,
      });
      setData((current) => current ? { ...current, items: current.items.map((item) => item.id === updated.id ? updated : item) } : current);
      setEditing(false);
      setEditDirty(false);
      setNotice("户型资料信息已保存。");
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function removeFile(item: FloorplanFile) {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.deleteFloorplan(item.id, item.version);
      const currentIndex = items.findIndex((candidate) => candidate.id === item.id);
      const focusCandidate = currentIndex >= 0 ? items[currentIndex + 1] ?? items[currentIndex - 1] ?? null : null;
      deleteFocusRef.current = { active: selectedId === null, fileId: focusCandidate?.id ?? null };
      setDeleteConfirm(null);
      if (selectedId !== null) closeDetail();
      else setReloadKey((value) => value + 1);
      setNotice(item.origin === "library" ? "资料已从系统永久移除，本地只读源文件没有改动。" : "资料及服务器托管文件已永久删除。");
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  if (selected) {
    return <div className="cases-admin-detail"><div className="case-detail">
      <header className="detail-head">
        <button className="back-action" type="button" disabled={editing && busy} onClick={editing ? () => { setEditing(false); setEditDirty(false); window.requestAnimationFrame(() => editButtonRef.current?.focus()); } : closeDetail}><ArrowLeft aria-hidden="true" />{editing ? "返回户型资料详情" : `返回${categoryLabel}`}</button>
        <div><span>{categoryLabel}</span><h1>{selected.title}</h1></div>
        <div className="detail-tools"><span className="detail-position">{selected.file_type}</span>{editing ? <button className="detail-edit-action detail-edit-placeholder" type="button" disabled tabIndex={-1} aria-hidden="true"><Pencil aria-hidden="true" />编辑资料</button> : <button ref={editButtonRef} className="detail-edit-action" type="button" onClick={() => { setEditing(true); setEditDirty(false); }}><Pencil aria-hidden="true" />编辑资料</button>}</div>
      </header>
          {error && <div className="form-error workspace-feedback" role="alert"><CircleAlert aria-hidden="true" />{error}</div>}
          {notice && <ActionFeedback className="detail-update-notice" message={notice} onDismiss={() => setNotice("")} />}
      {editing ? <div id="floorplan-editor-workspace" className="case-editor-workspace"><form className="case-editor" onSubmit={(event) => void saveFile(event)} onChange={() => setEditDirty(true)}>
        <header className="case-editor-head"><div><h2>编辑户型资料</h2><p>基本信息与文件共用同一份记录；取消不会写入服务器，保存后统一生效。</p></div><span className={editDirty ? "pending" : "saved"}>{editDirty ? "基本信息待保存" : "尚未修改"}</span></header>
        {error && <div className="form-error" role="alert"><CircleAlert aria-hidden="true" />{error}</div>}
        <section className="case-editor-section case-basic-editor" aria-labelledby="floorplan-basic-editor-title"><header><div><span className="case-editor-section-mark"><Pencil aria-hidden="true" /></span><div><h3 id="floorplan-basic-editor-title">基本信息</h3><p>维护名称、小区、楼栋、户型标签和内部备注，不改变已经保存的文件名。</p></div></div><strong>{editDirty ? "待保存" : "未修改"}</strong></header><FloorplanFields item={selected} editor firstFieldRef={firstEditFieldRef} /></section>
        <section className="case-editor-section floorplan-file-editor" aria-labelledby="floorplan-file-editor-title"><header><div><span className="case-editor-section-mark"><FileBox aria-hidden="true" /></span><div><h3 id="floorplan-file-editor-title">资料文件</h3><p>当前文件保持不变；需要替换时，请永久删除后重新上传一份资料。</p></div></div><strong>1 份</strong></header><div className="floorplan-file-record"><CadPlaceholder item={selected} /><div><b>{selected.file_name}</b><span>{selected.file_type} · {formatBytes(selected.size_bytes)}{selected.cad_version ? ` · ${selected.cad_version}` : ""}</span></div><a className="secondary-action" href={selected.download_url}>下载文件<Download aria-hidden="true" /></a></div></section>
        <footer className="case-editor-actions"><p>永久删除会移除网站记录；程序托管文件会同时从服务器删除。</p><div><ArchiveDeleteAction label={selected.title} imageCount={0} description={selected.origin === "library" ? "将从网站户型库中永久移除这条记录；本地 F 盘历史源文件保持只读，不会被改动。上云后的程序托管文件会同时从云服务器删除。" : "将永久删除户型资料记录和服务器中的托管文件，操作无法恢复。"} busy={busy} onConfirm={() => removeFile(selected)} /><button className="secondary-action" type="button" disabled={busy} onClick={() => { setEditing(false); setEditDirty(false); window.requestAnimationFrame(() => editButtonRef.current?.focus()); }}>取消编辑</button><button className="primary-action" type="submit" disabled={busy || !editDirty}>{busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <Save aria-hidden="true" />}{busy ? "正在保存" : "保存全部更改"}</button></div></footer>
      </form></div> : <div className="detail-gallery floorplan-detail-gallery"><section className="detail-stage">{selected.cover_url ? <button ref={detailPreviewButtonRef} className="detail-preview floorplan-document-preview" type="button" onClick={() => setImagePreviewOpen(true)} aria-label={`查看大图：${selected.title}`}><ImageSurface src={selected.cover_url} alt={selected.title} eager /><span>点击进入大图查看</span></button> : <div className="detail-preview floorplan-document-preview"><CadPlaceholder item={selected} /></div>}<aside className="detail-meta"><div><span>当前资料</span><strong>{selected.file_name}</strong><small>{selected.file_type} · {formatBytes(selected.size_bytes)}</small></div><dl><div><dt>名称</dt><dd>{selected.title}</dd></div><div><dt>小区</dt><dd>{selected.community}</dd></div>{selected.building && <div><dt>楼栋</dt><dd>{selected.building}</dd></div>}{selected.category === "drawing" && <div><dt>内容</dt><dd>{floorplanKindText(selected.kinds)}</dd></div>}<div><dt>户型</dt><dd>{selected.household_tags.length ? selected.household_tags.join("、") : "未填写"}</dd></div>{selected.remark && <div><dt>备注</dt><dd>{selected.remark}</dd></div>}</dl><a className="secondary-action" href={selected.download_url}>下载文件<Download aria-hidden="true" /></a></aside></section></div>}
      {imagePreviewOpen && selected.cover_url && <ImageWorkbenchLightbox src={selected.cover_url} originalSrc={selected.download_url} fileName={selected.file_name} caption={`${selected.community} · ${selected.title}`} dialogLabel="户型库图片大图查看与标注" previousDisabled nextDisabled onPrevious={() => undefined} onNext={() => undefined} onClose={() => setImagePreviewOpen(false)} returnFocusRef={detailPreviewButtonRef} />}
    </div></div>;
  }

  return <section ref={filesAdminRef} className="cases-admin">
      <div className="case-admin-toolbar">
        <SearchField className="case-admin-search" label={`搜索${categoryLabel}`} value={query} onChange={setQuery} placeholder="资料名称、小区、楼栋或户型标签" clearLabel={`清空${categoryLabel}搜索`} />
        <button ref={uploadTriggerRef} className={showUpload ? "secondary-action case-upload-toggle" : "primary-action case-upload-toggle"} type="button" aria-expanded={showUpload} onClick={() => { setShowUpload((value) => !value); setError(""); }}>{showUpload ? <X aria-hidden="true" /> : <Upload aria-hidden="true" />}{showUpload ? "收起上传" : "上传新资料"}</button>
        <ArchiveOrderManager archiveKind="floorplan" categoryLabel="小区顺序" itemLabel="户型资料顺序" onSaved={() => setReloadKey((value) => value + 1)} />
      </div>
      {showUpload && <form id={`floorplan-upload-${category}`} className="case-upload-panel" onSubmit={(event) => void uploadFile(event)}><header><div><span className="case-upload-mark"><FileBox aria-hidden="true" /></span><div><h3>建立一份新{categoryLabel}</h3><p>填写户型资料信息并选择一份文件，一份文件建立一条记录。</p></div></div><button className="text-action" type="button" disabled={busy} onClick={() => setShowUpload(false)}><X aria-hidden="true" />取消</button></header><FloorplanFields upload category={category} />{uploadProgress !== null && <UploadProgress value={uploadProgress} />}<div className="case-upload-boundary"><ShieldCheck aria-hidden="true" /><p><b>统一程序托管</b><span>新资料写入程序托管目录，上云后可通过“永久删除”同时删除服务器文件。</span></p></div>{error && <div className="form-error" role="alert"><CircleAlert aria-hidden="true" />{error}</div>}<div className="case-upload-actions"><button className="text-action" type="button" disabled={busy} onClick={() => setShowUpload(false)}>取消</button><button className="primary-action" type="submit" disabled={busy}>{busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <Upload aria-hidden="true" />}{busy ? "正在上传" : "上传并建立资料"}</button></div></form>}
      {error && !showUpload && <div className="form-error workspace-feedback" role="alert"><CircleAlert aria-hidden="true" />{error}</div>}
      {notice && <ActionFeedback className="workspace-feedback" message={notice} onDismiss={() => setNotice("")} />}
      {!data && !error ? <LoadingState label={`正在读取${categoryLabel}台账`} /> : items.length ? <div className="case-admin-list floorplan-admin-list">{items.map((item) => <article className="case-admin-row" key={item.id}>{item.cover_url ? <ImageSurface src={item.cover_url} alt="" /> : <CadPlaceholder item={item} />}<div className="case-admin-copy"><h3>{item.title}</h3><p>{item.community}{item.building ? ` · ${item.building}` : ""}{item.category === "drawing" ? ` · ${floorplanKindText(item.kinds)}` : ""} · {item.file_type} · {formatBytes(item.size_bytes)}</p></div><div className="case-admin-actions"><button id={`floorplan-admin-open-${item.id}`} className="secondary-action case-open-action" type="button" onClick={() => openDetail(item.id)}>查看详情 <ArrowUpRight aria-hidden="true" /></button><button id={`floorplan-delete-trigger-${item.id}`} className="danger-action danger-trigger case-delete-trigger" type="button" disabled={busy} aria-expanded={deleteConfirm?.id === item.id} aria-controls={`floorplan-delete-panel-${item.id}`} onClick={() => { setDeleteConfirm(item); setError(""); setNotice(""); }}><Trash2 aria-hidden="true" />永久删除</button></div>{deleteConfirm?.id === item.id && <div className="case-delete-confirm" id={`floorplan-delete-panel-${item.id}`} role="alert"><span className="case-delete-mark"><Trash2 aria-hidden="true" /></span><div><strong>确认永久删除“{item.title}”？</strong><p>{item.origin === "library" ? "将从网站户型库中永久移除这条记录；本地 F 盘历史源文件保持只读，不会被改动。" : "将永久删除户型资料记录和服务器中的托管文件，操作无法恢复。"}</p></div><div className="case-delete-confirm-actions"><button className="text-action" type="button" disabled={busy} onClick={() => { setDeleteConfirm(null); window.requestAnimationFrame(() => document.getElementById(`floorplan-delete-trigger-${item.id}`)?.focus({ preventScroll: true })); }}>取消</button><button id={`floorplan-delete-confirm-${item.id}`} className="danger-action" type="button" disabled={busy} onClick={() => void removeFile(item)}>{busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <Trash2 aria-hidden="true" />}{busy ? "正在永久删除" : "确认永久删除"}</button></div></div>}</article>)}</div> : <div className="case-admin-empty"><FolderSearch aria-hidden="true" /><strong>没有匹配的{categoryLabel}</strong><p>请调整搜索词后再试。</p></div>}
      {data && pageCount > 1 && <nav className="case-admin-pagination" aria-label={`${categoryLabel}管理分页`}><button className="text-action" type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft aria-hidden="true" />上一页</button><span>第 {page} / {pageCount} 页 · {filteredItems.length} 份</span><button className="text-action" type="button" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>下一页<ChevronRight aria-hidden="true" /></button></nav>}
  </section>;
}

function FloorplanFields({ item, upload = false, editor = false, category = item?.category ?? "drawing", firstFieldRef }: { item?: FloorplanFile; upload?: boolean; editor?: boolean; category?: "drawing" | "other"; firstFieldRef?: Ref<HTMLInputElement> }) {
  return <div className={`${editor ? "case-editor-fields" : "case-upload-grid"} floorplan-fields`}>
    <label><span>资料名称</span><input ref={firstFieldRef} name="title" required maxLength={160} defaultValue={item?.title ?? ""} placeholder="例如：格林春天全部户型" /></label>
    <label><span>所属小区</span><input name="community" required maxLength={120} defaultValue={item?.community ?? ""} placeholder="例如：格林春天" /></label>
    <label><span>楼栋或期数</span><input name="building" maxLength={120} defaultValue={item?.building ?? ""} placeholder="例如：一期 1-6 号楼" /></label>
    <label><span>包含户型</span><input name="household_tags" defaultValue={item?.household_tags.join("，") ?? ""} placeholder="A户型，120㎡，三室两厅" /><small>用逗号分隔，可填写多个户型名称、面积或房型。</small></label>
    {(category === "drawing") && <fieldset className="span-2 floorplan-kind-field"><legend>图纸内容</legend><div>{FLOORPLAN_KIND_OPTIONS.map((option) => <label key={option.id}><input name="kinds" type="checkbox" value={option.id} defaultChecked={item?.kinds.includes(option.id) ?? false} /><span>{option.label}</span></label>)}</div></fieldset>}
    <label className={editor ? "case-editor-remark" : "span-2"}><span>备注</span><textarea name="remark" maxLength={1000} defaultValue={item?.remark ?? ""} placeholder="记录需要长期保留的说明，可留空" /></label>
    {upload && <label className="span-2 case-upload-file"><span>资料文件</span><input name="file" type="file" required accept={category === "drawing" ? ".dwg" : ".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"} /><small>{category === "drawing" ? "图纸文件仅接收 DWG；一份文件建立一条记录。" : "支持图片、PDF、Word、Excel、PPT 和 TXT；一份文件建立一条记录。"}</small></label>}
  </div>;
}

function EnterpriseLibrary() {
  const [categories, setCategories] = useState<Community[]>([]);
  const [documents, setDocuments] = useState<Awaited<ReturnType<typeof api.enterpriseArchive>> | null>(null);
  const [query, setQuery] = useState("");
  const [settledQuery, setSettledQuery] = useState("");
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [recent, setRecent] = useState(false);
  const [recentTotal, setRecentTotal] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [openedId, setOpenedId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [detail, setDetail] = useState<EnterpriseDocumentDetail | null>(null);
  const [detailActiveImage, setDetailActiveImage] = useState(0);
  const [folioPanoramaPreview, setFolioPanoramaPreview] = useState<CaseMaterial | null>(null);
  const [folioPanoramaOpeningId, setFolioPanoramaOpeningId] = useState<number | null>(null);
  const [folioVideoOpeningId, setFolioVideoOpeningId] = useState<number | null>(null);
  const [folioPanoramaError, setFolioPanoramaError] = useTransientNotice();
  const [allDocumentTotal, setAllDocumentTotal] = useState(0);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [detailReloadKey, setDetailReloadKey] = useState(0);
  const viewRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<HTMLElement>(null);
  const indexRef = useRef<HTMLDivElement>(null);
  const lastTriggerRef = useRef<HTMLButtonElement | null>(null);
  const folioPanoramaDialogRef = useRef<HTMLDivElement>(null);
  const folioPanoramaCloseRef = useRef<HTMLButtonElement>(null);
  const folioPanoramaReturnRef = useRef<HTMLButtonElement | null>(null);
  const detailScrollTopRef = useRef(0);
  const returnPositionRef = useRef({ studio: 0, board: 0, index: 0, indexLeft: 0 });
  const shouldRestorePositionRef = useRef(false);
  const category = categories.find((item) => item.id === categoryId);

  useEffect(() => {
    const timer = window.setTimeout(() => setSettledQuery(query.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => setPage(1), [settledQuery, categoryId, recent]);
  useEffect(() => setPreviewId(null), [settledQuery, categoryId, page, recent]);

  useEffect(() => {
    let active = true;
    api.enterpriseCategories().then((items) => active && setCategories(items)).catch(() => undefined);
    return () => { active = false; };
  }, [reloadKey]);

  useEffect(() => {
    let active = true;
    api.recentEnterpriseDocuments({ pageSize: 1 })
      .then((payload) => active && setRecentTotal(payload.total))
      .catch(() => active && setRecentTotal(null));
    return () => { active = false; };
  }, [reloadKey]);

  useEffect(() => {
    let active = true;
    api.enterpriseArchive({ page: 1 })
      .then((payload) => active && setAllDocumentTotal(payload.total))
      .catch(() => undefined);
    return () => { active = false; };
  }, [reloadKey]);

  useEffect(() => {
    if (openedId !== null) return;
    let active = true;
    setError("");
    const request = recent
      ? api.recentEnterpriseDocuments({ q: settledQuery, page })
      : api.enterpriseArchive({ q: settledQuery, category: category?.name, page });
    request.then((items) => active && setDocuments(items))
      .catch((reason) => active && setError(messageFrom(reason)));
    return () => { active = false; };
  }, [category?.name, openedId, page, recent, reloadKey, settledQuery]);

  useEffect(() => {
    if (openedId === null) { setDetail(null); return; }
    let active = true;
    setError("");
    setDetail(null);
    api.openEnterpriseDocument(openedId)
      .then((item) => { if (active) { setDetail(item); setReloadKey((value) => value + 1); } })
      .catch((reason) => active && setError(messageFrom(reason)));
    return () => { active = false; };
  }, [detailReloadKey, openedId]);

  useLayoutEffect(() => {
    if (openedId !== null || !shouldRestorePositionRef.current) return;
    shouldRestorePositionRef.current = false;
    const position = returnPositionRef.current;
    const restore = () => {
      viewRef.current?.scrollTo({ top: position.studio, left: 0, behavior: "auto" });
      boardRef.current?.scrollTo({ top: position.board, left: 0, behavior: "auto" });
      indexRef.current?.scrollTo({ top: position.index, left: position.indexLeft, behavior: "auto" });
    };
    restore();
    const frame = window.requestAnimationFrame(() => {
      restore();
      lastTriggerRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [openedId]);

  function openDetail(documentId: number) {
    returnPositionRef.current = {
      studio: viewRef.current?.scrollTop ?? 0,
      board: boardRef.current?.scrollTop ?? 0,
      index: indexRef.current?.scrollTop ?? 0,
      indexLeft: indexRef.current?.scrollLeft ?? 0,
    };
    detailScrollTopRef.current = 0;
    setDetailActiveImage(0);
    setOpenedId(documentId);
  }

  function closeDetail() {
    shouldRestorePositionRef.current = true;
    setOpenedId(null);
  }

  async function openFolioPanorama(documentId: number, returnTarget: HTMLButtonElement) {
    folioPanoramaReturnRef.current = returnTarget;
    setFolioPanoramaError("");
    setFolioPanoramaOpeningId(documentId);
    try {
      const opened = await api.openEnterpriseDocument(documentId);
      const panorama = opened.materials.find((item) => item.kind === "panorama");
      if (!panorama) throw new Error("当前企业资料没有可查看的全景图。");
      setFolioPanoramaPreview(panorama);
      setReloadKey((value) => value + 1);
    } catch (reason) {
      setFolioPanoramaError(messageFrom(reason));
    } finally {
      setFolioPanoramaOpeningId(null);
    }
  }

  async function openFolioVideo(documentId: number) {
    const target = window.open("about:blank", "_blank");
    if (!target) {
      setFolioPanoramaError("浏览器阻止了新窗口，请允许弹出窗口后重试。");
      return;
    }
    target.opener = null;
    setFolioPanoramaError("");
    setFolioVideoOpeningId(documentId);
    try {
      const opened = await api.openEnterpriseDocument(documentId);
      const videos = opened.materials.filter((item) => item.kind === "douyin");
      if (videos.length === 0) throw new Error("当前企业资料没有可查看的视频。");
      if (videos.length > 1) {
        target.close();
        openDetail(documentId);
        return;
      }
      target.location.replace(new URL(videos[0].open_url, window.location.href).href);
      setReloadKey((value) => value + 1);
    } catch (reason) {
      target.close();
      setFolioPanoramaError(messageFrom(reason));
    } finally {
      setFolioVideoOpeningId(null);
    }
  }

  useEffect(() => {
    if (!folioPanoramaPreview) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    folioPanoramaCloseRef.current?.focus();
    function keydown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setFolioPanoramaPreview(null);
        return;
      }
      if (event.key !== "Tab" || !folioPanoramaDialogRef.current) return;
      const controls = Array.from(
        folioPanoramaDialogRef.current.querySelectorAll<HTMLElement>("button:not([disabled]), a[href]"),
      );
      const first = controls[0];
      const last = controls.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    window.addEventListener("keydown", keydown);
    return () => {
      window.removeEventListener("keydown", keydown);
      document.body.style.overflow = previousOverflow;
      window.requestAnimationFrame(() => folioPanoramaReturnRef.current?.focus({ preventScroll: true }));
    };
  }, [folioPanoramaPreview]);

  if (editingId !== null) {
    return (
      <div ref={viewRef} className="enterprise-detail-shell">
        <EnterpriseAdmin
          initialDocumentId={editingId}
          startEditing
          standalone
          initialActiveImage={detailActiveImage}
          initialScrollTop={detailScrollTopRef.current}
          onStandaloneBack={() => {
            setEditingId(null);
            setDetailReloadKey((value) => value + 1);
          }}
          onStandaloneDeleted={() => {
            shouldRestorePositionRef.current = true;
            setEditingId(null);
            setOpenedId(null);
            setDetail(null);
            setReloadKey((value) => value + 1);
          }}
        />
      </div>
    );
  }

  if (openedId !== null) {
    if (error) return <div ref={viewRef} className="enterprise-library"><ErrorState message={error} retry={() => setDetailReloadKey((value) => value + 1)} /></div>;
    if (!detail) return <div ref={viewRef} className="enterprise-library"><LoadingState label="正在打开企业资料" /></div>;
    return <div ref={viewRef} className="enterprise-detail-shell"><EnterpriseDetailView detail={detail} backLabel="返回企业库" onBack={closeDetail} initialActiveImage={detailActiveImage} initialScrollTop={detailScrollTopRef.current} onEdit={(state) => { setDetailActiveImage(state.activeImage); detailScrollTopRef.current = state.scrollTop; setEditingId(openedId); }} /></div>;
  }

  const preview = documents?.items.find((item) => item.id === previewId) ?? null;
  const pageCount = documents ? Math.max(1, Math.ceil(documents.total / documents.page_size)) : 1;
  return (
    <div ref={viewRef} className="archive-studio enterprise-archive-studio">
      <aside className="project-index">
        <div className="index-heading"><h1>企业图鉴</h1><p><span>{documents?.total ?? 0} 份</span><span>{documents?.image_total ?? 0} 张资料图</span></p></div>
        <SearchField className="index-search" label="搜索企业资料" value={query} onChange={setQuery} placeholder="搜索资料名称、分类或说明" clearLabel="清空企业资料搜索" />
        <label className="community-picker"><span>选择资料分类</span><select value={categoryId ?? ""} onChange={(event) => { setRecent(false); setCategoryId(event.target.value ? Number(event.target.value) : undefined); }}><option value="">全部</option>{categories.map((item) => <option value={item.id} key={item.id}>{item.name} · {item.case_count}</option>)}</select></label>
        <ArchiveIndexNavigation items={categories} groupLabel="资料分类" countLabel="份" selectedId={categoryId} recent={recent} recentTotal={recentTotal} allTotal={allDocumentTotal} indexRef={indexRef} onAll={() => { setCategoryId(undefined); setRecent(false); }} onRecent={() => { setCategoryId(undefined); setRecent(true); }} onSelect={(item) => { setCategoryId(item.id); setRecent(false); }} />
      </aside>
      <main ref={boardRef} className="project-board" aria-busy={!documents}>
        <header className="board-heading"><div><h2>{recent ? "最近浏览" : category?.name ?? (settledQuery ? "搜索结果" : "全部企业资料")}</h2><p>{recent ? "按最近打开时间排列，选择资料继续浏览" : "选择企业资料，右侧查看完整图册"}</p></div><p aria-live="polite">当前结果 <strong>{documents?.total ?? 0}</strong> 份</p></header>
        {!documents && !error && <LoadingState label="正在整理企业资料墙" />}
        {error && <ErrorState message={error} retry={() => setReloadKey((value) => value + 1)} />}
        {documents && !documents.items.length && !error && <div className="empty-archive"><FolderSearch aria-hidden="true" /><h2>{recent ? "还没有最近浏览" : "没有找到对应企业资料"}</h2><p>{recent ? "成功打开企业资料后，会按时间顺序显示在这里。" : "请调整搜索关键词或切换资料分类。"}</p></div>}
        {documents?.items.length ? <><section className="project-grid" aria-label="企业资料墙">{documents.items.map((item, index) => <article className="project-card" data-selected={previewId === item.id} key={item.id}><button className="project-select" type="button" aria-pressed={previewId === item.id} aria-label={`选择 ${item.title} 作为当前企业资料`} onClick={(event) => { lastTriggerRef.current = event.currentTarget; setPreviewId(item.id); }}><span className="project-image-wrap"><ImageSurface src={item.cover_url} alt={`${item.title}资料图`} className="project-image" />{previewId === item.id && <span className="project-current">当前资料</span>}</span><span className="project-copy"><span><b>{item.title}</b><span className="project-facts"><em>{item.image_count} 张</em>{Boolean(item.panorama_count) && <span className="project-panorama" aria-label="有配套全景图" title="有配套全景图"><Aperture aria-hidden="true" /></span>}{Boolean(item.video_count) && <span className="project-video" aria-label="有配套视频" title="有配套视频"><Video aria-hidden="true" /></span>}</span></span><small>{String((page - 1) * documents.page_size + index + 1).padStart(3, "0")} · {item.category || "未分类"}</small></span></button></article>)}</section><nav className="pagination studio-pagination" aria-label="企业资料分页"><button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft aria-hidden="true" />上一页</button><span>{page} / {pageCount}</span><button type="button" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>下一页<ChevronRight aria-hidden="true" /></button></nav></> : null}
      </main>
      <aside className={`project-folio ${preview ? "ready" : "waiting"}`} aria-label="当前企业资料图册"><div className="folio-bar"><span>ENTERPRISE FOLIO</span><span>{preview ? "已选择资料" : "等待选择"}</span>{preview && <button className="folio-close" type="button" onClick={() => setPreviewId(null)} aria-label="关闭当前企业资料"><X aria-hidden="true" /></button>}</div>{preview ? <div className={`folio-content ${Boolean(preview.panorama_count) && Boolean(preview.video_count) ? "folio-content-three-actions" : ""}`} key={preview.id} tabIndex={-1}><button className="folio-preview" type="button" onClick={() => openDetail(preview.id)}><ImageSurface src={preview.cover_url} alt={`${preview.title}资料图`} eager /><span>打开资料 <ArrowUpRight aria-hidden="true" /></span></button><div className="folio-index"><span>当前资料</span><span>ENTERPRISE {String(preview.id).padStart(4, "0")}</span></div><h2>{preview.title}</h2><dl><div><dt>分类</dt><dd>{preview.category || "未分类"}</dd></div><div><dt>图片</dt><dd>{preview.image_count} 张</dd></div>{preview.description && <div><dt>说明</dt><dd>{preview.description}</dd></div>}{Boolean(preview.panorama_count) && <div className="folio-panorama-row"><dt>全景</dt><dd><button className="folio-panorama-link" type="button" disabled={folioPanoramaOpeningId === preview.id} onClick={(event) => void openFolioPanorama(preview.id, event.currentTarget)}><Aperture aria-hidden="true" />{folioPanoramaOpeningId === preview.id ? "正在打开" : "查看"}</button></dd></div>}{Boolean(preview.video_count) && <div className="folio-video-row"><dt>视频</dt><dd><button className="folio-video-link" type="button" disabled={folioVideoOpeningId === preview.id} onClick={() => void openFolioVideo(preview.id)}><Video aria-hidden="true" />{folioVideoOpeningId === preview.id ? "正在打开" : "查看"}</button></dd></div>}</dl>{folioPanoramaError && <ActionFeedback className="folio-panorama-feedback" message={folioPanoramaError} tone="warning" onDismiss={() => setFolioPanoramaError("")} />}<div className={`folio-actions ${Boolean(preview.panorama_count) && Boolean(preview.video_count) ? "folio-actions-three" : ""}`}><button className="primary-action folio-action" type="button" onClick={() => openDetail(preview.id)}>资料图 <ArrowUpRight aria-hidden="true" /></button>{Boolean(preview.panorama_count) && <button className="folio-panorama-compact" type="button" disabled={folioPanoramaOpeningId === preview.id} onClick={(event) => void openFolioPanorama(preview.id, event.currentTarget)}><Aperture aria-hidden="true" />{folioPanoramaOpeningId === preview.id ? "正在打开" : "全景"}</button>}{Boolean(preview.video_count) && <button className="folio-video-compact" type="button" disabled={folioVideoOpeningId === preview.id} onClick={() => void openFolioVideo(preview.id)}><Video aria-hidden="true" />{folioVideoOpeningId === preview.id ? "正在打开" : "视频"}</button>}</div></div> : <div className="folio-empty"><div className="folio-empty-scene" aria-hidden="true"><span>ENTERPRISE<br />ARCHIVE</span></div><div className="folio-empty-copy"><span aria-hidden="true" /><h2>积淀品牌章法，<br />标定施工尺度。</h2><p>从左侧资料墙选择一份，完整图册将在这里展开。</p></div><ul className="folio-empty-pillars" aria-label="企业库内容"><li><BookOpen aria-hidden="true" /><span><b>品牌资料</b><small>BRAND</small></span></li><li><BadgeCheck aria-hidden="true" /><span><b>施工标准</b><small>STANDARD</small></span></li><li><PenTool aria-hidden="true" /><span><b>工艺规范</b><small>CRAFT</small></span></li><li><ShieldCheck aria-hidden="true" /><span><b>服务体系</b><small>SERVICE</small></span></li></ul></div>}</aside>
      {folioPanoramaPreview && <div ref={folioPanoramaDialogRef} className="panorama-dialog" role="dialog" aria-modal="true" aria-label={`预览全景图：${folioPanoramaPreview.label}`}><header><div><span>全景图预览</span><h2>{folioPanoramaPreview.label}</h2></div><div><a className="secondary-action" href={folioPanoramaPreview.open_url} target="_blank" rel="noreferrer">新窗口打开 <ArrowUpRight aria-hidden="true" /></a><button ref={folioPanoramaCloseRef} type="button" onClick={() => setFolioPanoramaPreview(null)} aria-label="关闭全景图预览"><X aria-hidden="true" /></button></div></header><div className="panorama-frame"><iframe src={folioPanoramaPreview.preview_url ?? folioPanoramaPreview.open_url} title={folioPanoramaPreview.label} sandbox="allow-forms allow-popups allow-same-origin allow-scripts" referrerPolicy="no-referrer" allow="fullscreen; gyroscope; accelerometer; xr-spatial-tracking" /><p>若预览方禁止嵌入，请使用上方“新窗口打开”。</p></div></div>}
    </div>
  );
}

function MapView({
  isAdmin,
  isRestricted,
  goAdmin,
  openCommunity,
  stats,
}: {
  isAdmin: boolean;
  isRestricted: boolean;
  goAdmin: () => void;
  openCommunity: (communityId: number, scope: "all" | "landed") => void;
  stats: CatalogStats | null | undefined;
}) {
  const [locations, setLocations] = useState<MapLocation[] | null>(null);
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [caseScope, setCaseScope] = useState<"all" | "landed">("all");
  const [error, setError] = useState("");
  const mapViewRef = useRef<HTMLDivElement>(null);
  const allScopeLabel = isRestricted ? "全部授权案例" : "全部案例";
  const landedScopeLabel = isRestricted ? "授权落地案例" : "落地案例";

  useLayoutEffect(() => {
    mapViewRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    Promise.all([api.mapLocations(), api.config()])
      .then(([items, clientConfig]) => {
        setLocations(items);
        setConfig(clientConfig);
      })
      .catch((reason) => setError(messageFrom(reason)));
  }, []);

  const filtered = useMemo(() => (locations ?? [])
    .filter((item) => `${item.name} ${item.address}`
      .toLocaleLowerCase("zh-CN")
      .includes(query.trim().toLocaleLowerCase("zh-CN")))
    .sort((left, right) => {
      if (left.place_type !== right.place_type) return left.place_type === "store" ? -1 : 1;
      return COMMUNITY_NAME_COLLATOR.compare(left.name, right.name);
    }), [locations, query]);
  const selected = filtered.find((item) => item.id === selectedId) ?? null;
  const pinnedLocation = filtered.find((item) => item.place_type === "store") ?? null;
  const ledgerLocations = filtered.filter((item) => item.place_type !== "store");

  useEffect(() => {
    if (selectedId !== null && locations && !filtered.some((item) => item.id === selectedId)) {
      setSelectedId(null);
    }
  }, [filtered, locations, selectedId]);

  const mapPoints: MapPoint[] = filtered.map((item) => ({
    id: item.id,
    community_id: item.community_id,
    name: item.name,
    lng: item.lng,
    lat: item.lat,
    place_type: item.place_type,
    case_count: caseScope === "all" ? item.all_case_count : item.landed_case_count,
  }));
  const outsideHailarCount = mapPoints.filter((point) => !isInHailarFocusArea(point)).length;
  const renderLedgerLocation = (item: MapLocation) => (
    <button
      type="button"
      key={item.id}
      className={`${item.id === selectedId ? "active" : ""} ${item.place_type === "store" ? "store-entry" : ""}`.trim()}
      aria-pressed={item.id === selectedId}
      aria-label={item.place_type === "store"
        ? `${item.name}，易高门店`
        : `${item.name}，${caseScope === "all" ? item.all_case_count : item.landed_case_count} 户${caseScope === "all" ? allScopeLabel : landedScopeLabel}`}
      onClick={() => setSelectedId(item.id)}
    >
      <span className={`map-location-badge ${item.place_type}`} aria-hidden="true">
        {item.place_type === "store" ? <StoreMark /> : caseScope === "all" ? item.all_case_count : item.landed_case_count}
      </span>
      <span><b>{item.name}</b><small>{item.address}</small></span>
      <Check aria-hidden="true" />
    </button>
  );

  return (
    <div ref={mapViewRef} className="map-view map-workspace">
      <aside className="map-ledger">
        <div className="map-ledger-title">
          <div><MapPinned aria-hidden="true" /><span>已核准地点</span></div>
          <b>
            {locations?.length ?? stats?.verified_locations ?? 0}
            {stats?.location_targets !== null && stats?.location_targets !== undefined && (
              <small>/{stats.location_targets}</small>
            )}
          </b>
        </div>
        <SearchField
          className="map-ledger-search"
          label="搜索已核准地点"
          value={query}
          onChange={setQuery}
          placeholder="搜索小区或地址"
          clearLabel="清空地点搜索"
        />
        {pinnedLocation && (
          <div className="map-ledger-pinned" aria-label="门店地点">
            {renderLedgerLocation(pinnedLocation)}
          </div>
        )}
        <div className="map-ledger-list">
          {ledgerLocations.map(renderLedgerLocation)}
        </div>
      </aside>

      <section className="map-stage">
        <header className="map-stage-head">
          <div>
            <h1>案例地图</h1>
            <p>{isRestricted ? "只展示授权案例对应的核准点位与易高门店" : "只展示人工核准的高德 GCJ-02 点位"}</p>
          </div>
          <div className="map-stage-status">
            <div className="map-case-scope" role="group" aria-label="地图案例范围">
              <button type="button" className={caseScope === "all" ? "active" : ""} aria-pressed={caseScope === "all"} onClick={() => setCaseScope("all")}>{allScopeLabel}</button>
              <button type="button" className={caseScope === "landed" ? "active" : ""} aria-pressed={caseScope === "landed"} onClick={() => setCaseScope("landed")}>{landedScopeLabel}</button>
            </div>
            <span><ShieldCheck aria-hidden="true" /> 未核准点位不会发布</span>
            {outsideHailarCount > 0 && (
              <small>默认聚焦海拉尔；{outsideHailarCount} 个区域外点位请从台账单独查看</small>
            )}
          </div>
        </header>

        {error && <ErrorState message={error} />}
        {locations === null && !error && <LoadingState label="正在读取已核准点位" />}
        {locations?.length === 0 && (
          <div className="map-zero-state">
            <div className="map-zero-coordinate" aria-hidden="true"><span /><i /></div>
            <div>
              <strong>
                0{stats?.location_targets !== null && stats?.location_targets !== undefined
                  ? ` / ${stats.location_targets}`
                  : ""}
              </strong>
              <h2>{isRestricted ? "暂时没有可查看的地图点位" : "地图正在等待第一枚核准点"}</h2>
              <p>{isRestricted
                ? "易高门店或已授权案例的小区完成地图核准后，会在这里显示。"
                : "所有小区和门店都必须经过高德 POI 搜索、地址核对和人工发布，才能出现在员工地图中。"}</p>
              {isAdmin && (
                <button className="primary-action" type="button" onClick={goAdmin}>
                  开始核准地点 <ArrowRight aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        )}
        {locations && locations.length > 0 && filtered.length === 0 && (
          <div className="map-no-results">
            <Search aria-hidden="true" />
            <span>地点台账中没有匹配“{query.trim()}”的已核准记录。</span>
            <button type="button" className="secondary-action" onClick={() => setQuery("")}>
              <X aria-hidden="true" /> 清空地点搜索
            </button>
          </div>
        )}
        {locations && filtered.length > 0 && (
          <>
            <MapCanvas
              config={config?.amap ?? null}
              points={mapPoints}
              selectedId={selectedId}
              onSelect={(id) => setSelectedId(Number(id))}
              label={`易高已核准地点地图，共 ${filtered.length} 个点位`}
            />
            {selected ? (
              <article className="map-place-folio" key={selected.id}>
                <div className="map-place-index">
                  <span>{selected.place_type === "store" ? "易高门店" : "案例小区"}</span>
                  <span><ShieldCheck aria-hidden="true" /> 已人工核准</span>
                </div>
                <h2>{selected.name}</h2>
                <p>{selected.address}</p>
                <dl>
                  <div><dt>行政区</dt><dd>{selected.district}</dd></div>
                  <div><dt>坐标</dt><dd>{selected.lng.toFixed(6)}, {selected.lat.toFixed(6)}</dd></div>
                </dl>
                <div className="map-place-actions">
                  {selected.place_type === "community" && selected.community_id !== null && (
                    <button
                      className="map-navigation"
                      type="button"
                      onClick={() => openCommunity(selected.community_id as number, caseScope)}
                    >
                      <Archive aria-hidden="true" /> {isRestricted ? "查看授权案例" : "查看本小区案例"} <ArrowRight aria-hidden="true" />
                    </button>
                  )}
                  <a className="map-navigation" href={amapMarkerUrl(selected)} target="_blank" rel="noreferrer">
                    <Navigation aria-hidden="true" /> 在高德地图中打开 <ArrowUpRight aria-hidden="true" />
                  </a>
                </div>
              </article>
            ) : (
              <div className="map-select-hint"><LocateFixed aria-hidden="true" /> 默认查看海拉尔；选择台账或地图标记可单独查看地点</div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function AdminView({
  initialTab,
  currentUserId,
  locationDirty,
  onLocationDirtyChange,
  onStatsChanged,
}: {
  initialTab: AdminTab;
  currentUserId: number;
  locationDirty: boolean;
  onLocationDirtyChange: (dirty: boolean) => void;
  onStatsChanged: () => void;
}) {
  const [tab, setTab] = useState<AdminTab>(initialTab);
  const adminViewRef = useRef<HTMLDivElement>(null);
  const adminTabsRef = useRef<HTMLDivElement>(null);
  const tabs = [
    { id: "cases" as const, label: "案例管理", icon: Archive },
    { id: "materials" as const, label: "素材管理", icon: Images },
    { id: "enterprise" as const, label: "企业库管理", icon: BookOpen },
    { id: "floorplans" as const, label: "户型库管理", icon: FileBox },
    { id: "locations" as const, label: "地图核准", icon: MapPinned },
    { id: "social" as const, label: "社媒管理", icon: Music2 },
    { id: "users" as const, label: "员工账号", icon: Users },
    { id: "views" as const, label: "查看记录", icon: Eye },
    { id: "system" as const, label: "系统状态", icon: Database },
  ];

  useLayoutEffect(() => {
    adminViewRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const revealActiveTab = () => adminTabsRef.current?.querySelector<HTMLElement>(".active")?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
    const frame = window.requestAnimationFrame(() => {
      adminViewRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
      revealActiveTab();
    });
    window.addEventListener("resize", revealActiveTab);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", revealActiveTab);
    };
  }, [tab]);

  function selectTab(next: AdminTab): boolean {
    if (tab === "locations" && next !== "locations" && locationDirty) {
      if (!confirmDiscardLocationChanges()) return false;
      onLocationDirtyChange(false);
    }
    setTab(next);
    return true;
  }

  function moveTab(event: ReactKeyboardEvent<HTMLButtonElement>, index: number) {
    if (!(["ArrowLeft", "ArrowRight", "Home", "End"] as string[]).includes(event.key)) return;
    event.preventDefault();
    const next = event.key === "Home"
      ? 0
      : event.key === "End"
        ? tabs.length - 1
        : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
    if (selectTab(tabs[next].id)) {
      document.getElementById(`admin-tab-${tabs[next].id}`)?.focus();
    }
  }

  return (
    <div ref={adminViewRef} className={`admin-view ${tab === "locations" ? "admin-view--locations" : ""}`}>
      <header className="workspace-head">
        <div>
          <h1>管理与核准</h1>
          <p>所有高风险操作都需要管理员权限并写入审计记录。</p>
        </div>
        <span className="admin-badge"><KeyRound aria-hidden="true" /> ADMIN</span>
      </header>
      <div ref={adminTabsRef} className="admin-tabs" role="tablist" aria-label="管理功能">
        {tabs.map((item, index) => {
          const Icon = item.icon;
          return (
            <button
              id={`admin-tab-${item.id}`}
              className={tab === item.id ? "active" : ""}
              onClick={() => selectTab(item.id)}
              onKeyDown={(event) => moveTab(event, index)}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              aria-controls={`admin-panel-${item.id}`}
              tabIndex={tab === item.id ? 0 : -1}
              key={item.id}
            >
              <Icon aria-hidden="true" /> {item.label}
            </button>
          );
        })}
      </div>
      <section
        role="tabpanel"
        id={`admin-panel-${tab}`}
        aria-labelledby={`admin-tab-${tab}`}
        tabIndex={0}
      >
        {tab === "locations" && (
          <LocationAdmin dirty={locationDirty} onDirtyChange={onLocationDirtyChange} />
        )}
        {tab === "cases" && <CaseManagementAdmin onStatsChanged={onStatsChanged} />}
        {tab === "materials" && <CaseManagementAdmin archiveKind="material" onStatsChanged={onStatsChanged} />}
        {tab === "enterprise" && <EnterpriseManagementAdmin />}
        {tab === "floorplans" && <FloorplanManagementAdmin />}
        {tab === "social" && <Suspense fallback={<LoadingState label="正在打开社媒管理" />}><DeferredSocialManagement /></Suspense>}
        {tab === "users" && <UsersAdmin currentUserId={currentUserId} />}
        {tab === "views" && <CaseViewsAdmin />}
        {tab === "system" && <SystemAdmin />}
      </section>
    </div>
  );
}

function ArchiveManagementModule({
  idPrefix,
  title,
  description,
  informationLabel,
  informationIcon: InformationIcon,
  information,
  materials,
  materialsLabel = "关联资料",
  materialsIcon: MaterialsIcon = FileBox,
  className,
}: {
  idPrefix: string;
  title: string;
  description: string;
  informationLabel: string;
  informationIcon: typeof Images;
  information: ReactNode;
  materials: ReactNode;
  materialsLabel?: string;
  materialsIcon?: typeof Images;
  className: string;
}) {
  type ManagementSection = "archive" | "materials";
  const [section, setSection] = useState<ManagementSection>("archive");
  const sections = [
    { id: "archive" as const, label: informationLabel, icon: InformationIcon },
    { id: "materials" as const, label: materialsLabel, icon: MaterialsIcon },
  ];

  function moveSection(event: ReactKeyboardEvent<HTMLButtonElement>, index: number) {
    if (!(["ArrowLeft", "ArrowRight", "Home", "End"] as string[]).includes(event.key)) return;
    event.preventDefault();
    const next = event.key === "Home"
      ? 0
      : event.key === "End"
        ? sections.length - 1
        : (index + (event.key === "ArrowRight" ? 1 : -1) + sections.length) % sections.length;
    setSection(sections[next].id);
    document.getElementById(`${idPrefix}-management-tab-${sections[next].id}`)?.focus();
  }

  return (
    <section className={`management-module ${className}`}>
      <header className="management-module-head">
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <div className="management-module-tabs" role="tablist" aria-label={`${title}内容`}>
          {sections.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                id={`${idPrefix}-management-tab-${item.id}`}
                type="button"
                role="tab"
                className={section === item.id ? "active" : ""}
                aria-selected={section === item.id}
                aria-controls={`${idPrefix}-management-panel-${item.id}`}
                tabIndex={section === item.id ? 0 : -1}
                onClick={() => setSection(item.id)}
                onKeyDown={(event) => moveSection(event, index)}
                key={item.id}
              >
                <Icon aria-hidden="true" /> {item.label}
              </button>
            );
          })}
        </div>
      </header>
      <div
        className="management-module-panel"
        id={`${idPrefix}-management-panel-${section}`}
        role="tabpanel"
        aria-labelledby={`${idPrefix}-management-tab-${section}`}
        tabIndex={0}
      >
        {section === "archive" ? information : materials}
      </div>
    </section>
  );
}

function CaseManagementAdmin({ archiveKind = "case", onStatsChanged }: { archiveKind?: ArchiveKind; onStatsChanged: () => void }) {
  const copy = ARCHIVE_COPY[archiveKind];
  return (
    <ArchiveManagementModule
      idPrefix={archiveKind}
      title={`${copy.singular}管理`}
      description={`在同一处维护${copy.singular}信息、${copy.image}和关联资料。`}
      informationLabel={copy.information}
      informationIcon={Images}
      information={<CasesAdmin archiveKind={archiveKind} onStatsChanged={onStatsChanged} />}
      materials={<MaterialsAdmin scope={archiveKind} embedded />}
      className="archive-management-admin"
    />
  );
}

function EnterpriseManagementAdmin() {
  return (
    <ArchiveManagementModule
      idPrefix="enterprise"
      title="企业库管理"
      description="在同一处维护企业资料信息、图片和关联资料。"
      informationLabel="企业资料"
      informationIcon={BookOpen}
      information={<EnterpriseAdmin />}
      materials={<MaterialsAdmin embedded scope="enterprise" />}
      className="archive-management-admin"
    />
  );
}

type LocationQueueStatus = "unlocated" | "candidate" | "verified";

const LOCATION_QUEUE_FILTERS: ReadonlyArray<{
  status: LocationQueueStatus;
  label: string;
}> = [
  { status: "unlocated", label: "待定位" },
  { status: "candidate", label: "待核准" },
  { status: "verified", label: "已发布" },
];

function LocationAdmin({
  dirty,
  onDirtyChange,
}: {
  dirty: boolean;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [locations, setLocations] = useState<AdminLocation[] | null>(null);
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useTransientNotice();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [candidates, setCandidates] = useState<AmapCandidate[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<AmapCandidate | null>(null);
  const [communityName, setCommunityName] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState<LocationQueueStatus | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const locationItemsRef = useRef<HTMLDivElement>(null);
  const filteredLocations = statusFilter
    ? locations?.filter((item) => item.status === statusFilter)
    : locations;
  const selected = filteredLocations?.find((item) => item.id === selectedId);

  async function reload() {
    setError("");
    try {
      const payload = await api.adminLocations();
      setLocations(payload);
      const scopedLocations = statusFilter
        ? payload.filter((item) => item.status === statusFilter)
        : payload;
      setSelectedId((current) => (
        current !== null && scopedLocations.some((item) => item.id === current)
          ? current
          : scopedLocations[0]?.id ?? null
      ));
    } catch (reason) {
      setError(messageFrom(reason));
    }
  }

  useEffect(() => {
    void reload();
    api.config().then(setConfig).catch((reason) => setError(messageFrom(reason)));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setSearchQuery(selected.name);
    setCommunityName(selected.name);
    setSearchError("");
    setCandidates([]);
    setSelectedCandidate(null);
  }, [selected?.id, selected?.name]);

  function setFormValue(name: string, value: string | number) {
    const field = formRef.current?.elements.namedItem(name);
    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
      field.value = String(value);
    }
  }

  function chooseCandidate(candidate: AmapCandidate) {
    setSelectedCandidate(candidate);
    setFormValue("poi_id", candidate.poi_id);
    setFormValue("address", candidate.address);
    setFormValue("district", candidate.district);
    setFormValue("adcode", candidate.adcode);
    setFormValue("lng", candidate.lng);
    setFormValue("lat", candidate.lat);
    setFormValue("evidence_url", amapMarkerUrl({
      name: candidate.name,
      lng: candidate.lng,
      lat: candidate.lat,
    }));
    setFormValue(
      "evidence_note",
      `由高德 POI「${candidate.name}」选入；管理员需核对名称、地址与地图位置后再发布。`,
    );
    onDirtyChange(true);
    setNotice("");
    setError("");
  }

  async function searchAmap() {
    if (!selected) return;
    const query = searchQuery.trim();
    if (query.length < 2) {
      setSearchError("至少输入 2 个字再搜索。");
      return;
    }
    setSearchBusy(true);
    setSearchError("");
    setCandidates([]);
    setSelectedCandidate(null);
    try {
      const result = await api.searchAmap(query, selected.place_type);
      setCandidates(result.items);
      if (result.items.length === 0) {
        setSearchError("全国范围内没有找到匹配地点。请尝试完整名称，外地地点可加城市或道路名。");
      }
    } catch (reason) {
      setSearchError(messageFrom(reason));
    } finally {
      setSearchBusy(false);
    }
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api.saveLocationCandidate(selected.id, {
        expected_revision: selected.revision,
        poi_id: String(data.get("poi_id")),
        address: String(data.get("address")),
        district: String(data.get("district")),
        adcode: String(data.get("adcode")),
        lng: Number(data.get("lng")),
        lat: Number(data.get("lat")),
        evidence_url: String(data.get("evidence_url")),
        evidence_note: String(data.get("evidence_note")),
      });
      await reload();
      onDirtyChange(false);
      setNotice("候选点位已保存。请再次核对表单，再执行人工核准。");
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (!selected) return;
    if (dirty) {
      setError("表单有尚未保存的修改。请先保存为候选，再执行人工核准。");
      return;
    }
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api.verifyLocation(selected.id, selected.revision);
      await reload();
      setNotice("点位已人工核准并向员工地图发布。");
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function renameCommunity() {
    if (!selected || selected.place_type !== "community" || selected.community_id == null || renameBusy) return;
    const nextName = communityName.trim();
    if (!nextName) {
      setError("小区名称不能为空。");
      return;
    }
    if (nextName === selected.name) {
      setNotice("小区名称没有变化。");
      return;
    }
    setRenameBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await api.updateCommunity(selected.community_id, nextName);
      await reload();
      setCommunityName(result.name);
      setNotice(`小区已更名为“${result.name}”，并同步更新 ${result.case_count} 个案例和地图记录。`);
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setRenameBusy(false);
    }
  }

  function filterByStatus(status: LocationQueueStatus) {
    if (dirty && !confirmDiscardLocationChanges()) return;
    const nextFilter = statusFilter === status ? null : status;
    const nextLocations = nextFilter
      ? locations?.filter((item) => item.status === nextFilter) ?? []
      : locations ?? [];
    const retainedSelection = nextFilter === null
      ? nextLocations.find((item) => item.id === selectedId)
      : undefined;
    setStatusFilter(nextFilter);
    setSelectedId(retainedSelection?.id ?? nextLocations[0]?.id ?? null);
    onDirtyChange(false);
    setNotice("");
    setError("");
    setSearchError("");
    setCandidates([]);
    setSelectedCandidate(null);
    window.requestAnimationFrame(() => locationItemsRef.current?.scrollTo({ top: 0 }));
  }

  if (!locations) return error ? <ErrorState message={error} retry={reload} /> : <LoadingState />;
  const counts = locations.reduce<Record<string, number>>((result, item) => {
    result[item.status] = (result[item.status] ?? 0) + 1;
    return result;
  }, {});

  return (
    <div className="location-admin">
      <section className="location-queue">
        <div className="queue-summary">
          {LOCATION_QUEUE_FILTERS.map(({ status, label }) => {
            const count = counts[status] ?? 0;
            const active = statusFilter === status;
            return (
              <button
                key={status}
                type="button"
                className={active ? "active" : ""}
                aria-pressed={active}
                aria-controls="location-status-list"
                aria-label={`${label}地点，${count}项${active ? "，当前已筛选；再次点击显示全部" : "；点击筛选"}`}
                onClick={() => filterByStatus(status)}
              >
                <b>{count}</b>
                <span>{label}</span>
              </button>
            );
          })}
        </div>
        <div
          ref={locationItemsRef}
          id="location-status-list"
          className="location-items"
          aria-label={statusFilter
            ? `${LOCATION_QUEUE_FILTERS.find((item) => item.status === statusFilter)?.label ?? "当前状态"}地点列表`
            : "全部地点列表"}
        >
          {filteredLocations?.map((item) => (
            <button
              key={item.id}
              type="button"
              className={item.id === selected?.id ? "active" : ""}
              aria-pressed={item.id === selected?.id}
              onClick={() => {
                if (dirty && !confirmDiscardLocationChanges()) return;
                setSelectedId(item.id);
                onDirtyChange(false);
                setNotice("");
                setError("");
              }}
            >
              {item.place_type === "store" ? (
                <span className="location-store-mark" aria-hidden="true"><StoreMark /></span>
              ) : (
                <span className={`status-dot status-${item.status}`} aria-hidden="true" />
              )}
              <span><b>{item.name}</b><small>{item.place_type === "store" ? "门店" : "小区"}</small></span>
              <span>{item.status === "verified" ? "已核准" : item.status === "candidate" ? "候选" : "未定位"}</span>
            </button>
          ))}
          {filteredLocations?.length === 0 && (
            <p className="location-filter-empty" role="status">
              当前没有{LOCATION_QUEUE_FILTERS.find((item) => item.status === statusFilter)?.label ?? "符合条件的"}地点
            </p>
          )}
        </div>
      </section>
      {selected && (
        <div className="location-workbench">
          <section className="poi-locator" aria-labelledby="poi-locator-title">
            <div className="poi-locator-head">
              <div>
                <span>01 · 高德地点搜索</span>
                <h2 id="poi-locator-title">从全国高德地点中找准真实位置</h2>
              </div>
              <span className={config?.amap.search_enabled ? "service-ready" : "service-offline"}>
                {config?.amap.search_enabled ? "搜索服务已连接" : "搜索服务待配置"}
              </span>
            </div>
            <SearchField
              className="poi-search"
              label="搜索高德地点"
              value={searchQuery}
              onChange={setSearchQuery}
              onEnter={() => void searchAmap()}
              placeholder={selected.place_type === "store" ? "输入门店名称，外地地点可加城市" : "输入地点名称，外地地点可加城市"}
              action={(
                <button
                  className="ui-search-action"
                  type="button"
                  onClick={() => void searchAmap()}
                  disabled={searchBusy || !config?.amap.search_enabled}
                >
                  {searchBusy ? <LoaderCircle className="spin" aria-hidden="true" /> : <LocateFixed aria-hidden="true" />}
                  搜索全国地点
                </button>
              )}
            />
            <p className="poi-search-note">
              当前地点：<b>{selected.name}</b>{selected.address ? ` · 地址线索：${selected.address}` : ""}
            </p>
            {searchError && <div className="poi-search-error" role="alert"><CircleAlert /> {searchError}</div>}
            {candidates.length > 0 && (
              <div className="poi-results" aria-label="高德地点候选">
                {candidates.map((candidate, index) => (
                  <button
                    type="button"
                    key={candidate.poi_id}
                    className={candidate.poi_id === selectedCandidate?.poi_id ? "active" : ""}
                    onClick={() => chooseCandidate(candidate)}
                    aria-pressed={candidate.poi_id === selectedCandidate?.poi_id}
                  >
                    <span className="poi-result-index">{String(index + 1).padStart(2, "0")}</span>
                    <span><b>{candidate.name}</b><small>{candidate.address || "高德未返回门牌地址"}</small></span>
                    <span><MapPin aria-hidden="true" /> {candidate.district}</span>
                    <Check aria-hidden="true" />
                  </button>
                ))}
              </div>
            )}
            {(selectedCandidate || (selected.lng != null && selected.lat != null)) && (
              <div className="poi-map-preview">
                <MapCanvas
                  config={config?.amap ?? null}
                  points={[selectedCandidate ? {
                    id: selectedCandidate.poi_id,
                    name: selectedCandidate.name,
                    lng: selectedCandidate.lng,
                    lat: selectedCandidate.lat,
                    place_type: selected.place_type,
                  } : {
                    id: selected.id,
                    name: selected.name,
                    lng: selected.lng as number,
                    lat: selected.lat as number,
                    place_type: selected.place_type,
                  }]}
                  selectedId={selectedCandidate?.poi_id ?? selected.id}
                  onSelect={() => undefined}
                  label={`${selected.name}候选点位预览`}
                />
                <div>
                  <span>{selectedCandidate ? "本次高德候选" : "当前已保存点位"}</span>
                  <b>{selectedCandidate?.name ?? selected.name}</b>
                  <small>{selectedCandidate?.address ?? selected.address}</small>
                </div>
              </div>
            )}
          </section>

          <form
            ref={formRef}
            className="location-form"
            key={`${selected.id}-${selected.revision}`}
            onSubmit={save}
            onChange={(event) => {
              if (event.target instanceof HTMLInputElement && event.target.name === "community_name") return;
              onDirtyChange(true);
              setNotice("");
            }}
          >
            <div className="form-title">
              <div><span>02 · 保存与核准</span><h2>{selected.name}</h2></div>
              <span>REV {selected.revision}</span>
            </div>
            {selected.place_type === "community" && selected.community_id != null && (
              <div className="location-name-editor">
                <label htmlFor={`location-community-name-${selected.id}`}>
                  <span>小区名称</span>
                  <input
                    id={`location-community-name-${selected.id}`}
                    name="community_name"
                    aria-describedby={`location-community-name-help-${selected.id}`}
                    value={communityName}
                    onChange={(event) => {
                      setCommunityName(event.target.value);
                      setError("");
                      setNotice("");
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void renameCommunity();
                      }
                    }}
                    maxLength={80}
                    autoComplete="off"
                  />
                </label>
                <button
                  className="secondary-action"
                  type="button"
                  onClick={() => void renameCommunity()}
                  disabled={renameBusy || !communityName.trim() || communityName.trim() === selected.name}
                >
                  {renameBusy ? <LoaderCircle className="spin" aria-hidden="true" /> : <Pencil aria-hidden="true" />}
                  {renameBusy ? "正在同步" : "保存名称"}
                </button>
                <small id={`location-community-name-help-${selected.id}`}>名称会同步到对应案例、搜索和地图，不会改动已上传的原图文件名。</small>
              </div>
            )}
            <div className="form-grid">
              <label><span>高德 POI ID</span><input name="poi_id" defaultValue={selected.poi_id ?? ""} required /></label>
              <label><span>行政区编码</span><input name="adcode" defaultValue={selected.adcode ?? ""} pattern="\d{6}" required /></label>
              <label className="span-2"><span>完整地址</span><input name="address" defaultValue={selected.address ?? ""} required /></label>
              <label><span>行政区</span><input name="district" defaultValue={selected.district ?? ""} placeholder="选择高德候选后自动填入" required /></label>
              <label><span>经度 GCJ-02</span><input name="lng" type="number" step="0.000001" defaultValue={selected.lng ?? ""} required /></label>
              <label><span>纬度 GCJ-02</span><input name="lat" type="number" step="0.000001" defaultValue={selected.lat ?? ""} required /></label>
              <label className="span-2"><span>高德证据链接</span><input name="evidence_url" type="url" defaultValue={selected.evidence_url ?? ""} required /></label>
              <label className="span-2"><span>核对说明</span><textarea name="evidence_note" defaultValue={selected.evidence_note ?? ""} required /></label>
            </div>
            {error && <div className="form-error" role="alert"><CircleAlert aria-hidden="true" />{error}</div>}
            {notice && <ActionFeedback message={notice} onDismiss={() => setNotice("")} />}
            {dirty && <div className="form-dirty" role="status">表单已修改，必须先保存候选才能核准发布。</div>}
            <div className="form-actions">
              <button type="submit" className="secondary-action" disabled={busy}>
                {busy ? <LoaderCircle className="spin" /> : <Settings2 />} 保存为候选
              </button>
              <button
                type="button"
                className="primary-action"
                onClick={verify}
                disabled={busy || dirty || selected.status !== "candidate"}
              >
                <ShieldCheck aria-hidden="true" /> 人工核准并发布
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function CasesAdmin({
  archiveKind = "case",
  onStatsChanged,
}: {
  archiveKind?: ArchiveKind;
  onStatsChanged: () => void;
}) {
  const copy = ARCHIVE_COPY[archiveKind];
  const [cases, setCases] = useState<Awaited<ReturnType<typeof api.adminCases>> | null>(null);
  const [query, setQuery] = useState("");
  const [settledQuery, setSettledQuery] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [changingId, setChangingId] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<AdminCaseSummary | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [communitySuggestions, setCommunitySuggestions] = useState<Community[]>([]);
  const [error, setError] = useState("");
  const [noticeTone, setNoticeTone] = useState<FeedbackTone>("success");
  const [notice, setNotice] = useTransientNotice(5200, noticeTone === "warning");
  const uploadTriggerRef = useRef<HTMLButtonElement>(null);
  const uploadCommunityRef = useRef<HTMLInputElement>(null);
  const caseAdminRef = useRef<HTMLElement>(null);
  const returnPositionRef = useRef({ scrollTop: 0, caseId: null as number | null });
  const shouldRestorePositionRef = useRef(false);
  const deleteFocusRef = useRef({ active: false, caseId: null as number | null });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSettledQuery(query.trim());
      setPage(1);
    }, 220);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (selectedId !== null) return;
    let active = true;
    setError("");
    (archiveKind === "case" ? api.adminCases({ q: settledQuery, page }) : api.adminMaterialCases({ q: settledQuery, page }))
      .then((payload) => active && setCases(payload))
      .catch((reason) => active && setError(messageFrom(reason)));
    return () => {
      active = false;
    };
  }, [archiveKind, page, reloadKey, selectedId, settledQuery]);

  useLayoutEffect(() => {
    if (selectedId !== null || !shouldRestorePositionRef.current) return;
    shouldRestorePositionRef.current = false;
    const position = returnPositionRef.current;
    const restore = () => {
      const scroller = getCaseAdminScroller();
      scroller?.scrollTo({ top: position.scrollTop, left: 0, behavior: "auto" });
    };
    restore();
    const frame = window.requestAnimationFrame(() => {
      restore();
      if (position.caseId !== null) {
        document
          .getElementById(`case-admin-open-${position.caseId}`)
          ?.focus({ preventScroll: true });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selectedId]);

  useLayoutEffect(() => {
    if (!deleteFocusRef.current.active || !cases || selectedId !== null) return;
    deleteFocusRef.current.active = false;
    const targetId = deleteFocusRef.current.caseId;
    const frame = window.requestAnimationFrame(() => {
      const target = targetId === null ? null : document.getElementById(`case-admin-open-${targetId}`);
      (target ?? uploadTriggerRef.current)?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [cases, selectedId]);

  useEffect(() => {
    if (!showUpload) return;
    let active = true;
    (archiveKind === "case" ? api.communities() : api.materialLibraries())
      .then((items) => active && setCommunitySuggestions(items))
      .catch(() => active && setCommunitySuggestions([]));
    const frame = window.requestAnimationFrame(() => {
      if (showUpload) uploadCommunityRef.current?.focus();
    });
    function keydown(event: KeyboardEvent) {
      if (event.defaultPrevented || document.querySelector(".image-workbench")) return;
      if (event.key === "Escape" && !uploadBusy) {
        event.preventDefault();
        setShowUpload(false);
        setUploadFiles([]);
        setUploadProgress(null);
        setError("");
        window.requestAnimationFrame(() => uploadTriggerRef.current?.focus());
      }
    }
    window.addEventListener("keydown", keydown);
    return () => {
      active = false;
      window.cancelAnimationFrame(frame);
      window.removeEventListener("keydown", keydown);
    };
  }, [archiveKind, showUpload, uploadBusy]);

  useEffect(() => {
    if (!deleteConfirm) return;
    const caseId = deleteConfirm.id;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(`case-delete-confirm-${caseId}`)?.focus();
    });
    function keydown(event: KeyboardEvent) {
      if (event.key !== "Escape" || changingId !== null) return;
      event.preventDefault();
      setDeleteConfirm(null);
      window.requestAnimationFrame(() => {
        document.getElementById(`case-delete-trigger-${caseId}`)?.focus({ preventScroll: true });
      });
    }
    document.addEventListener("keydown", keydown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", keydown);
    };
  }, [changingId, deleteConfirm]);

  function closeUpload() {
    if (uploadBusy) return;
    setShowUpload(false);
    setUploadFiles([]);
    setUploadProgress(null);
    setError("");
    window.requestAnimationFrame(() => uploadTriggerRef.current?.focus());
  }

  async function uploadCase(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const fileError = archiveImageFileError(uploadFiles, archiveKind);
    if (fileError || uploadBusy) {
      if (fileError) setError(fileError);
      return;
    }
    const form = event.currentTarget;
    const data = new FormData(form);
    setUploadBusy(true);
    setUploadProgress(0);
    setError("");
    setNotice("");
    try {
      const values = {
        community: String(data.get("community") ?? ""),
        room: String(data.get("room") ?? ""),
        area: String(data.get("area") ?? ""),
        style: String(data.get("style") ?? ""),
        remark: String(data.get("remark") ?? ""),
        files: uploadFiles,
        onProgress: setUploadProgress,
      };
      const result = archiveKind === "case" ? await api.uploadCase(values) : await api.uploadMaterialCase(values);
      setNoticeTone(result.published ? "success" : "warning");
      setNotice(
        result.published
          ? `${result.source_folder} 已上传并生成 ${result.image_count} 张预览图。`
          : `${result.source_folder} 已保存，但部分预览图生成失败，${copy.singular}暂未发布。`,
      );
      setShowUpload(false);
      setUploadFiles([]);
      setReloadKey((value) => value + 1);
      setSelectedId(result.id);
      onStatsChanged();
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setUploadBusy(false);
      setUploadProgress(null);
    }
  }

  function closeDetail() {
    shouldRestorePositionRef.current = true;
    setSelectedId(null);
    setReloadKey((value) => value + 1);
  }

  function getCaseAdminScroller() {
    return caseAdminRef.current
      ?.closest<HTMLElement>(".admin-view")
      ?.querySelector<HTMLElement>(":scope > section[role='tabpanel']") ?? null;
  }

  function openDetail(caseId: number) {
    const scroller = getCaseAdminScroller();
    returnPositionRef.current = {
      scrollTop: scroller?.scrollTop ?? 0,
      caseId,
    };
    setSelectedId(caseId);
    setNotice("");
  }

  function closeDeleteConfirm() {
    if (!deleteConfirm || changingId !== null) return;
    const caseId = deleteConfirm.id;
    setDeleteConfirm(null);
    window.requestAnimationFrame(() => {
      document.getElementById(`case-delete-trigger-${caseId}`)?.focus({ preventScroll: true });
    });
  }

  async function deleteCase(item: AdminCaseSummary) {
    if (changingId !== null) return;
    setChangingId(item.id);
    setError("");
    setNotice("");
    const currentIndex = cases?.items.findIndex((candidate) => candidate.id === item.id) ?? -1;
    const focusCandidate = currentIndex >= 0
      ? cases?.items[currentIndex + 1] ?? cases?.items[currentIndex - 1] ?? null
      : null;
    deleteFocusRef.current = { active: true, caseId: focusCandidate?.id ?? null };
    try {
      const result = archiveKind === "case"
        ? await api.deleteCase(item.id, item.version)
        : await api.deleteMaterialCase(item.id, item.version);
      setDeleteConfirm(null);
      setNoticeTone("success");
      setNotice(
        "cleanup_pending" in result && result.cleanup_pending
          ? `${item.source_folder} 已从系统永久删除；少量缓存文件将在后续维护中清理。`
          : `${item.source_folder} 已从系统永久删除。`,
      );
      if (cases?.items.length === 1 && page > 1) setPage((value) => value - 1);
      else setReloadKey((value) => value + 1);
      onStatsChanged();
    } catch (reason) {
      deleteFocusRef.current.active = false;
      setError(messageFrom(reason));
    } finally {
      setChangingId(null);
    }
  }

  if (selectedId !== null) {
    return (
      <div className="cases-admin-detail">
        <CaseDetail
          caseId={selectedId}
          archiveKind={archiveKind}
          isAdmin
          backLabel={`返回${copy.singular}管理`}
          externalNotice={notice}
          externalNoticeTone={noticeTone}
          onDismissExternalNotice={() => setNotice("")}
          onBack={closeDetail}
          onDeleted={closeDetail}
          onChanged={() => {
            setReloadKey((value) => value + 1);
            onStatsChanged();
          }}
        />
      </div>
    );
  }

  const pageCount = cases ? Math.max(1, Math.ceil(cases.total / cases.page_size)) : 1;
  const uploadFileError = uploadFiles.length ? archiveImageFileError(uploadFiles, archiveKind) : "";
  return (
    <section ref={caseAdminRef} className="cases-admin">
      <div className="case-admin-toolbar">
        <SearchField
          className="case-admin-search"
          label={`搜索${copy.singular}`}
          value={query}
          onChange={setQuery}
          placeholder={`${copy.group}、${copy.room}、${copy.area}或${copy.style}`}
          clearLabel={`清空${copy.singular}搜索`}
        />
        <button
          ref={uploadTriggerRef}
          className={`case-upload-toggle ${showUpload ? "secondary-action" : "primary-action"}`}
          type="button"
          aria-expanded={showUpload}
          aria-controls="case-upload-panel"
          onClick={() => {
            if (showUpload) closeUpload();
            else {
              setShowUpload(true);
              setError("");
              setNotice("");
            }
          }}
        >
          {showUpload ? <X aria-hidden="true" /> : <Upload aria-hidden="true" />}
          {showUpload ? "收起上传" : `上传新${copy.singular}`}
        </button>
        <ArchiveOrderManager
          archiveKind={archiveKind}
          categoryLabel={archiveKind === "case" ? "小区顺序" : "素材大分类顺序"}
          itemLabel={`${copy.singular}顺序`}
          onSaved={() => setReloadKey((value) => value + 1)}
        />
      </div>

      {showUpload && (
        <form id="case-upload-panel" className="case-upload-panel" onSubmit={(event) => void uploadCase(event)}>
          <header>
            <div>
              <span className="case-upload-mark"><Images aria-hidden="true" /></span>
              <div><h3>建立一个新{copy.singular}</h3><p>填写{copy.singular}信息并一次选择整套{copy.image}。</p></div>
            </div>
            <button type="button" className="text-action" onClick={closeUpload} disabled={uploadBusy}>
              <X aria-hidden="true" />取消
            </button>
          </header>
          <div className="case-upload-grid">
            <div className="case-upload-field">
              <label className="case-upload-field-label" htmlFor="case-upload-community">{copy.group}名称</label>
              <SuggestionInput
                id="case-upload-community"
                inputRef={uploadCommunityRef}
                name="community"
                suggestions={communitySuggestions.map((item) => item.name)}
                placeholder={archiveKind === "case" ? "例如：中海府" : "例如：软装搭配"}
                required
                suggestionLabel={`${copy.group}名称候选`}
              />
            </div>
            <label><span>{copy.room}</span><input name="room" placeholder={archiveKind === "case" ? "研发案例可留空" : "请输入素材名称"} required={archiveKind === "material"} autoComplete="off" /></label>
            <label><span>{copy.area}{archiveKind === "case" ? "（㎡）" : ""}</span><input name="area" placeholder="可稍后补充" autoComplete="off" /></label>
            <label><span>{copy.style}</span><input name="style" placeholder="可稍后补充" autoComplete="off" /></label>
            <label className="span-2"><span>{copy.singular}备注</span><textarea name="remark" placeholder={`记录需要长期保留的${copy.singular}说明，可留空`} /></label>
            <label className="span-2 case-upload-file">
              <span>整套{copy.image}</span>
              <input
                name="files"
                type="file"
                accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                multiple
                required
                onChange={(event) => {
                  const selectedFiles = naturalSortFiles(Array.from(event.currentTarget.files ?? []));
                  setUploadFiles(selectedFiles);
                  setUploadProgress(null);
                  setError(selectedFiles.length ? archiveImageFileError(selectedFiles, archiveKind) : "");
                }}
              />
              <small>选择后可预览大图、拖动排序并设置封面；支持 PNG、JPG、JPEG、WebP，最多 {ARCHIVE_UPLOAD_MAX_FILES} 张，单张不超过 160 MB。</small>
            </label>
          </div>
          <div className="case-upload-summary" aria-live="polite">
            <div>
              <span>{uploadFileError ? "所选图片无法上传" : uploadFiles.length ? "图片已就绪" : "等待选择图片"}</span>
              <b>{uploadFileError || (uploadFiles.length ? `${uploadFiles.length} 张 · ${formatBytes(uploadFiles.reduce((sum, file) => sum + file.size, 0))}` : `请选择整套${copy.image}`)}</b>
            </div>
            {uploadFiles.length > 0 && (
              <p>{uploadFiles.slice(0, 3).map((file) => file.name).join("、")}{uploadFiles.length > 3 ? ` 等 ${uploadFiles.length} 张` : ""}</p>
            )}
          </div>
          <UploadImageOrganizer files={uploadFiles} onChange={setUploadFiles} disabled={uploadBusy} />
          {uploadProgress !== null && <UploadProgress value={uploadProgress} />}
          <div className="case-upload-boundary">
            <ShieldCheck aria-hidden="true" />
            <p><b>统一程序托管</b><span>新{copy.singular}与现有{copy.singular}使用同一资料区，可在{copy.singular}管理中追加图片或永久删除。</span></p>
          </div>
          {error && <div className="form-error" role="alert"><CircleAlert aria-hidden="true" /> {error}</div>}
          <div className="case-upload-actions">
            <button type="button" className="text-action" onClick={closeUpload} disabled={uploadBusy}>取消</button>
            <button type="submit" className="primary-action" disabled={uploadBusy || !uploadFiles.length || Boolean(uploadFileError)}>
              {uploadProgress !== null ? <LoaderCircle className="spin" aria-hidden="true" /> : <Upload aria-hidden="true" />}
              {uploadProgress === null
                ? `上传并建立${copy.singular}`
                : uploadProgress >= 100
                  ? "服务器正在生成预览"
                  : `正在上传 ${uploadProgress}%`}
            </button>
          </div>
        </form>
      )}

      {error && !showUpload && <div className="account-feedback form-error workspace-feedback" role="alert"><CircleAlert aria-hidden="true" /> {error}</div>}
      {notice && <ActionFeedback className="account-feedback workspace-feedback" message={notice} tone={noticeTone} onDismiss={() => setNotice("")} />}

      {!cases && !error ? (
        <LoadingState label={`正在读取${copy.singular}台账`} />
      ) : cases?.items.length ? (
        <div className="case-admin-list">
          {cases.items.map((item) => (
            <article className="case-admin-row" key={item.id}>
              <ImageSurface src={item.cover_url} alt="" />
              <div className="case-admin-copy">
                <h3>{caseDisplayTitle(item, archiveKind)}</h3>
                <p>{item.community_name} · {item.room || (archiveKind === "case" ? "研发案例" : "未命名")}{item.area ? ` · ${archiveKind === "case" ? formatArea(item.area) : item.area}` : ""} · {item.image_count} 张</p>
              </div>
              <div className="case-admin-actions">
                <button
                  id={`case-admin-open-${item.id}`}
                  className="secondary-action case-open-action"
                  type="button"
                  onClick={() => openDetail(item.id)}
                >查看详情 <ArrowUpRight aria-hidden="true" /></button>
                <button
                  id={`case-delete-trigger-${item.id}`}
                  className="danger-action danger-trigger case-delete-trigger"
                  type="button"
                  disabled={changingId !== null}
                  aria-expanded={deleteConfirm?.id === item.id}
                  aria-controls={`case-delete-panel-${item.id}`}
                  onClick={() => {
                    setDeleteConfirm(item);
                    setError("");
                    setNotice("");
                  }}
                ><Trash2 aria-hidden="true" />永久删除</button>
              </div>
              {deleteConfirm?.id === item.id && (
                <div className="case-delete-confirm" id={`case-delete-panel-${item.id}`} role="alert">
                  <span className="case-delete-mark"><Trash2 aria-hidden="true" /></span>
                  <div>
                    <strong>确认永久删除“{caseDisplayTitle(item, archiveKind)}”？</strong>
                    <p>
                      将永久删除{copy.singular}记录及其 {item.image_count} 张原图、缩略图和预览缓存，操作无法恢复。
                    </p>
                  </div>
                  <div className="case-delete-confirm-actions">
                    <button className="text-action" type="button" disabled={changingId !== null} onClick={closeDeleteConfirm}>取消</button>
                    <button
                      id={`case-delete-confirm-${item.id}`}
                      className="danger-action"
                      type="button"
                      disabled={changingId !== null}
                      onClick={() => void deleteCase(item)}
                    >
                      {changingId === item.id ? <LoaderCircle className="spin" aria-hidden="true" /> : <Trash2 aria-hidden="true" />}
                      {changingId === item.id ? "正在永久删除" : "确认永久删除"}
                    </button>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      ) : (
        <div className="case-admin-empty">
          <FolderSearch aria-hidden="true" />
          <strong>没有匹配的{copy.singular}</strong>
          <p>请调整搜索词后再试。</p>
        </div>
      )}

      {cases && pageCount > 1 && (
        <nav className="case-admin-pagination" aria-label={`${copy.singular}管理分页`}>
          <button className="text-action" type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft aria-hidden="true" />上一页</button>
          <span>第 {page} / {pageCount} 页 · {cases.total} 份</span>
          <button className="text-action" type="button" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>下一页<ChevronRight aria-hidden="true" /></button>
        </nav>
      )}
    </section>
  );
}

function MaterialCasePicker({
  archiveKind = "case",
  idPrefix,
  options,
  selectedIds,
  onChange,
}: {
  archiveKind?: ArchiveKind;
  idPrefix: string;
  options: MaterialCaseOption[];
  selectedIds: number[];
  onChange: (values: number[]) => void;
}) {
  const copy = ARCHIVE_COPY[archiveKind];
  const [query, setQuery] = useState("");
  const communityGroups = useMemo(() => {
    const grouped = new Map<number, { id: number; name: string; cases: MaterialCaseOption[] }>();
    for (const item of options) {
      const group = grouped.get(item.community_id);
      if (group) group.cases.push(item);
      else grouped.set(item.community_id, { id: item.community_id, name: item.community_name, cases: [item] });
    }
    return Array.from(grouped.values()).sort((left, right) => COMMUNITY_NAME_COLLATOR.compare(left.name, right.name));
  }, [options]);
  const [communityChoice, setCommunityChoice] = useState(() => communityGroups[0]?.id ?? 0);
  const selectedCommunity = communityGroups.find((item) => item.id === communityChoice) ?? communityGroups[0] ?? null;
  const selectedSet = new Set(selectedIds);
  const selectedCommunityCount = selectedCommunity
    ? selectedCommunity.cases.filter((item) => selectedSet.has(item.id)).length
    : 0;
  const wholeCommunitySelected = Boolean(
    selectedCommunity && selectedCommunityCount === selectedCommunity.cases.length,
  );
  const needle = query.trim().toLocaleLowerCase("zh-CN");
  const filtered = options.filter((item) => {
    return !needle || `${item.community_name} ${item.source_folder}`.toLocaleLowerCase("zh-CN").includes(needle);
  });

  function toggle(id: number, checked: boolean) {
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    onChange(Array.from(next));
  }

  function toggleCommunity() {
    if (!selectedCommunity) return;
    const next = new Set(selectedIds);
    for (const item of selectedCommunity.cases) {
      if (wholeCommunitySelected) next.delete(item.id);
      else next.add(item.id);
    }
    onChange(Array.from(next));
  }

  return (
    <fieldset className="material-case-picker">
      <legend>关联{copy.singular}</legend>
      <div className="material-case-picker-head">
        <span>已选择 {selectedIds.length} 个{copy.singular}</span>
        {selectedIds.length > 0 && <button className="text-action" type="button" onClick={() => onChange([])}>清空</button>}
      </div>
      {selectedCommunity && (
        <div className="material-community-section">
          <div className="material-community-bulk">
            <label>
              <span>按{copy.group}整组关联</span>
              <select
                value={selectedCommunity.id}
                onChange={(event) => setCommunityChoice(Number(event.target.value))}
                aria-label={`选择要整组关联的${copy.group}`}
              >
                {communityGroups.map((group) => (
                  <option key={group.id} value={group.id}>{group.name} · {group.cases.length} {copy.count}</option>
                ))}
              </select>
            </label>
            <button className="secondary-action" type="button" onClick={toggleCommunity}>
              {wholeCommunitySelected ? <X aria-hidden="true" /> : <Check aria-hidden="true" />}
              {wholeCommunitySelected ? "取消整组" : selectedCommunityCount ? `补齐整个${copy.group}` : `关联整个${copy.group}`}
            </button>
          </div>
          <p aria-live="polite">
            {selectedCommunity.name}已选择 {selectedCommunityCount} / {selectedCommunity.cases.length} {copy.count}；整组选取后仍可在下方取消个别{copy.singular}。
          </p>
        </div>
      )}
      <SearchField
        className="material-case-search"
        label={`筛选关联${copy.singular}`}
        value={query}
        onChange={setQuery}
        placeholder={`搜索${copy.group}或${copy.singular}名称`}
        clearLabel={`清空关联${copy.singular}筛选`}
      />
      <div className="material-case-options">
        {filtered.map((item) => (
          <label className={selectedIds.includes(item.id) ? "selected" : ""} key={item.id} htmlFor={`${idPrefix}-${item.id}`}>
            <input
              id={`${idPrefix}-${item.id}`}
              type="checkbox"
              checked={selectedIds.includes(item.id)}
              onChange={(event) => toggle(item.id, event.target.checked)}
            />
            <span><b>{item.source_folder}</b><small>{item.community_name}</small></span>
          </label>
        ))}
        {!filtered.length && <p>没有匹配的{copy.singular}。</p>}
      </div>
    </fieldset>
  );
}

function MaterialEnterprisePicker({
  idPrefix,
  options,
  selectedIds,
  onChange,
}: {
  idPrefix: string;
  options: MaterialEnterpriseOption[];
  selectedIds: number[];
  onChange: (values: number[]) => void;
}) {
  const [query, setQuery] = useState("");
  const needle = query.trim().toLocaleLowerCase("zh-CN");
  const filtered = options.filter((item) => (
    !needle || `${item.title} ${item.category ?? ""}`.toLocaleLowerCase("zh-CN").includes(needle)
  ));

  function toggle(id: number, checked: boolean) {
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    onChange(Array.from(next));
  }

  return (
    <fieldset className="material-case-picker">
      <legend>关联企业资料</legend>
      <div className="material-case-picker-head">
        <span>已选择 {selectedIds.length} 份企业资料</span>
        {selectedIds.length > 0 && <button className="text-action" type="button" onClick={() => onChange([])}>清空</button>}
      </div>
      <SearchField
        className="material-case-search"
        label="筛选关联企业资料"
        value={query}
        onChange={setQuery}
        placeholder="搜索资料名称或分类"
        clearLabel="清空关联企业资料筛选"
      />
      <div className="material-case-options">
        {filtered.map((item) => (
          <label className={selectedIds.includes(item.id) ? "selected" : ""} key={item.id} htmlFor={`${idPrefix}-${item.id}`}>
            <input
              id={`${idPrefix}-${item.id}`}
              type="checkbox"
              checked={selectedIds.includes(item.id)}
              onChange={(event) => toggle(item.id, event.target.checked)}
            />
            <span><b>{item.title}</b><small>{item.category || "未分类"}</small></span>
          </label>
        ))}
        {!filtered.length && <p>没有匹配的企业资料。</p>}
      </div>
    </fieldset>
  );
}

function MaterialsAdmin({
  embedded = false,
  scope = "case",
}: {
  embedded?: boolean;
  scope?: "case" | "material" | "enterprise";
}) {
  const archiveLabel = scope === "case" ? "案例" : scope === "material" ? "素材" : "企业";
  const [materials, setMaterials] = useState<AdminMaterial[] | null>(null);
  const [caseOptions, setCaseOptions] = useState<MaterialCaseOption[] | null>(null);
  const [enterpriseOptions, setEnterpriseOptions] = useState<MaterialEnterpriseOption[] | null>(null);
  const [query, setQuery] = useState("");
  const [settledQuery, setSettledQuery] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createMode, setCreateMode] = useState<"upload" | "link">("upload");
  const [createKind, setCreateKind] = useState<"douyin" | "panorama" | "link">("panorama");
  const [createCaseIds, setCreateCaseIds] = useState<number[]>([]);
  const [editCaseIds, setEditCaseIds] = useState<number[]>([]);
  const [createEnterpriseIds, setCreateEnterpriseIds] = useState<number[]>([]);
  const [editEnterpriseIds, setEditEnterpriseIds] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useTransientNotice();
  const [reloadKey, setReloadKey] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [downloadPrompt, setDownloadPrompt] = useState<MaterialDownloadPrompt | null>(null);
  const createFormRef = useRef<HTMLFormElement>(null);
  const deleteFocusRef = useRef({ active: false, materialId: null as number | null });
  const materialDeleteTriggerRef = useRef<HTMLButtonElement>(null);
  const materialDeleteConfirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setSettledQuery(query.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let active = true;
    setError("");
    Promise.all([
      api.adminMaterials(settledQuery, scope),
      scope === "case" ? api.materialCaseOptions() : scope === "material" ? api.materialMaterialOptions() : Promise.resolve(null),
      scope === "enterprise" ? api.materialEnterpriseOptions() : Promise.resolve(null),
    ])
      .then(([materialPayload, casePayload, enterprisePayload]) => {
        if (!active) return;
        setMaterials(materialPayload);
        setCaseOptions(casePayload);
        setEnterpriseOptions(enterprisePayload);
        if (selectedId !== null && !materialPayload.some((item) => item.id === selectedId)) {
          setSelectedId(null);
        }
      })
      .catch((reason) => active && setError(messageFrom(reason)));
    return () => { active = false; };
  }, [reloadKey, scope, selectedId, settledQuery]);

  useLayoutEffect(() => {
    if (!deleteFocusRef.current.active || !materials || selectedId !== null) return;
    deleteFocusRef.current.active = false;
    const targetId = deleteFocusRef.current.materialId;
    const frame = window.requestAnimationFrame(() => {
      const target = targetId === null ? null : document.getElementById(`material-ledger-${targetId}`);
      (target ?? createFormRef.current?.querySelector<HTMLElement>("input, button"))?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [materials, selectedId]);

  useEffect(() => {
    if (!deleteConfirm) return;
    const frame = window.requestAnimationFrame(() => materialDeleteConfirmRef.current?.focus());
    function keydown(event: KeyboardEvent) {
      if (event.key !== "Escape" || busy) return;
      event.preventDefault();
      setDeleteConfirm(false);
      window.requestAnimationFrame(() => materialDeleteTriggerRef.current?.focus({ preventScroll: true }));
    }
    window.addEventListener("keydown", keydown);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("keydown", keydown);
    };
  }, [busy, deleteConfirm]);

  const selected = materials?.find((item) => item.id === selectedId) ?? null;
  const selectedAssociations = selected
    ? scope === "case" ? selected.cases : scope === "material" ? selected.material_cases : selected.enterprise_documents
    : [];

  useEffect(() => {
    setEditCaseIds((scope === "material" ? selected?.material_cases : selected?.cases)?.map((item) => item.id) ?? []);
    setEditEnterpriseIds(selected?.enterprise_documents.map((item) => item.id) ?? []);
    setDeleteConfirm(false);
  }, [selectedId, selected?.version]);

  async function createMaterial(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const label = String(data.get("label") ?? "").trim();
    setBusy(true);
    setUploadProgress(null);
    setError("");
    setNotice("");
    try {
      if (createMode === "upload") {
        const file = data.get("file");
        if (!(file instanceof File) || !file.name) throw new Error("请选择要上传的附件。");
        setUploadProgress(0);
        await api.uploadMaterial(
          file,
          label,
          scope === "case" ? createCaseIds : [],
          setUploadProgress,
          scope,
          scope === "enterprise" ? createEnterpriseIds : [],
          scope === "material" ? createCaseIds : [],
        );
      } else {
        await api.createLinkMaterial({
          scope,
          kind: createKind,
          label,
          url: String(data.get("url") ?? "").trim(),
          preview_url: createKind === "panorama" ? String(data.get("preview_url") ?? "").trim() || null : null,
          case_ids: scope === "case" ? createCaseIds : [],
          material_case_ids: scope === "material" ? createCaseIds : [],
          enterprise_document_ids: scope === "enterprise" ? createEnterpriseIds : [],
        });
      }
      form.reset();
      setCreateCaseIds([]);
      setCreateEnterpriseIds([]);
      setNotice(`${label} 已加入${archiveLabel}资料库。`);
      setReloadKey((value) => value + 1);
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
      setUploadProgress(null);
    }
  }

  async function saveMaterial(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected || busy) return;
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api.updateMaterial(selected.id, {
        version: selected.version,
        label: String(data.get("label") ?? "").trim(),
        target_url: selected.target_url ? String(data.get("target_url") ?? "").trim() : null,
        preview_url: selected.kind === "panorama" ? String(data.get("preview_url") ?? "").trim() || null : null,
        case_ids: scope === "case" ? editCaseIds : [],
        material_case_ids: scope === "material" ? editCaseIds : [],
        enterprise_document_ids: scope === "enterprise" ? editEnterpriseIds : [],
        published: data.get("published") === "on",
      });
      setNotice(`资料信息和${archiveLabel}关联已保存；共享资料会同步更新到所有关联项。`);
      setReloadKey((value) => value + 1);
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function removeMaterial() {
    if (!selected || busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    const currentIndex = materials?.findIndex((item) => item.id === selected.id) ?? -1;
    const focusCandidate = currentIndex >= 0
      ? materials?.[currentIndex + 1] ?? materials?.[currentIndex - 1] ?? null
      : null;
    deleteFocusRef.current = { active: true, materialId: focusCandidate?.id ?? null };
    try {
      await api.deleteMaterial(selected.id);
      setNotice(`${selected.label} 已从资料库删除。`);
      setSelectedId(null);
      setReloadKey((value) => value + 1);
    } catch (reason) {
      deleteFocusRef.current.active = false;
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="materials-admin">
      <div className="section-actions">
        <div>
          {embedded ? <h3>关联资料</h3> : <h2>{archiveLabel}资料库</h2>}
          <p>{scope === "enterprise" ? "文件上传一次即可关联多份企业资料；解除单份关联不会删除资料文件。" : `文件上传一次即可关联多个${archiveLabel}；解除单项关联不会删除资料文件。`}</p>
        </div>
      </div>
      {error && <div className="account-feedback form-error workspace-feedback" role="alert"><CircleAlert aria-hidden="true" /> {error}</div>}
      {notice && <ActionFeedback className="account-feedback workspace-feedback" message={notice} onDismiss={() => setNotice("")} />}

      <div className="material-admin-layout">
        <form ref={createFormRef} className="material-create-panel" onSubmit={createMaterial}>
          <header><span className="case-upload-mark"><FileBox aria-hidden="true" /></span><div><h3>新增{archiveLabel}资料</h3><p>上传附件或登记链接，不写入原始效果图目录。</p></div></header>
          <div className="material-create-mode" role="group" aria-label="新增资料方式">
            <button type="button" className={createMode === "upload" ? "active" : ""} aria-pressed={createMode === "upload"} onClick={() => setCreateMode("upload")}><Upload aria-hidden="true" /> 上传附件</button>
            <button type="button" className={createMode === "link" ? "active" : ""} aria-pressed={createMode === "link"} onClick={() => setCreateMode("link")}><Link aria-hidden="true" /> 登记链接</button>
          </div>
          <label><span>资料名称</span><input name="label" required maxLength={160} placeholder={createMode === "upload" ? "例如：万豪国际 A 户型附件" : "例如：客厅全景展示"} /></label>
          {createMode === "upload" ? (
            <>
              <label className="material-file-field">
                <span>选择文件</span>
                <input name="file" type="file" required disabled={busy} />
                <small>文件格式不限；本地单文件上限 1 GB。</small>
              </label>
              {uploadProgress !== null && <UploadProgress value={uploadProgress} />}
            </>
          ) : (
            <>
              <label><span>链接类型</span><select value={createKind} onChange={(event) => setCreateKind(event.target.value as typeof createKind)}><option value="panorama">全景图</option><option value="douyin">抖音视频</option><option value="link">普通网页</option></select></label>
              <label><span>打开链接</span><input name="url" type="url" required placeholder="https://" /></label>
              {createKind === "panorama" && <label><span>嵌入预览链接（可留空）</span><input name="preview_url" type="url" placeholder="留空则尝试使用打开链接预览" /></label>}
            </>
          )}
          {caseOptions && <MaterialCasePicker archiveKind={scope === "material" ? "material" : "case"} idPrefix="create-material-case" options={caseOptions} selectedIds={createCaseIds} onChange={setCreateCaseIds} />}
          {enterpriseOptions && <MaterialEnterprisePicker idPrefix="create-material-enterprise" options={enterpriseOptions} selectedIds={createEnterpriseIds} onChange={setCreateEnterpriseIds} />}
          <button className="primary-action" type="submit" disabled={busy}>{busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <FileBox aria-hidden="true" />}{uploadProgress === null ? (busy ? "正在保存" : "加入资料库") : uploadProgress >= 100 ? "服务器处理中" : `正在上传 ${uploadProgress}%`}</button>
        </form>

        <section className="material-library-panel">
          <header>
            <div className="material-library-title"><h3>资料台账</h3><span>{materials?.length ?? 0} 项</span></div>
            <SearchField className="material-library-search" label="搜索资料台账" value={query} onChange={setQuery} placeholder="搜索资料名称或文件名" clearLabel="清空资料台账搜索" />
          </header>
          {!materials && !error ? <LoadingState label={`正在读取${archiveLabel}资料`} /> : selected ? (
            <form className="material-editor" key={`${selected.id}-${selected.version}`} onSubmit={saveMaterial}>
              <button className="back-action" type="button" onClick={() => setSelectedId(null)}><ArrowLeft aria-hidden="true" /> 返回资料台账</button>
              <div className="material-editor-title"><MaterialKindIcon kind={selected.kind} /><div><span>{materialKindLabel(selected.kind)}</span><h3>{selected.label}</h3><small>{selected.file_name || selected.target_url}</small></div></div>
              {selectedAssociations.length > 1 && <div className="material-shared-note"><CircleAlert aria-hidden="true" />当前资料由 {selectedAssociations.length} 个{archiveLabel}共用；修改名称或链接会同步影响全部关联项。</div>}
              <label><span>资料名称</span><input name="label" defaultValue={selected.label} required maxLength={160} /></label>
              {selected.target_url && <label><span>打开链接</span><input name="target_url" type="url" defaultValue={selected.target_url} required /></label>}
              {selected.kind === "panorama" && <label><span>嵌入预览链接</span><input name="preview_url" type="url" defaultValue={selected.preview_url ?? ""} placeholder="留空则使用打开链接" /></label>}
              <label className="material-published-toggle"><input name="published" type="checkbox" defaultChecked={Boolean(selected.published)} /><span><b>员工端展示</b><small>关闭后所有关联{archiveLabel}暂时隐藏这项资料。</small></span></label>
              {caseOptions && <MaterialCasePicker archiveKind={scope === "material" ? "material" : "case"} idPrefix={`edit-material-${selected.id}-case`} options={caseOptions} selectedIds={editCaseIds} onChange={setEditCaseIds} />}
              {enterpriseOptions && <MaterialEnterprisePicker idPrefix={`edit-material-${selected.id}-enterprise`} options={enterpriseOptions} selectedIds={editEnterpriseIds} onChange={setEditEnterpriseIds} />}
              <div className="material-editor-footer">
                <div className="material-delete-zone">
                  {deleteConfirm ? <div id={`material-delete-confirm-${selected.id}`} role="alert"><span>确定永久删除？{(scope === "case" ? selected.cases.length : selected.enterprise_documents.length) ? `与 ${scope === "case" ? selected.cases.length : selected.enterprise_documents.length} 个关联项也会解除；` : ""}上传文件会从应用资料目录移除，无法恢复。</span><button className="text-action" type="button" onClick={() => { setDeleteConfirm(false); window.requestAnimationFrame(() => materialDeleteTriggerRef.current?.focus({ preventScroll: true })); }}>取消</button><button ref={materialDeleteConfirmRef} className="danger-action" type="button" disabled={busy} onClick={() => void removeMaterial()}><Trash2 aria-hidden="true" />确认永久删除</button></div> : <button ref={materialDeleteTriggerRef} className="danger-action danger-trigger" type="button" aria-expanded={false} aria-controls={`material-delete-confirm-${selected.id}`} onClick={() => { setNotice(""); setDeleteConfirm(true); }}><Trash2 aria-hidden="true" />永久删除资料</button>}
                </div>
                <div className="material-editor-actions">
                  {selected.kind === "cad" || selected.kind === "cad_archive" ? (
                    <button
                      className="secondary-action"
                      type="button"
                      onClick={(event) => setDownloadPrompt({ material: selected, returnTarget: event.currentTarget })}
                    >
                      测试下载 <Download aria-hidden="true" />
                    </button>
                  ) : (
                    <a className="secondary-action" href={selected.open_url} target="_blank" rel="noreferrer">测试打开 <ArrowUpRight aria-hidden="true" /></a>
                  )}
                  <button className="primary-action" type="submit" disabled={busy}>{busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <Save aria-hidden="true" />}{busy ? "保存中" : "保存资料"}</button>
                </div>
              </div>
            </form>
          ) : materials?.length ? (
            <div className="material-ledger">
              {materials.map((item) => (
                <button id={`material-ledger-${item.id}`} type="button" onClick={() => { setSelectedId(item.id); setNotice(""); }} key={item.id}>
                  <MaterialKindIcon kind={item.kind} />
                  <span><b>{item.label}</b><small>{materialKindLabel(item.kind)}{item.file_name ? ` · ${item.file_name}` : ""}</small></span>
                  <span><b>{scope === "case" ? item.cases.length : scope === "material" ? item.material_cases.length : item.enterprise_documents.length}</b><small>关联{archiveLabel}</small></span>
                  <ArrowRight aria-hidden="true" />
                </button>
              ))}
            </div>
          ) : <div className="case-admin-empty"><FileBox aria-hidden="true" /><strong>资料库还是空的</strong><p>从左侧上传附件，或登记全景图、抖音和网页链接。</p></div>}
        </section>
      </div>
      {downloadPrompt && (
        <MaterialDownloadDialog prompt={downloadPrompt} onClose={() => setDownloadPrompt(null)} />
      )}
    </section>
  );
}

function EnterpriseAdmin({
  initialDocumentId = null,
  startEditing = false,
  standalone = false,
  initialActiveImage = 0,
  initialScrollTop = 0,
  onStandaloneBack,
  onStandaloneDeleted,
}: {
  initialDocumentId?: number | null;
  startEditing?: boolean;
  standalone?: boolean;
  initialActiveImage?: number;
  initialScrollTop?: number;
  onStandaloneBack?: () => void;
  onStandaloneDeleted?: () => void;
} = {}) {
  const [documents, setDocuments] = useState<Awaited<ReturnType<typeof api.adminEnterpriseDocuments>> | null>(null);
  const [query, setQuery] = useState("");
  const [settledQuery, setSettledQuery] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(initialDocumentId);
  const [detail, setDetail] = useState<EnterpriseDocumentDetail | null>(null);
  const [editing, setEditing] = useState(false);
  const [editorDraft, setEditorDraft] = useState({ title: "", category: "", description: "" });
  const [showUpload, setShowUpload] = useState(false);
  const [orderedIds, setOrderedIds] = useState<number[]>([]);
  const [coverId, setCoverId] = useState<number | null>(null);
  const [activeDragImageId, setActiveDragImageId] = useState<number | null>(null);
  const [imageNotice, setImageNotice] = useTransientNotice();
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [editorPreviewImage, setEditorPreviewImage] = useState<EnterpriseSortableImage | null>(null);
  const [imageUploadProgress, setImageUploadProgress] = useState<number | null>(null);
  const [imageError, setImageError] = useState("");
  const [imageDeleteConfirm, setImageDeleteConfirm] = useState<EnterpriseSortableImage | null>(null);
  const [pendingImageDeletes, setPendingImageDeletes] = useState<EnterpriseSortableImage[]>([]);
  const [editorMaterials, setEditorMaterials] = useState<AdminMaterial[] | null>(null);
  const [materialError, setMaterialError] = useState("");
  const [materialNotice, setMaterialNotice] = useTransientNotice();
  const [materialCreateMode, setMaterialCreateMode] = useState<"upload" | "link">("upload");
  const [materialDraft, setMaterialDraft] = useState({ label: "", kind: "link" as "panorama" | "douyin" | "link", url: "", previewUrl: "", file: null as File | null });
  const [pendingMaterialCreates, setPendingMaterialCreates] = useState<PendingMaterialCreate[]>([]);
  const [pendingMaterialRemoveIds, setPendingMaterialRemoveIds] = useState<number[]>([]);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useTransientNotice();
  const [reloadKey, setReloadKey] = useState(0);
  const [detailActiveImage, setDetailActiveImage] = useState(initialActiveImage);
  const [deleteConfirm, setDeleteConfirm] = useState<EnterpriseDocument | null>(null);
  const uploadTriggerRef = useRef<HTMLButtonElement>(null);
  const uploadTitleRef = useRef<HTMLInputElement>(null);
  const editorTitleRef = useRef<HTMLInputElement>(null);
  const editorPreviewButtonRef = useRef<HTMLButtonElement>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);
  const materialFileInputRef = useRef<HTMLInputElement>(null);
  const orderedIdsRef = useRef<number[]>([]);
  const enterpriseAdminRef = useRef<HTMLElement>(null);
  const returnPositionRef = useRef({ scrollTop: 0, documentId: null as number | null });
  const shouldRestorePositionRef = useRef(false);
  const deleteFocusRef = useRef({ active: false, documentId: null as number | null });
  const detailScrollTopRef = useRef(initialScrollTop);
  const autoEditPendingRef = useRef(startEditing);
  const imageSortSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 140, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    orderedIdsRef.current = orderedIds;
  }, [orderedIds]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSettledQuery(query.trim());
      setPage(1);
    }, 220);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (selectedId !== null) return;
    let active = true;
    setError("");
    api.adminEnterpriseDocuments({ q: settledQuery, page })
      .then((items) => active && setDocuments(items))
      .catch((reason) => active && setError(messageFrom(reason)));
    return () => { active = false; };
  }, [page, reloadKey, selectedId, settledQuery]);

  useLayoutEffect(() => {
    if (selectedId !== null || !shouldRestorePositionRef.current) return;
    shouldRestorePositionRef.current = false;
    const position = returnPositionRef.current;
    const restore = () => {
      const scroller = enterpriseAdminRef.current
        ?.closest<HTMLElement>(".admin-view")
        ?.querySelector<HTMLElement>(":scope > section[role='tabpanel']");
      scroller?.scrollTo({ top: position.scrollTop, left: 0, behavior: "auto" });
    };
    restore();
    const frame = window.requestAnimationFrame(() => {
      restore();
      if (position.documentId !== null) {
        document.getElementById(`enterprise-admin-open-${position.documentId}`)?.focus({ preventScroll: true });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selectedId]);

  useLayoutEffect(() => {
    if (!deleteFocusRef.current.active || !documents || selectedId !== null) return;
    deleteFocusRef.current.active = false;
    const targetId = deleteFocusRef.current.documentId;
    const frame = window.requestAnimationFrame(() => {
      const target = targetId === null ? null : document.getElementById(`enterprise-admin-open-${targetId}`);
      (target ?? uploadTriggerRef.current)?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [documents, selectedId]);

  useEffect(() => {
    if (selectedId === null) {
      setDetail(null);
      return;
    }
    let active = true;
    setError("");
    api.enterpriseDocument(selectedId)
      .then((item) => {
        if (!active) return;
        setDetail(item);
        const shouldStartEditing = autoEditPendingRef.current;
        autoEditPendingRef.current = false;
        setEditing(shouldStartEditing);
        setEditorDraft({ title: item.title, category: item.category ?? "", description: item.description ?? "" });
        setOrderedIds(item.images.map((image) => image.id));
        orderedIdsRef.current = item.images.map((image) => image.id);
        setCoverId(item.cover_image_id);
        setActiveDragImageId(null);
        setImageNotice("");
        setImageFiles([]);
        setPendingImageDeletes([]);
        setImageDeleteConfirm(null);
        setEditorMaterials(null);
        setPendingMaterialCreates([]);
        setPendingMaterialRemoveIds([]);
        if (shouldStartEditing) {
          window.requestAnimationFrame(() => editorTitleRef.current?.focus({ preventScroll: true }));
        }
      })
      .catch((reason) => active && setError(messageFrom(reason)));
    return () => { active = false; };
  }, [reloadKey, selectedId]);

  useEffect(() => {
    if (!editing || selectedId === null) return;
    let active = true;
    setEditorMaterials(null);
    setMaterialError("");
    api.adminMaterials("", "enterprise")
      .then((items) => {
        if (!active) return;
        setEditorMaterials(items.filter((item) => item.enterprise_documents.some((document) => document.id === selectedId)));
      })
      .catch((reason) => active && setMaterialError(`附件读取失败：${messageFrom(reason)} 请稍后重试。`));
    return () => { active = false; };
  }, [editing, selectedId]);

  useEffect(() => {
    if (!showUpload) return;
    const frame = window.requestAnimationFrame(() => uploadTitleRef.current?.focus());
    function keydown(event: KeyboardEvent) {
      if (event.defaultPrevented || document.querySelector(".image-workbench")) return;
      if (event.key !== "Escape" || busy) return;
      event.preventDefault();
      setShowUpload(false);
      setUploadFiles([]);
      setUploadProgress(null);
      setError("");
      window.requestAnimationFrame(() => uploadTriggerRef.current?.focus());
    }
    window.addEventListener("keydown", keydown);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("keydown", keydown);
    };
  }, [busy, showUpload]);

  useEffect(() => {
    if (!deleteConfirm) return;
    const documentId = deleteConfirm.id;
    const frame = window.requestAnimationFrame(() => document.getElementById(`enterprise-delete-confirm-${documentId}`)?.focus());
    function keydown(event: KeyboardEvent) {
      if (event.key !== "Escape" || busy) return;
      event.preventDefault();
      setDeleteConfirm(null);
      window.requestAnimationFrame(() => document.getElementById(`enterprise-delete-trigger-${documentId}`)?.focus());
    }
    window.addEventListener("keydown", keydown);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("keydown", keydown);
    };
  }, [busy, deleteConfirm]);

  useEffect(() => {
    if (!editing) return;
    const confirmedImageId = imageDeleteConfirm?.id;
    const frame = confirmedImageId
      ? window.requestAnimationFrame(() => document.getElementById(`enterprise-image-confirm-${confirmedImageId}`)?.focus())
      : 0;
    function keydown(event: KeyboardEvent) {
      if (event.defaultPrevented || document.querySelector(".image-workbench")) return;
      if (event.key !== "Escape" || busy) return;
      event.preventDefault();
      if (confirmedImageId) {
        setImageDeleteConfirm(null);
        window.requestAnimationFrame(() => document.getElementById(`enterprise-image-delete-${confirmedImageId}`)?.focus());
        return;
      }
      closeEditor();
    }
    window.addEventListener("keydown", keydown);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("keydown", keydown);
    };
  }, [busy, editing, imageDeleteConfirm]);

  async function uploadDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const fileError = enterpriseFileError(uploadFiles);
    if (fileError || busy) {
      if (fileError) setError(fileError);
      return;
    }
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setUploadProgress(0);
    setError("");
    setNotice("");
    try {
      const result = await api.uploadEnterpriseDocument({
        title: String(data.get("title") ?? "").trim(),
        category: String(data.get("category") ?? "").trim(),
        description: String(data.get("description") ?? "").trim(),
        files: uploadFiles,
        onProgress: setUploadProgress,
      });
      form.reset();
      setUploadFiles([]);
      setShowUpload(false);
      setNotice(`“${result.title}”已上传并发布，共 ${result.image_count} 张图片。`);
      setSelectedId(result.id);
      setReloadKey((value) => value + 1);
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
      setUploadProgress(null);
    }
  }

  function beginImageSort(event: DragStartEvent) {
    setActiveDragImageId(Number(event.active.id));
    setError("");
  }

  function finishImageSort(event: DragEndEvent) {
    const imageId = Number(event.active.id);
    const targetId = event.over ? Number(event.over.id) : null;
    setActiveDragImageId(null);
    if (targetId !== null && targetId !== imageId) {
      const current = orderedIdsRef.current;
      const from = current.indexOf(imageId);
      const to = current.indexOf(targetId);
      if (from >= 0 && to >= 0) {
        const next = arrayMove(current, from, to);
        orderedIdsRef.current = next;
        setOrderedIds(next);
        const matchesSavedOrder = Boolean(
          detail
          && next.length === detail.images.length
          && next.every((id, index) => detail.images[index]?.id === id),
        );
        if (matchesSavedOrder) {
          setImageNotice(coverId === detail?.cover_image_id
            ? "图片顺序已恢复为已保存状态。"
            : "图片顺序已恢复，封面选择仍待保存。");
        } else {
          setImageNotice(`已移到第 ${to + 1} 张，保存企业资料后生效。`);
        }
      }
    }
    window.requestAnimationFrame(() => document.getElementById(`enterprise-image-sort-handle-${imageId}`)?.focus());
  }

  function cancelImageSort() {
    const imageId = activeDragImageId;
    setActiveDragImageId(null);
    setImageNotice("已取消本次拖动，图片顺序未改变。");
    if (imageId !== null) {
      window.requestAnimationFrame(() => document.getElementById(`enterprise-image-sort-handle-${imageId}`)?.focus());
    }
  }

  function openEditor(viewState?: { activeImage: number; scrollTop: number }) {
    if (!detail) return;
    if (viewState) {
      setDetailActiveImage(viewState.activeImage);
      detailScrollTopRef.current = viewState.scrollTop;
    }
    const initialIds = detail.images.map((image) => image.id);
    orderedIdsRef.current = initialIds;
    setOrderedIds(initialIds);
    setCoverId(detail.cover_image_id ?? initialIds[0] ?? null);
    setEditorDraft({ title: detail.title, category: detail.category ?? "", description: detail.description ?? "" });
    setImageFiles([]);
    setImageUploadProgress(null);
    setImageError("");
    setImageNotice("");
    setImageDeleteConfirm(null);
    setPendingImageDeletes([]);
    setEditorMaterials(null);
    setMaterialError("");
    setMaterialNotice("");
    setMaterialCreateMode("upload");
    setMaterialDraft({ label: "", kind: "link", url: "", previewUrl: "", file: null });
    setPendingMaterialCreates([]);
    setPendingMaterialRemoveIds([]);
    setNotice("");
    setError("");
    setEditing(true);
    window.requestAnimationFrame(() => editorTitleRef.current?.focus({ preventScroll: true }));
  }

  function closeImageDeleteConfirm() {
    if (!imageDeleteConfirm || busy) return;
    const imageId = imageDeleteConfirm.id;
    setImageDeleteConfirm(null);
    window.requestAnimationFrame(() => document.getElementById(`enterprise-image-delete-${imageId}`)?.focus());
  }

  function stageImageDelete(item: EnterpriseSortableImage) {
    if (orderedIds.length + imageFiles.length <= 1) {
      setImageError("企业资料至少需要保留一张图片，请先追加新图片再移除当前图片。");
      return;
    }
    const nextOrder = orderedIds.filter((id) => id !== item.id);
    orderedIdsRef.current = nextOrder;
    setOrderedIds(nextOrder);
    setPendingImageDeletes((items) => [...items, item]);
    if (coverId === item.id) setCoverId(nextOrder[0] ?? null);
    setImageDeleteConfirm(null);
    setImageError("");
    setImageNotice(`“${item.file_name}”已加入待删除，取消编辑不会删除。`);
  }

  function undoImageDelete(item: EnterpriseSortableImage) {
    const originalIndex = detail?.images.findIndex((candidate) => candidate.id === item.id) ?? -1;
    const nextOrder = [...orderedIds];
    nextOrder.splice(Math.max(0, Math.min(originalIndex, nextOrder.length)), 0, item.id);
    orderedIdsRef.current = nextOrder;
    setOrderedIds(nextOrder);
    setPendingImageDeletes((items) => items.filter((candidate) => candidate.id !== item.id));
    setImageError("");
    setImageNotice(`已撤销删除“${item.file_name}”。`);
  }

  function stageMaterialCreate() {
    const label = materialDraft.label.trim();
    if (!label) {
      setMaterialError("请先填写资料名称。");
      return;
    }
    const tempId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    if (materialCreateMode === "upload") {
      const file = materialDraft.file;
      if (!file?.name) {
        setMaterialError("请选择要上传的附件。");
        return;
      }
      if (file.size > 1024 * 1024 * 1024) {
        setMaterialError("单个附件不能超过 1 GB。");
        return;
      }
      setPendingMaterialCreates((items) => [...items, { tempId, mode: "upload", label, file }]);
    } else {
      const url = materialDraft.url.trim();
      const previewUrl = materialDraft.previewUrl.trim();
      try {
        const parsed = new URL(url);
        if (!/^https?:$/.test(parsed.protocol)) throw new Error();
        if (previewUrl) {
          const preview = new URL(previewUrl);
          if (!/^https?:$/.test(preview.protocol)) throw new Error();
        }
      } catch {
        setMaterialError("请输入以 http:// 或 https:// 开头的有效链接。");
        return;
      }
      setPendingMaterialCreates((items) => [...items, { tempId, mode: "link", label, kind: materialDraft.kind, url, previewUrl: materialDraft.kind === "panorama" ? previewUrl || null : null }]);
    }
    setMaterialDraft((value) => ({ ...value, label: "", url: "", previewUrl: "", file: null }));
    if (materialFileInputRef.current) materialFileInputRef.current.value = "";
    setMaterialError("");
    setMaterialNotice(`“${label}”已加入待新增，保存全部更改后上传。`);
  }

  function closeEditor() {
    if (!detail || busy) return;
    const restoredIds = detail.images.map((image) => image.id);
    orderedIdsRef.current = restoredIds;
    setOrderedIds(restoredIds);
    setCoverId(detail.cover_image_id);
    setImageNotice("");
    setImageFiles([]);
    setImageUploadProgress(null);
    setImageError("");
    setImageDeleteConfirm(null);
    setPendingImageDeletes([]);
    setEditorMaterials(null);
    setMaterialError("");
    setMaterialNotice("");
    setMaterialCreateMode("upload");
    setMaterialDraft({ label: "", kind: "link", url: "", previewUrl: "", file: null });
    setPendingMaterialCreates([]);
    setPendingMaterialRemoveIds([]);
    if (imageFileInputRef.current) imageFileInputRef.current.value = "";
    if (materialFileInputRef.current) materialFileInputRef.current.value = "";
    setEditorDraft({ title: detail.title, category: detail.category ?? "", description: detail.description ?? "" });
    setError("");
    setEditing(false);
    window.requestAnimationFrame(() => document.getElementById("enterprise-detail-edit")?.focus());
  }

  async function deleteCurrentDocument() {
    if (!detail || busy) return;
    setBusy(true);
    setError("");
    try {
      await api.deleteEnterpriseDocument(detail.id);
      setNotice(`“${detail.title}”及其 ${detail.image_count} 张服务器图片已永久删除。`);
      setEditing(false);
      setSelectedId(null);
      setDetail(null);
      setReloadKey((value) => value + 1);
      onStandaloneDeleted?.();
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function saveDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!detail || busy) return;
    if (materialDraft.label.trim() || materialDraft.url.trim() || materialDraft.previewUrl.trim() || materialDraft.file) {
      setMaterialError("附件新增区还有尚未加入待保存的内容，请先点击“加入待保存”或清空输入。");
      document.getElementById("enterprise-attachment-editor-title")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const fileError = imageFiles.length ? enterpriseFileError(imageFiles) : "";
    if (fileError) {
      setImageError(fileError);
      return;
    }
    if (orderedIds.length + imageFiles.length < 1) {
      setImageError("企业资料至少需要保留一张图片，请先追加新图片或撤销待删除项。");
      return;
    }
    if (orderedIds.length + imageFiles.length > ARCHIVE_UPLOAD_MAX_FILES) {
      setImageError(`每份企业资料最多保留 ${ARCHIVE_UPLOAD_MAX_FILES} 张图片。`);
      return;
    }
    setBusy(true);
    setImageUploadProgress(null);
    setError("");
    setNotice("");
    setImageError("");
    setMaterialError("");
    let serverMutated = false;
    let remainingImageDeletes = [...pendingImageDeletes];
    let remainingMaterialRemoveIds = [...pendingMaterialRemoveIds];
    let remainingMaterialCreates = [...pendingMaterialCreates];
    try {
      let version = detail.version;
      let addedImageIds: number[] = [];
      if (imageFiles.length) {
        setImageUploadProgress(0);
        const uploadResult = await api.addEnterpriseImages(detail.id, version, imageFiles, setImageUploadProgress);
        version = uploadResult.version;
        addedImageIds = uploadResult.image_ids;
        serverMutated = true;
        setImageFiles([]);
        if (imageFileInputRef.current) imageFileInputRef.current.value = "";
      }
      for (const item of pendingImageDeletes) {
        const result = await api.deleteEnterpriseImage(detail.id, item.id, version);
        version = result.version;
        serverMutated = true;
        remainingImageDeletes = remainingImageDeletes.filter((candidate) => candidate.id !== item.id);
        setPendingImageDeletes(remainingImageDeletes);
      }
      const finalImageIds = [...orderedIds, ...addedImageIds];
      const finalCoverId = coverId && finalImageIds.includes(coverId) ? coverId : finalImageIds[0] ?? null;
      await api.updateEnterpriseDocument(detail.id, {
        version,
        title: editorDraft.title.trim(),
        category: editorDraft.category.trim() || null,
        description: editorDraft.description.trim() || null,
        cover_image_id: finalCoverId,
        image_ids: finalImageIds,
        published: Boolean(detail.published),
      });
      serverMutated = true;
      for (const materialId of pendingMaterialRemoveIds) {
        const item = editorMaterials?.find((candidate) => candidate.id === materialId);
        if (!item) continue;
        if (item.enterprise_documents.length > 1) {
          await api.updateMaterial(item.id, {
            version: item.version,
            label: item.label,
            target_url: item.target_url,
            preview_url: item.preview_url,
            case_ids: [],
            enterprise_document_ids: item.enterprise_documents.filter((document) => document.id !== detail.id).map((document) => document.id),
            published: Boolean(item.published),
          });
        } else {
          await api.deleteMaterial(item.id);
        }
        serverMutated = true;
        remainingMaterialRemoveIds = remainingMaterialRemoveIds.filter((id) => id !== materialId);
        setPendingMaterialRemoveIds(remainingMaterialRemoveIds);
      }
      for (const item of pendingMaterialCreates) {
        if (item.mode === "upload") {
          await api.uploadMaterial(item.file, item.label, [], undefined, "enterprise", [detail.id]);
        } else {
          await api.createLinkMaterial({
            scope: "enterprise",
            kind: item.kind,
            label: item.label,
            url: item.url,
            preview_url: item.previewUrl,
            case_ids: [],
            enterprise_document_ids: [detail.id],
          });
        }
        serverMutated = true;
        remainingMaterialCreates = remainingMaterialCreates.filter((candidate) => candidate.tempId !== item.tempId);
        setPendingMaterialCreates(remainingMaterialCreates);
      }
      const refreshed = await api.enterpriseDocument(detail.id);
      setDetail(refreshed);
      const refreshedIds = refreshed.images.map((image) => image.id);
      orderedIdsRef.current = refreshedIds;
      setOrderedIds(refreshedIds);
      setCoverId(refreshed.cover_image_id);
      setNotice("基本信息、图片和附件已统一保存。");
      setImageNotice("");
      setImageFiles([]);
      setPendingImageDeletes([]);
      setPendingMaterialCreates([]);
      setPendingMaterialRemoveIds([]);
      setEditorMaterials(null);
      setEditing(false);
      setEditorDraft({ title: refreshed.title, category: refreshed.category ?? "", description: refreshed.description ?? "" });
      setReloadKey((value) => value + 1);
      window.requestAnimationFrame(() => document.getElementById("enterprise-detail-edit")?.focus());
    } catch (reason) {
      if (serverMutated) {
        try {
          const refreshed = await api.enterpriseDocument(detail.id);
          setDetail(refreshed);
          const refreshedIds = refreshed.images.map((image) => image.id);
          orderedIdsRef.current = refreshedIds;
          setOrderedIds(refreshedIds);
          setPendingImageDeletes([]);
          setImageFiles([]);
          if (imageFileInputRef.current) imageFileInputRef.current.value = "";
          const materials = await api.adminMaterials("", "enterprise");
          setEditorMaterials(materials.filter((item) => item.enterprise_documents.some((document) => document.id === detail.id)));
        } catch {
          // Preserve the original error; retrying reloads current server state.
        }
      }
      setError(serverMutated
        ? `保存未完整完成，已重新读取成功写入的部分。请检查后继续保存：${messageFrom(reason)}`
        : `保存失败，所有待保存内容仍保留在当前页面：${messageFrom(reason)}`);
    } finally {
      setBusy(false);
      setImageUploadProgress(null);
    }
  }

  async function deleteDocument(item: EnterpriseDocument) {
    if (busy) return;
    setBusy(true);
    setError("");
    const currentIndex = documents?.items.findIndex((candidate) => candidate.id === item.id) ?? -1;
    const focusCandidate = currentIndex >= 0
      ? documents?.items[currentIndex + 1] ?? documents?.items[currentIndex - 1] ?? null
      : null;
    deleteFocusRef.current = { active: true, documentId: focusCandidate?.id ?? null };
    try {
      await api.deleteEnterpriseDocument(item.id);
      setNotice(`“${item.title}”及其 ${item.image_count} 张服务器图片已永久删除。`);
      setDeleteConfirm(null);
      setReloadKey((value) => value + 1);
    } catch (reason) {
      deleteFocusRef.current.active = false;
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  const orderedImages = orderedIds
    .map((id) => detail?.images.find((image) => image.id === id))
    .filter((image): image is EnterpriseDocumentDetail["images"][number] => Boolean(image));
  const editorPreviewIndex = editorPreviewImage
    ? orderedImages.findIndex((image) => image.id === editorPreviewImage.id)
    : -1;
  const activeDragImage = activeDragImageId === null
    ? null
    : orderedImages.find((image) => image.id === activeDragImageId) ?? null;
  const activeDragIndex = activeDragImageId === null ? -1 : orderedIds.indexOf(activeDragImageId);
  const imageOrderChanged = Boolean(detail && (
    detail.images.length !== orderedIds.length
    || detail.images.some((item, index) => orderedIds[index] !== item.id)
  ));
  const imageChangesPending = Boolean(detail && (
    imageOrderChanged
    || coverId !== detail.cover_image_id
    || imageFiles.length > 0
    || pendingImageDeletes.length > 0
  ));
  const editorDirty = Boolean(detail && (
    editorDraft.title !== detail.title
    || editorDraft.category !== (detail.category ?? "")
    || editorDraft.description !== (detail.description ?? "")
  ));
  const detailShellClass = standalone ? "enterprise-standalone-detail" : "cases-admin-detail";

  if (selectedId !== null && !detail && !error) {
    return <div className={detailShellClass}><LoadingState label="正在打开企业资料" /></div>;
  }

  if (selectedId !== null && !detail && error) {
    return <div className={detailShellClass}><ErrorState message={error} retry={() => setReloadKey((value) => value + 1)} /></div>;
  }

  if (selectedId !== null && detail && !editing) {
    const returningId = selectedId;
    return (
      <div className={detailShellClass}>
        <EnterpriseDetailView
          detail={detail}
          backLabel={standalone ? "返回企业库" : "返回企业库管理"}
          notice={notice}
          onDismissNotice={() => setNotice("")}
          onBack={() => {
            if (standalone) {
              onStandaloneBack?.();
              return;
            }
            returnPositionRef.current.documentId = returningId;
            shouldRestorePositionRef.current = true;
            setSelectedId(null);
            setDetail(null);
            setNotice("");
            setReloadKey((value) => value + 1);
          }}
          initialActiveImage={detailActiveImage}
          initialScrollTop={detailScrollTopRef.current}
          onEdit={openEditor}
        />
      </div>
    );
  }

  if (selectedId !== null && detail && editing) {
    const materialChangesPending = pendingMaterialCreates.length > 0 || pendingMaterialRemoveIds.length > 0;
    const pendingChangeCount = Number(editorDirty) + Number(imageChangesPending) + Number(materialChangesPending);
    return (
      <div className={detailShellClass}>
        <div className="case-detail">
          <header className="detail-head">
            <button className="back-action" type="button" onClick={closeEditor}><ArrowLeft aria-hidden="true" />返回资料详情</button>
            <div><span>{detail.category || "企业资料"}</span><h1>{detail.title}</h1></div>
            <div className="detail-tools"><span className="detail-position">{detailActiveImage + 1} / {detail.images.length}</span><button className="detail-edit-action detail-edit-placeholder" type="button" disabled tabIndex={-1} aria-hidden="true"><Pencil aria-hidden="true" />编辑资料</button></div>
          </header>
          <div className="case-editor-workspace">
            <form className="case-editor" onSubmit={(event) => void saveDocument(event)}>
              <header className="case-editor-head">
                <div><h2>编辑企业资料</h2><p>基本信息、图片与附件共用同一份草稿；取消不会写入服务器，保存后统一生效。</p></div>
                <span className={pendingChangeCount ? "pending" : "saved"}>{pendingChangeCount ? `${pendingChangeCount} 类待保存` : "尚未修改"}</span>
              </header>
              {error && <div className="form-error" role="alert"><CircleAlert aria-hidden="true" />{error}</div>}
              <section className="case-editor-section" aria-labelledby="enterprise-basic-editor-title">
                <header>
                  <div><span className="case-editor-section-mark"><Pencil aria-hidden="true" /></span><div><h3 id="enterprise-basic-editor-title">基本信息</h3><p>维护资料名称、分类和说明，不改变已上传文件名。</p></div></div>
                  <strong>{editorDirty ? "待保存" : "未修改"}</strong>
                </header>
                <div className="case-editor-fields">
                  <label><span>资料名称</span><input ref={editorTitleRef} name="title" value={editorDraft.title} required maxLength={160} onChange={(event) => setEditorDraft((value) => ({ ...value, title: event.target.value }))} /></label>
                  <label><span>资料分类</span><input name="category" value={editorDraft.category} maxLength={80} placeholder="例如：品牌文化" onChange={(event) => setEditorDraft((value) => ({ ...value, category: event.target.value }))} /></label>
                  <label className="case-editor-remark"><span>资料说明</span><textarea name="description" value={editorDraft.description} maxLength={1000} placeholder="简要说明这份资料的用途，可留空" onChange={(event) => setEditorDraft((value) => ({ ...value, description: event.target.value }))} /></label>
                </div>
              </section>
              <section className="case-editor-section case-image-manager" aria-labelledby="enterprise-image-editor-title">
                <header>
                  <div><span className="case-editor-section-mark"><Images aria-hidden="true" /></span><div><h3 id="enterprise-image-editor-title">图片编辑</h3><p>追加图片、调整顺序、设置封面；删除只先标记，保存全部更改后执行。</p></div></div>
                  <strong>{orderedIds.length + imageFiles.length} 张{pendingImageDeletes.length ? ` · 待删除 ${pendingImageDeletes.length}` : ""}</strong>
                </header>
                <div id="enterprise-image-sort-help" className="case-image-sort-guide">
                  <GripVertical aria-hidden="true" />
                  <div><strong>图片卡片可直接拖动排序</strong><p>电脑拖动手柄；手机长按后移动，朱砂框表示放置位置。键盘按空格抓取、方向键移动，再按空格放下。</p></div>
                  {imageOrderChanged && <span className="pending">顺序待保存</span>}
                </div>
                <div className="case-image-upload">
                  <label><span>追加资料图片</span><input ref={imageFileInputRef} type="file" accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" multiple disabled={busy} onChange={(event) => { const files = naturalSortFiles(Array.from(event.currentTarget.files ?? [])); setImageFiles(files); setImageUploadProgress(null); setImageError(files.length ? enterpriseFileError(files) : ""); setImageNotice(files.length ? `已选择 ${files.length} 张，可在上传前预览和排序。` : ""); }} /><small>支持 PNG、JPG、JPEG、WebP；最多 {ARCHIVE_UPLOAD_MAX_FILES} 张，可在上传前检查顺序。</small></label>
                  <div><span>{imageFiles.length ? `${imageFiles.length} 张待新增 · ${formatBytes(imageFiles.reduce((sum, file) => sum + file.size, 0))}` : "尚未选择新图片"}</span>{imageFiles.length > 0 && <button className="secondary-action" type="button" disabled={busy} onClick={() => { setImageFiles([]); setImageError(""); setImageNotice("已撤销待新增图片。"); if (imageFileInputRef.current) imageFileInputRef.current.value = ""; }}><X aria-hidden="true" />撤销待新增</button>}</div>
                  <UploadImageOrganizer files={imageFiles} onChange={setImageFiles} disabled={busy} allowCover={false} />
                  {imageUploadProgress !== null && <UploadProgress value={imageUploadProgress} />}
                </div>
                {imageError && <div className="form-error" role="alert"><CircleAlert aria-hidden="true" /> {imageError}</div>}
                {imageNotice && <div className="form-notice order-pending" role="status"><Check aria-hidden="true" />{imageNotice}</div>}
                {pendingImageDeletes.length > 0 && <div className="case-pending-list" aria-label="待删除图片"><strong>待删除图片</strong>{pendingImageDeletes.map((item) => <div key={item.id}><span>{item.file_name}</span><button className="text-action" type="button" disabled={busy} onClick={() => undoImageDelete(item)}>撤销</button></div>)}</div>}
                <DndContext sensors={imageSortSensors} modifiers={restrictDragToGrid} collisionDetection={closestCenter} autoScroll={{ threshold: { x: 0.12, y: 0.12 }, acceleration: 10, interval: 5 }} onDragStart={beginImageSort} onDragEnd={finishImageSort} onDragCancel={cancelImageSort} accessibility={{ screenReaderInstructions: { draggable: "按空格抓取图片，使用方向键调整位置，再按空格放下；按 Esc 取消。" } }}>
                  <SortableContext items={orderedIds} strategy={rectSortingStrategy}>
                    <div className="case-image-grid is-direct-sorting" aria-label="企业资料图片、封面与排序列表">
                      {orderedImages.map((image, index) => <SortableEnterpriseImageCard key={image.id} item={image} index={index} coverImageId={coverId} savedCoverImageId={detail.cover_image_id} disabled={busy} canDelete={orderedIds.length + imageFiles.length > 1} deleteConfirming={imageDeleteConfirm?.id === image.id} onPreview={(trigger) => { editorPreviewButtonRef.current = trigger; setEditorPreviewImage(image); }} onSetCover={() => { setCoverId(image.id); setError(""); setImageNotice(`已选择第 ${index + 1} 张作为封面，保存全部更改后生效。`); }} onDelete={() => { setImageDeleteConfirm(image); setImageError(""); setImageNotice(""); }} onCancelDelete={closeImageDeleteConfirm} onConfirmDelete={() => stageImageDelete(image)} />)}
                    </div>
                  </SortableContext>
                  {createPortal(<DragOverlay modifiers={restrictDragToGrid} dropAnimation={{ duration: 180, easing: "cubic-bezier(.16, 1, .3, 1)" }}>{activeDragImage && activeDragIndex >= 0 ? <EnterpriseImageDragPreview item={activeDragImage} index={activeDragIndex} isCover={activeDragImage.id === coverId} /> : null}</DragOverlay>, document.body)}
                </DndContext>
                {orderedIds.length + imageFiles.length <= 1 && <p className="case-image-minimum"><ShieldCheck aria-hidden="true" />企业资料至少保留一张图片。</p>}
              </section>
              <section className="case-editor-section case-attachment-editor" aria-labelledby="enterprise-attachment-editor-title">
                <header><div><span className="case-editor-section-mark"><FileBox aria-hidden="true" /></span><div><h3 id="enterprise-attachment-editor-title">附件编辑</h3><p>上传文件或登记全景图、视频和网页链接；所有操作与上方内容一起保存或取消。</p></div></div><strong>{(editorMaterials?.length ?? 0) - pendingMaterialRemoveIds.length + pendingMaterialCreates.length} 项</strong></header>
                {materialError && <div className="form-error" role="alert"><CircleAlert aria-hidden="true" /> {materialError}</div>}
                {materialNotice && <div className="form-notice order-pending" role="status"><Check aria-hidden="true" /> {materialNotice}</div>}
                <div className="case-attachment-editor-body">
                  <div className="case-attachment-create" onKeyDown={(event) => { if (event.key === "Enter" && !(event.target instanceof HTMLTextAreaElement)) { event.preventDefault(); stageMaterialCreate(); } }}>
                    <div className="material-create-mode" role="group" aria-label="新增附件方式"><button type="button" disabled={busy} className={materialCreateMode === "upload" ? "active" : ""} aria-pressed={materialCreateMode === "upload"} onClick={() => setMaterialCreateMode("upload")}><Upload aria-hidden="true" />上传附件</button><button type="button" disabled={busy} className={materialCreateMode === "link" ? "active" : ""} aria-pressed={materialCreateMode === "link"} onClick={() => setMaterialCreateMode("link")}><Link aria-hidden="true" />登记链接</button></div>
                    <label><span>资料名称</span><input value={materialDraft.label} maxLength={160} disabled={busy} onChange={(event) => setMaterialDraft((value) => ({ ...value, label: event.target.value }))} placeholder={materialCreateMode === "upload" ? "例如：品牌介绍附件" : "例如：展厅全景展示"} /></label>
                    {materialCreateMode === "upload" ? <label className="material-file-field"><span>选择文件</span><input ref={materialFileInputRef} type="file" disabled={busy} onChange={(event) => setMaterialDraft((value) => ({ ...value, file: event.currentTarget.files?.[0] ?? null }))} /><small>文件写入应用资料区；单文件上限 1 GB。</small></label> : <><label><span>链接类型</span><select value={materialDraft.kind} disabled={busy} onChange={(event) => setMaterialDraft((value) => ({ ...value, kind: event.target.value as typeof value.kind }))}><option value="link">普通网页</option><option value="panorama">全景图</option><option value="douyin">抖音视频</option></select></label><label><span>打开链接</span><input type="url" value={materialDraft.url} disabled={busy} onChange={(event) => setMaterialDraft((value) => ({ ...value, url: event.target.value }))} placeholder="https://" /></label>{materialDraft.kind === "panorama" && <label><span>嵌入预览链接（可留空）</span><input type="url" value={materialDraft.previewUrl} disabled={busy} onChange={(event) => setMaterialDraft((value) => ({ ...value, previewUrl: event.target.value }))} placeholder="留空则尝试使用打开链接预览" /></label>}</>}
                    <button className="secondary-action" type="button" disabled={busy} onClick={stageMaterialCreate}>{materialCreateMode === "upload" ? <Upload aria-hidden="true" /> : <Link aria-hidden="true" />}加入待保存</button>
                  </div>
                  <div className="case-attachment-ledger" aria-live="polite">
                    {!editorMaterials && !materialError ? <LoadingState label="正在读取当前企业资料附件" /> : <>
                      {editorMaterials?.map((item) => { const pendingRemoval = pendingMaterialRemoveIds.includes(item.id); const shared = item.enterprise_documents.length > 1; return <article key={item.id} className={pendingRemoval ? "pending-remove" : ""}><MaterialKindIcon kind={item.kind} /><div><span>{pendingRemoval ? (shared ? "待解除关联" : "待永久删除") : materialKindLabel(item.kind)}{item.published ? "" : " · 员工端隐藏"}</span><strong>{item.label}</strong><small>{item.file_name || item.target_url}{shared ? ` · 共 ${item.enterprise_documents.length} 份企业资料使用` : ""}</small></div><div className="case-attachment-row-actions">{!pendingRemoval && <a className="secondary-action" href={item.open_url} target="_blank" rel="noreferrer">{item.file_name ? "下载" : "打开"}<ArrowUpRight aria-hidden="true" /></a>}<button className={pendingRemoval ? "secondary-action" : "case-material-remove-action"} type="button" disabled={busy} onClick={() => setPendingMaterialRemoveIds((ids) => pendingRemoval ? ids.filter((id) => id !== item.id) : [...ids, item.id])}>{pendingRemoval ? <RefreshCw aria-hidden="true" /> : shared ? <Link aria-hidden="true" /> : <Trash2 aria-hidden="true" />}{pendingRemoval ? "撤销" : shared ? "标记解除" : "标记删除"}</button></div></article>; })}
                      {pendingMaterialCreates.map((item) => <article key={item.tempId} className="pending-new">{item.mode === "upload" ? <FileBox aria-hidden="true" /> : <MaterialKindIcon kind={item.kind} />}<div><span>待新增 · {item.mode === "upload" ? "附件" : materialKindLabel(item.kind)}</span><strong>{item.label}</strong><small>{item.mode === "upload" ? `${item.file.name} · ${formatBytes(item.file.size)}` : item.url}</small></div><div className="case-attachment-row-actions"><button className="secondary-action" type="button" disabled={busy} onClick={() => setPendingMaterialCreates((items) => items.filter((candidate) => candidate.tempId !== item.tempId))}><X aria-hidden="true" />撤销</button></div></article>)}
                      {!editorMaterials?.length && !pendingMaterialCreates.length && <div className="case-attachment-empty"><FileBox aria-hidden="true" /><strong>当前企业资料还没有附件</strong><p>在左侧选择文件或填写链接，再加入待保存。</p></div>}
                    </>}
                  </div>
                </div>
              </section>
              <footer className="case-editor-actions">
                <p aria-live="polite">{busy ? "正在保存企业资料" : pendingChangeCount ? `共有 ${pendingChangeCount} 类更改等待统一保存` : "修改基本信息、图片或附件后，可在这里一次保存"}</p>
                <div><ArchiveDeleteAction label={detail.title} imageCount={detail.image_count} busy={busy} onConfirm={deleteCurrentDocument} /><button className="secondary-action" type="button" onClick={closeEditor} disabled={busy}>取消编辑</button><button className="primary-action" type="submit" disabled={busy || !pendingChangeCount}>{busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <Save aria-hidden="true" />}{busy ? "正在保存" : "保存全部更改"}</button></div>
              </footer>
            </form>
          </div>
          {editorPreviewImage && <ImageWorkbenchLightbox src={editorPreviewImage.preview_url} originalSrc={editorPreviewImage.original_url} fileName={editorPreviewImage.file_name} caption={`${editorPreviewImage.file_name} · ${editorPreviewIndex + 1}/${orderedImages.length} · 封面选择预览`} dialogLabel="封面大图预览" previousDisabled={editorPreviewIndex <= 0} nextDisabled={editorPreviewIndex < 0 || editorPreviewIndex >= orderedImages.length - 1} onPrevious={() => setEditorPreviewImage(orderedImages[Math.max(0, editorPreviewIndex - 1)] ?? null)} onNext={() => setEditorPreviewImage(orderedImages[Math.min(orderedImages.length - 1, editorPreviewIndex + 1)] ?? null)} onClose={() => setEditorPreviewImage(null)} returnFocusRef={editorPreviewButtonRef} />}
        </div>
      </div>
    );
  }

  const uploadFileError = uploadFiles.length ? enterpriseFileError(uploadFiles) : "";
  const pageCount = documents ? Math.max(1, Math.ceil(documents.total / documents.page_size)) : 1;
  return (
    <section ref={enterpriseAdminRef} className="cases-admin">
      <div className="case-admin-toolbar">
        <SearchField className="case-admin-search" label="搜索企业资料" value={query} onChange={setQuery} placeholder="资料名称、分类或说明" clearLabel="清空企业资料搜索" />
        <button ref={uploadTriggerRef} className={`case-upload-toggle ${showUpload ? "secondary-action" : "primary-action"}`} type="button" aria-expanded={showUpload} aria-controls="enterprise-upload-panel" onClick={() => { if (showUpload) { setShowUpload(false); setUploadFiles([]); setUploadProgress(null); setError(""); } else { setShowUpload(true); setError(""); setNotice(""); } }}>{showUpload ? <X aria-hidden="true" /> : <Upload aria-hidden="true" />}{showUpload ? "收起上传" : "上传新资料"}</button>
        <ArchiveOrderManager archiveKind="enterprise" categoryLabel="企业库大分类顺序" itemLabel="企业资料顺序" onSaved={() => setReloadKey((value) => value + 1)} />
      </div>
      {showUpload && (
        <form id="enterprise-upload-panel" className="case-upload-panel" onSubmit={(event) => void uploadDocument(event)}>
          <header><div><span className="case-upload-mark"><BookOpen aria-hidden="true" /></span><div><h3>建立一份企业资料</h3><p>填写基本信息并按阅读顺序一次选择全部图片。</p></div></div><button className="text-action" type="button" disabled={busy} onClick={() => { setShowUpload(false); setUploadFiles([]); setUploadProgress(null); setError(""); window.requestAnimationFrame(() => uploadTriggerRef.current?.focus()); }}><X aria-hidden="true" />取消</button></header>
          <div className="case-upload-grid">
            <label><span>资料名称</span><input ref={uploadTitleRef} name="title" required maxLength={160} placeholder="例如：易高企业介绍" /></label>
            <label><span>资料分类</span><input name="category" maxLength={80} placeholder="例如：品牌文化" /></label>
            <label className="span-2"><span>资料说明</span><textarea name="description" maxLength={1000} placeholder="简要说明这份资料的用途，可留空" /></label>
            <label className="span-2 case-upload-file"><span>资料图片</span><input type="file" multiple required disabled={busy} accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" onChange={(event) => { const files = naturalSortFiles(Array.from(event.currentTarget.files ?? [])); setUploadFiles(files); setUploadProgress(null); setError(files.length ? enterpriseFileError(files) : ""); }} /><small>支持 PNG、JPG、JPEG、WebP；最多 {ARCHIVE_UPLOAD_MAX_FILES} 张，可在上传前检查顺序和封面。</small></label>
          </div>
          <div className="case-upload-summary" aria-live="polite"><div><span>{uploadFileError ? "所选图片无法上传" : uploadFiles.length ? "图片已就绪" : "等待选择图片"}</span><b>{uploadFileError || (uploadFiles.length ? `${uploadFiles.length} 张 · ${formatBytes(uploadFiles.reduce((sum, file) => sum + file.size, 0))}` : "请选择企业资料图片")}</b></div>{uploadFiles.length > 0 && <p>{uploadFiles.slice(0, 3).map((file) => file.name).join("、")}{uploadFiles.length > 3 ? ` 等 ${uploadFiles.length} 张` : ""}</p>}</div>
          <UploadImageOrganizer files={uploadFiles} onChange={setUploadFiles} disabled={busy} />
          {uploadProgress !== null && <UploadProgress value={uploadProgress} />}
          <div className="case-upload-boundary"><ShieldCheck aria-hidden="true" /><p><b>统一程序托管</b><span>新资料与现有资料使用同一资料区，可在企业库管理中追加图片或永久删除。</span></p></div>
          {error && <div className="form-error" role="alert"><CircleAlert aria-hidden="true" />{error}</div>}
          <div className="case-upload-actions"><button className="text-action" type="button" disabled={busy} onClick={() => { setShowUpload(false); setUploadFiles([]); setUploadProgress(null); setError(""); window.requestAnimationFrame(() => uploadTriggerRef.current?.focus()); }}>取消</button><button className="primary-action" type="submit" disabled={busy || !uploadFiles.length || Boolean(uploadFileError)}>{uploadProgress !== null ? <LoaderCircle className="spin" aria-hidden="true" /> : <Upload aria-hidden="true" />}{uploadProgress === null ? "上传并建立资料" : uploadProgress >= 100 ? "服务器正在生成预览" : `正在上传 ${uploadProgress}%`}</button></div>
        </form>
      )}
      {error && !showUpload && <div className="account-feedback form-error workspace-feedback" role="alert"><CircleAlert aria-hidden="true" />{error}</div>}
      {notice && <ActionFeedback className="account-feedback workspace-feedback" message={notice} onDismiss={() => setNotice("")} />}
      {!documents && !error ? <LoadingState label="正在读取企业资料台账" /> : documents?.items.length ? (
        <div className="case-admin-list">
          {documents.items.map((item) => <article className="case-admin-row" key={item.id}>
            <ImageSurface src={item.cover_url} alt="" />
            <div className="case-admin-copy"><h3>{item.title}</h3><p>{item.category || "未分类"} · {item.image_count} 张</p></div>
            <div className="case-admin-actions"><button id={`enterprise-admin-open-${item.id}`} className="secondary-action case-open-action" type="button" onClick={() => { const scroller = enterpriseAdminRef.current?.closest<HTMLElement>(".admin-view")?.querySelector<HTMLElement>(":scope > section[role='tabpanel']"); returnPositionRef.current = { scrollTop: scroller?.scrollTop ?? 0, documentId: item.id }; detailScrollTopRef.current = 0; setDetailActiveImage(0); setSelectedId(item.id); setNotice(""); setError(""); }}>查看详情 <ArrowUpRight aria-hidden="true" /></button><button id={`enterprise-delete-trigger-${item.id}`} className="danger-action danger-trigger case-delete-trigger" type="button" disabled={busy} aria-expanded={deleteConfirm?.id === item.id} aria-controls={`enterprise-delete-panel-${item.id}`} onClick={() => { setDeleteConfirm(item); setError(""); setNotice(""); }}><Trash2 aria-hidden="true" />永久删除</button></div>
            {deleteConfirm?.id === item.id && <div className="case-delete-confirm" id={`enterprise-delete-panel-${item.id}`} role="alert"><span className="case-delete-mark"><Trash2 aria-hidden="true" /></span><div><strong>确认永久删除“{item.title}”？</strong><p>将永久删除资料记录及其 {item.image_count} 张原图、缩略图和预览缓存，操作无法恢复。</p></div><div className="case-delete-confirm-actions"><button className="text-action" type="button" disabled={busy} onClick={() => { setDeleteConfirm(null); window.requestAnimationFrame(() => document.getElementById(`enterprise-delete-trigger-${item.id}`)?.focus()); }}>取消</button><button id={`enterprise-delete-confirm-${item.id}`} className="danger-action" type="button" disabled={busy} onClick={() => void deleteDocument(item)}>{busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <Trash2 aria-hidden="true" />}{busy ? "正在永久删除" : "确认永久删除"}</button></div></div>}
          </article>)}
        </div>
      ) : <div className="case-admin-empty"><FolderSearch aria-hidden="true" /><strong>没有匹配的企业资料</strong><p>请调整搜索词后再试。</p></div>}
      {documents && pageCount > 1 && <nav className="case-admin-pagination" aria-label="企业库管理分页"><button className="text-action" type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft aria-hidden="true" />上一页</button><span>第 {page} / {pageCount} 页 · {documents.total} 份</span><button className="text-action" type="button" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>下一页<ChevronRight aria-hidden="true" /></button></nav>}
    </section>
  );
}

function accountTypeLabel(account: Pick<AdminUser, "account_type">): string {
  if (account.account_type === "admin") return "管理员";
  if (account.account_type === "restricted") return "受限员工";
  return "普通员工";
}

function caseAccessTitle(item: CaseAccessItem): string {
  const research = item.source_folder.includes("研发") || item.room?.includes("研发");
  return [item.community_name, research ? null : item.room, item.area]
    .filter(Boolean)
    .join("_") || item.source_folder;
}

function CaseAccessEditor({
  account,
  onClose,
  onSaved,
}: {
  account: AdminUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [items, setItems] = useState<CaseAccessItem[] | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [initial, setInitial] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useTransientNotice();
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let active = true;
    api.userCaseAccess(account.id)
      .then((payload) => {
        if (!active) return;
        const granted = new Set(payload.items.filter((item) => item.authorized).map((item) => item.id));
        setItems(payload.items);
        setSelected(granted);
        setInitial(new Set(granted));
        window.requestAnimationFrame(() => searchRef.current?.focus());
      })
      .catch((reason) => active && setError(messageFrom(reason)));
    return () => { active = false; };
  }, [account.id]);

  useEffect(() => {
    function keydown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    }
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [onClose]);

  const filtered = useMemo(() => {
    const term = query.trim().toLocaleLowerCase("zh-CN");
    if (!items || !term) return items ?? [];
    return items.filter((item) =>
      [item.community_name, item.room, item.style, item.source_folder]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase("zh-CN").includes(term)),
    );
  }, [items, query]);
  const groups = useMemo(() => {
    const grouped = new Map<number, { name: string; items: CaseAccessItem[] }>();
    filtered.forEach((item) => {
      const group = grouped.get(item.community_id) ?? { name: item.community_name, items: [] };
      group.items.push(item);
      grouped.set(item.community_id, group);
    });
    return [...grouped.entries()].sort((left, right) => COMMUNITY_NAME_COLLATOR.compare(left[1].name, right[1].name));
  }, [filtered]);
  const dirty = selected.size !== initial.size || [...selected].some((id) => !initial.has(id));

  function toggleCase(id: number) {
    setNotice("");
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleGroup(groupItems: CaseAccessItem[]) {
    setNotice("");
    setSelected((current) => {
      const next = new Set(current);
      const fullySelected = groupItems.every((item) => next.has(item.id));
      groupItems.forEach((item) => fullySelected ? next.delete(item.id) : next.add(item.id));
      return next;
    });
  }

  function selectAllCases() {
    if (!items) return;
    setError("");
    setNotice("");
    setSelected(new Set(items.map((item) => item.id)));
  }

  function clearAllCases() {
    setError("");
    setNotice("");
    setSelected(new Set());
  }

  async function save() {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api.updateUserCaseAccess(account.id, [...selected].sort((a, b) => a - b));
      setInitial(new Set(selected));
      setNotice("授权范围已保存，员工下一次刷新即可按新范围查看。");
      await onSaved();
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="case-access-editor" aria-labelledby={`case-access-title-${account.id}`}>
      <header>
        <div>
          <h3 id={`case-access-title-${account.id}`}>配置 {account.display_name} 的案例范围</h3>
          <p>可逐户、按小区或批量调整；所有更改点击“保存授权”后生效。</p>
        </div>
        <div className="case-access-count"><b>{selected.size}</b><span>已授权案例</span></div>
        <button className="text-action" type="button" onClick={onClose}><X aria-hidden="true" />关闭</button>
      </header>
      <div className="case-access-toolbar">
        <SearchField className="case-access-search" label="搜索授权案例" value={query} onChange={setQuery} placeholder="搜索小区、房号或风格" clearLabel="清空授权案例搜索" inputRef={searchRef} />
        <div className="case-access-bulk-actions" role="group" aria-label="批量调整案例授权">
          <button className="secondary-action" type="button" onClick={selectAllCases} disabled={busy || items === null || items.length === 0 || selected.size === items.length}><Check aria-hidden="true" />授权所有</button>
          <button className="danger-action danger-trigger" type="button" onClick={clearAllCases} disabled={busy || items === null || selected.size === 0}><X aria-hidden="true" />取消所有</button>
        </div>
        <button className="primary-action" type="button" onClick={() => void save()} disabled={busy || !dirty}>{busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <Save aria-hidden="true" />}{busy ? "保存中" : "保存授权"}</button>
      </div>
      {error && <div className="form-error" role="alert">{error}</div>}
      {notice && <ActionFeedback message={notice} onDismiss={() => setNotice("")} />}
      {items === null && !error && <LoadingState label="正在整理案例授权" />}
      {items && selected.size === 0 && !query && <div className="case-access-empty"><LockKeyhole aria-hidden="true" /><strong>尚未授权任何案例</strong><p>可逐户、按小区整组或授权所有，点击保存后生效。</p></div>}
      {items && groups.length === 0 && <div className="case-access-empty"><FolderSearch aria-hidden="true" /><strong>没有匹配的案例</strong><p>请调整小区、房号或风格关键词。</p></div>}
      <div className="case-access-groups">
        {groups.map(([communityId, group]) => {
          const selectedCount = group.items.filter((item) => selected.has(item.id)).length;
          return (
            <section key={communityId}>
              <header>
                <div><h4>{group.name}</h4><span>{selectedCount} / {group.items.length} 户</span></div>
                <button className="secondary-action" type="button" onClick={() => toggleGroup(group.items)}>{selectedCount === group.items.length ? "取消整组" : "整组授权"}</button>
              </header>
              <div>
                {group.items.map((item) => (
                  <label key={item.id}>
                    <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggleCase(item.id)} />
                    <span><b>{caseAccessTitle(item)}</b><small>{item.room || "研发案例"}{item.style ? ` · ${item.style}` : ""}</small></span>
                  </label>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </section>
  );
}

function UsersAdmin({ currentUserId }: { currentUserId: number }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useTransientNotice();
  const [busy, setBusy] = useState(false);
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [changingId, setChangingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [authorizingId, setAuthorizingId] = useState<number | null>(null);
  const createUserButtonRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLButtonElement | null>(null);

  function returnToAction() {
    const target = returnFocusRef.current;
    window.requestAnimationFrame(() => target?.focus({ preventScroll: true }));
  }

  function closeAccountAction() {
    setEditingId(null);
    setDeletingId(null);
    setAuthorizingId(null);
    setError("");
    returnToAction();
  }

  function closeCreateForm() {
    setShowForm(false);
    setError("");
    window.requestAnimationFrame(() => createUserButtonRef.current?.focus({ preventScroll: true }));
  }

  useEffect(() => {
    if (editingId === null && deletingId === null && authorizingId === null) return;
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeAccountAction();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [editingId, deletingId, authorizingId]);

  async function reload() {
    try {
      setUsers(await api.adminUsers());
    } catch (reason) {
      setError(messageFrom(reason));
    }
  }
  useEffect(() => void reload(), []);

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    setNotice("");
    const accountType = String(data.get("account_type"));
    try {
      await api.createUser({
        username: String(data.get("username")),
        display_name: String(data.get("display_name")),
        password: String(data.get("password")),
        role: accountType === "admin" ? "admin" : "employee",
        access_level: accountType === "restricted" ? "restricted" : "full",
      });
      setShowForm(false);
      setNotice("账号已创建，可以立即登录。");
      await reload();
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function toggle(user: AdminUser) {
    setChangingId(user.id);
    setError("");
    setNotice("");
    try {
      await api.setUserDisabled(user.id, !user.disabled);
      setConfirmingId(null);
      setNotice(user.disabled ? `${user.display_name} 已恢复登录。` : `${user.display_name} 已停用并退出登录。`);
      await reload();
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setChangingId(null);
    }
  }

  async function saveUser(event: FormEvent<HTMLFormElement>, account: AdminUser) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const password = String(data.get("password") ?? "").trim();
    setChangingId(account.id);
    setError("");
    setNotice("");
    const accountType = account.id === currentUserId ? "admin" : String(data.get("account_type"));
    try {
      await api.updateUser(account.id, {
        username: String(data.get("username")),
        display_name: String(data.get("display_name")),
        role: accountType === "admin" ? "admin" : "employee",
        access_level: accountType === "restricted" ? "restricted" : "full",
        ...(password ? { password } : {}),
      });
      setEditingId(null);
      setNotice(`${String(data.get("display_name"))} 的账号资料已保存。${password ? "原登录会话已撤销。" : ""}`);
      await reload();
      returnToAction();
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setChangingId(null);
    }
  }

  async function removeUser(account: AdminUser) {
    setChangingId(account.id);
    setError("");
    setNotice("");
    try {
      await api.deleteUser(account.id);
      setDeletingId(null);
      setNotice(`${account.display_name} 已删除，账号已退出且不能再登录。`);
      await reload();
      window.requestAnimationFrame(() => createUserButtonRef.current?.focus({ preventScroll: true }));
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setChangingId(null);
    }
  }

  if (!users) return error ? <ErrorState message={error} retry={reload} /> : <LoadingState />;
  return (
    <section className="users-admin">
      <div className="section-actions">
        <div><h2>员工账号</h2><p>管理员可编辑、停用或删除账号；删除后保留历史审计记录。</p></div>
        <button
          ref={createUserButtonRef}
          className="primary-action"
          type="button"
          aria-expanded={showForm}
          aria-controls="create-user-form"
          onClick={() => {
            if (showForm) closeCreateForm();
            else {
              setEditingId(null);
              setDeletingId(null);
              setAuthorizingId(null);
              setError("");
              setNotice("");
              setShowForm(true);
            }
          }}
        >
          {showForm ? <X aria-hidden="true" /> : <UserPlus aria-hidden="true" />}
          {showForm ? "收起表单" : "新建账号"}
        </button>
      </div>
      {showForm && (
        <form id="create-user-form" className="inline-user-form" onSubmit={create} autoComplete="off">
          <label><span>登录账号</span><input name="username" autoComplete="username" autoFocus required /></label>
          <label><span>员工姓名</span><input name="display_name" autoComplete="name" required /></label>
          <label><span>初始密码（至少 6 位）</span><input name="password" type="password" autoComplete="new-password" minLength={6} required /></label>
          <label><span>权限</span><select name="account_type"><option value="employee">普通员工</option><option value="restricted">受限员工</option><option value="admin">管理员</option></select></label>
          {error && <div className="form-error" role="alert"><CircleAlert aria-hidden="true" />{error}</div>}
          <div className="inline-user-form-actions">
            <button className="text-action" type="button" onClick={closeCreateForm} disabled={busy}>取消</button>
            <button className="secondary-action" type="submit" disabled={busy}>确认创建</button>
          </div>
        </form>
      )}
      {error && !showForm && <div className="account-feedback form-error" role="alert">{error}</div>}
      {notice && <ActionFeedback className="account-feedback workspace-feedback" message={notice} onDismiss={() => setNotice("")} />}
      <div className="user-table">
        <div className="table-row table-head"><span>员工</span><span>账号</span><span>权限</span><span>状态</span><span /></div>
        {users.map((user) => (
          <Fragment key={user.id}>
            <div className="table-row">
              <span data-label="员工"><b>{user.display_name}</b>{user.id === currentUserId && <small className="current-user-mark">当前账号</small>}</span>
              <span data-label="账号">{user.username}</span>
              <span data-label="权限"><b className={`account-type account-type-${user.account_type}`}>{accountTypeLabel(user)}</b>{user.account_type === "restricted" && <small>已授权 {user.granted_case_count} 个</small>}</span>
              <span data-label="状态" className={user.disabled ? "status-disabled" : "status-enabled"}>{user.disabled ? "已停用" : "可登录"}</span>
              <div className={`user-row-action${confirmingId === user.id ? " user-row-action-confirming" : ""}`} data-label="操作">
                {confirmingId === user.id ? (
                  <>
                    <span className="user-stop-prompt">确认停用？</span>
                    <button className="danger-action" type="button" disabled={changingId !== null} onClick={() => void toggle(user)}>
                      {changingId === user.id ? "停用中" : "确认停用"}
                    </button>
                    <button className="text-action" type="button" disabled={changingId !== null} onClick={() => setConfirmingId(null)}>取消</button>
                  </>
                ) : (
                  <>
                    <button
                      className="text-action account-icon-action"
                      type="button"
                      aria-expanded={editingId === user.id}
                      aria-controls={`edit-user-${user.id}`}
                      disabled={changingId !== null}
                      onClick={(event) => {
                        returnFocusRef.current = event.currentTarget;
                        setShowForm(false);
                        setDeletingId(null);
                        setAuthorizingId(null);
                        setConfirmingId(null);
                        setNotice("");
                        setError("");
                        setEditingId(user.id);
                      }}
                    >
                      <Pencil aria-hidden="true" />编辑
                    </button>
                    {user.account_type === "restricted" && (
                      <button
                        className="text-action account-icon-action"
                        type="button"
                        aria-expanded={authorizingId === user.id}
                        aria-controls={`case-access-${user.id}`}
                        disabled={changingId !== null}
                        onClick={(event) => {
                          returnFocusRef.current = event.currentTarget;
                          setShowForm(false);
                          setEditingId(null);
                          setDeletingId(null);
                          setConfirmingId(null);
                          setError("");
                          setNotice("");
                          setAuthorizingId(user.id);
                        }}
                      >
                        <LockKeyhole aria-hidden="true" />案例授权
                      </button>
                    )}
                    {user.disabled ? (
                      <button className="text-action" type="button" disabled={changingId !== null} onClick={() => void toggle(user)}>
                        {changingId === user.id ? "恢复中" : "恢复"}
                      </button>
                    ) : user.id !== currentUserId ? (
                      <button className="text-action" type="button" disabled={changingId !== null} onClick={() => setConfirmingId(user.id)}>停用</button>
                    ) : null}
                    {user.id !== currentUserId && (
                      <button
                        className="danger-action danger-trigger account-icon-action"
                        type="button"
                        aria-expanded={deletingId === user.id}
                        aria-controls={`delete-user-${user.id}`}
                        disabled={changingId !== null}
                        onClick={(event) => {
                          returnFocusRef.current = event.currentTarget;
                          setShowForm(false);
                          setEditingId(null);
                          setAuthorizingId(null);
                          setConfirmingId(null);
                          setNotice("");
                          setError("");
                          setDeletingId(user.id);
                        }}
                      >
                        <Trash2 aria-hidden="true" />删除
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            {editingId === user.id && (
              <form id={`edit-user-${user.id}`} className="inline-user-form user-edit-form" onSubmit={(event) => void saveUser(event, user)} autoComplete="off">
                <div className="user-form-heading"><strong>编辑 {user.display_name}</strong><span>Esc 可取消并返回操作按钮</span></div>
                <label><span>登录账号</span><input name="username" defaultValue={user.username} autoComplete="username" autoFocus required /></label>
                <label><span>员工姓名</span><input name="display_name" defaultValue={user.display_name} autoComplete="name" required /></label>
                <label><span>权限</span><select name="account_type" defaultValue={user.account_type} disabled={user.id === currentUserId}><option value="employee">普通员工</option><option value="restricted">受限员工</option><option value="admin">管理员</option></select></label>
                {user.id !== currentUserId ? (
                  <label><span>新密码（留空则不修改）</span><input name="password" type="password" autoComplete="new-password" minLength={6} /></label>
                ) : (
                  <p className="self-account-note">当前登录账号不可移除管理员权限；本页不会重置自身密码。</p>
                )}
                <div className="inline-user-form-actions">
                  <button className="text-action" type="button" onClick={closeAccountAction} disabled={changingId === user.id}>取消</button>
                  <button className="secondary-action" type="submit" disabled={changingId === user.id}>
                    <Save aria-hidden="true" />{changingId === user.id ? "保存中" : "保存修改"}
                  </button>
                </div>
              </form>
            )}
            {authorizingId === user.id && (
              <div id={`case-access-${user.id}`} className="case-access-row">
                <CaseAccessEditor
                  account={user}
                  onClose={closeAccountAction}
                  onSaved={reload}
                />
              </div>
            )}
            {deletingId === user.id && (
              <div id={`delete-user-${user.id}`} className="user-delete-confirm" role="alert">
                <div><strong>确定删除 {user.display_name}？</strong><p>该账号会立即退出并无法恢复，历史记录与审计记录仍会保留。</p></div>
                <div className="user-delete-actions">
                  <button className="text-action" type="button" onClick={closeAccountAction} disabled={changingId === user.id}>取消</button>
                  <button className="danger-action" type="button" onClick={() => void removeUser(user)} disabled={changingId === user.id}>
                    <Trash2 aria-hidden="true" />{changingId === user.id ? "删除中" : "确认删除账号"}
                  </button>
                </div>
              </div>
            )}
          </Fragment>
        ))}
      </div>
    </section>
  );
}

function CaseViewsAdmin() {
  const [logs, setLogs] = useState<CaseViewLogPage | null>(null);
  const [draft, setDraft] = useState({ employee: "", case: "", dateFrom: "", dateTo: "" });
  const [filters, setFilters] = useState(draft);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const resultsRef = useRef<HTMLDivElement>(null);
  const filterToggleRef = useRef<HTMLButtonElement>(null);
  const filterPanelId = useId();

  useEffect(() => {
    let active = true;
    setError("");
    api.caseViews({ ...filters, page })
      .then((payload) => active && setLogs(payload))
      .catch((reason) => active && setError(messageFrom(reason)));
    return () => { active = false; };
  }, [filters, page, reloadKey]);

  function applyFilters(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPage(1);
    setFilters({
      employee: draft.employee.trim(),
      case: draft.case.trim(),
      dateFrom: draft.dateFrom,
      dateTo: draft.dateTo,
    });
    setFiltersOpen(false);
    window.requestAnimationFrame(() => filterToggleRef.current?.focus());
  }

  function resetFilters() {
    const empty = { employee: "", case: "", dateFrom: "", dateTo: "" };
    setDraft(empty);
    setFilters(empty);
    setPage(1);
    setFiltersOpen(false);
    window.requestAnimationFrame(() => filterToggleRef.current?.focus());
  }

  function closeFiltersOnEscape(event: ReactKeyboardEvent<HTMLFormElement>) {
    if (event.key !== "Escape") return;
    event.preventDefault();
    setFiltersOpen(false);
    window.requestAnimationFrame(() => filterToggleRef.current?.focus());
  }

  function changePage(nextPage: number) {
    setPage(nextPage);
    window.requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({ block: "start", behavior: "auto" });
    });
  }

  const pageCount = logs ? Math.max(1, Math.ceil(logs.total / logs.page_size)) : 1;
  const hasFilters = Boolean(filters.employee || filters.case || filters.dateFrom || filters.dateTo);
  return (
    <section className="case-views-admin">
      <header className="section-actions">
        <div><h2>案例查看记录</h2><p>记录账号成功进入案例详情的时间与当时账号、权限和案例快照。</p></div>
        <div className="case-view-header-actions">
          <span className="case-view-total">共 <b>{logs?.total ?? 0}</b> {hasFilters ? "条结果" : "条"}</span>
          <button
            ref={filterToggleRef}
            className={`case-view-filter-toggle${hasFilters ? " active" : ""}`}
            type="button"
            aria-expanded={filtersOpen}
            aria-controls={filterPanelId}
            onClick={() => setFiltersOpen((value) => !value)}
          >
            <Settings2 aria-hidden="true" />
            {hasFilters ? "已筛选" : "筛选"}
          </button>
        </div>
      </header>
      {filtersOpen && (
        <form id={filterPanelId} className="case-view-filters" onSubmit={applyFilters} onKeyDown={closeFiltersOnEscape}>
          <label><span>账号</span><input value={draft.employee} onChange={(event) => setDraft((value) => ({ ...value, employee: event.target.value }))} placeholder="姓名或账号" /></label>
          <label><span>案例</span><input value={draft.case} onChange={(event) => setDraft((value) => ({ ...value, case: event.target.value }))} placeholder="小区、房号或案例名" /></label>
          <label><span>开始日期</span><input type="date" value={draft.dateFrom} onChange={(event) => setDraft((value) => ({ ...value, dateFrom: event.target.value }))} /></label>
          <label><span>结束日期</span><input type="date" min={draft.dateFrom || undefined} value={draft.dateTo} onChange={(event) => setDraft((value) => ({ ...value, dateTo: event.target.value }))} /></label>
          <div>
            <button className="text-action" type="button" onClick={resetFilters}>重置</button>
            <button className="primary-action" type="submit"><Search aria-hidden="true" />筛选记录</button>
          </div>
        </form>
      )}
      <div ref={resultsRef} className="case-view-results-anchor" aria-hidden="true" />
      {error && <ErrorState message={error} retry={() => setReloadKey((value) => value + 1)} />}
      {!logs && !error && <LoadingState label="正在读取查看记录" />}
      {logs && logs.items.length === 0 && <div className="case-view-empty"><Eye aria-hidden="true" /><strong>当前条件下没有查看记录</strong><p>账号成功进入案例详情后，记录会显示在这里。</p></div>}
      {logs && logs.items.length > 0 && (
        <div className="case-view-ledger">
          <div className="case-view-row case-view-head"><span>查看时间</span><span>员工</span><span>权限</span><span>案例</span></div>
          {logs.items.map((item) => (
            <article className="case-view-row" key={item.id}>
              <time dateTime={item.viewed_at}>{formatViewTime(item.viewed_at)}</time>
              <span><b>{item.display_name_snapshot}</b><small>{item.username_snapshot}</small></span>
              <span><b className={`account-type account-type-${item.account_type_snapshot}`}>{item.account_type_snapshot === "admin" ? "管理员" : item.account_type_snapshot === "restricted" ? "受限员工" : "普通员工"}</b></span>
              <span>
                <b>{item.case_title_snapshot}</b>
                <small>{item.community_name_snapshot}{item.room_snapshot ? ` · ${item.room_snapshot}` : " · 研发案例"}</small>
                {item.case_deleted && <em className="case-view-deleted">历史案例 · 已删除</em>}
              </span>
            </article>
          ))}
        </div>
      )}
      {logs && pageCount > 1 && (
        <nav className="case-admin-pagination" aria-label="查看记录分页">
          <button type="button" disabled={page <= 1} onClick={() => changePage(page - 1)}><ChevronLeft aria-hidden="true" />上一页</button>
          <span>第 {page} / {pageCount} 页</span>
          <button type="button" disabled={page >= pageCount} onClick={() => changePage(page + 1)}>下一页<ChevronRight aria-hidden="true" /></button>
        </nav>
      )}
    </section>
  );
}

function SystemAdmin() {
  const [system, setSystem] = useState<SystemStatus | null>(null);
  const [error, setError] = useState("");

  async function reload() {
    try {
      setSystem(await api.system());
    } catch (reason) {
      setError(messageFrom(reason));
    }
  }
  useEffect(() => void reload(), []);

  if (!system) return error ? <ErrorState message={error} retry={reload} /> : <LoadingState />;
  const ready = system.counts.images === system.counts.derivatives_ready;
  return (
    <div className="system-admin">
      <section className="system-ledger">
        <div><span>案例</span><b>{system.counts.cases}</b><small>{system.catalog_unified ? "全部统一托管" : `${system.counts.managed_cases} 个已托管`}</small></div>
        <div><span>图片</span><b>{system.counts.images}</b><small>{ready ? `预览就绪 · 水印缓存 ${system.counts.watermarked_ready}` : `${system.counts.derivatives_ready} 已就绪`}</small></div>
        <div><span>地图</span><b>{system.counts.verified_locations}</b><small>已核准点位</small></div>
        <div><span>磁盘余量</span><b>{formatBytes(system.disk.free)}</b><small>{system.low_disk ? "低于安全线" : "空间正常"}</small></div>
      </section>
    </div>
  );
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [booting, setBooting] = useState(true);
  const [stats, setStats] = useState<CatalogStats | null | undefined>(undefined);
  const [statsReloadKey, setStatsReloadKey] = useState(0);
  const [view, setView] = useState<View>("archive");
  const [archiveCommunity, setArchiveCommunity] = useState<number | undefined>();
  const [archiveScope, setArchiveScope] = useState<"all" | "landed">("all");
  const [adminInitialTab, setAdminInitialTab] = useState<AdminTab>("cases");
  const [viewResetKey, setViewResetKey] = useState(0);
  const [mobileNav, setMobileNav] = useState(false);
  const [locationDirty, setLocationDirty] = useState(false);
  const mobileMenuRef = useRef<HTMLButtonElement>(null);
  const mobileCloseRef = useRef<HTMLButtonElement>(null);
  const mobileRailRef = useRef<HTMLElement>(null);
  const appMainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    api
      .authStatus()
      .then((payload) => setUser(payload.user))
      .catch((error) => {
        console.error(error);
      })
      .finally(() => setBooting(false));
  }, []);

  useEffect(() => {
    if (!user) {
      setStats(undefined);
      return;
    }
    let active = true;
    api.stats()
      .then((payload) => active && setStats(payload))
      .catch(() => active && setStats(null));
    return () => {
      active = false;
    };
  }, [statsReloadKey, user]);

  function refreshStats() {
    setStatsReloadKey((value) => value + 1);
  }

  useEffect(() => {
    if (!mobileNav) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    mobileCloseRef.current?.focus();
    function keydown(event: KeyboardEvent) {
      if (event.key === "Escape") setMobileNav(false);
      if (event.key === "Tab" && mobileRailRef.current) {
        const controls = Array.from(
          mobileRailRef.current.querySelectorAll<HTMLElement>("button:not([disabled]), a[href]"),
        );
        const first = controls[0];
        const last = controls.at(-1);
        if (!first || !last) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
    window.addEventListener("keydown", keydown);
    return () => {
      window.removeEventListener("keydown", keydown);
      document.body.style.overflow = previousOverflow;
      mobileMenuRef.current?.focus();
    };
  }, [mobileNav]);

  useEffect(() => {
    if (!locationDirty) return;
    function beforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      event.returnValue = "";
    }
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [locationDirty]);

  useLayoutEffect(() => {
    appMainRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const frame = window.requestAnimationFrame(() => {
      const surface = appMainRef.current?.querySelector<HTMLElement>(
        ".archive-studio, .floorplan-library, .map-workspace, .enterprise-library, .social-hub, .admin-view, .case-detail",
      );
      surface?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [view]);

  async function logout() {
    if (locationDirty && !confirmDiscardLocationChanges()) return;
    setLocationDirty(false);
    try { await api.logout(); } finally { setUser(null); setView("archive"); }
  }

  function navigate(next: View, initialAdminTab: AdminTab = "cases") {
    if (view === "admin" && locationDirty) {
      if (!confirmDiscardLocationChanges()) return;
      setLocationDirty(false);
    }
    if (next === "archive") {
      setArchiveCommunity(undefined);
      setArchiveScope("all");
    }
    if (next === "admin") setAdminInitialTab(initialAdminTab);
    if (next === view) setViewResetKey((value) => value + 1);
    setView(next);
    setMobileNav(false);
    refreshStats();
  }

  function openArchiveCommunity(communityId: number, scope: "all" | "landed") {
    setArchiveCommunity(communityId);
    setArchiveScope(scope);
    setView("archive");
    setMobileNav(false);
    refreshStats();
  }

  const nav = useMemo(
    () => user?.account_type === "restricted"
      ? [
          { id: "archive" as const, label: "案例", icon: Archive },
          { id: "map" as const, label: "地图", icon: MapPinned },
        ]
      : [
          { id: "archive" as const, label: "案例", icon: Archive },
          { id: "materials" as const, label: "素材", icon: Images },
          { id: "enterprise" as const, label: "企业库", icon: BookOpen },
          { id: "floorplans" as const, label: "户型库", icon: FileBox },
          { id: "map" as const, label: "地图", icon: MapPinned },
          { id: "social" as const, label: "社媒", icon: Music2 },
          ...(user?.role === "admin" ? [{ id: "admin" as const, label: "管理", icon: Settings2 }] : []),
        ],
    [user?.account_type, user?.role],
  );

  if (booting) return <div className="app-boot"><img className="brand-logo brand-logo-boot" src={BRAND_LOGO_SRC} alt={BRAND_NAME} /><LoadingState label="正在打开空间美学馆" /></div>;
  if (!user) return <Login onAuthenticated={setUser} />;

  return (
    <div className="app-shell">
      <aside
        ref={mobileRailRef}
        className={`app-rail ${mobileNav ? "open" : ""}`}
        role={mobileNav ? "dialog" : undefined}
        aria-modal={mobileNav || undefined}
        aria-label={mobileNav ? "主导航面板" : undefined}
      >
        <button ref={mobileCloseRef} className="rail-close" type="button" onClick={() => setMobileNav(false)} aria-label="关闭导航">
          <X aria-hidden="true" />
        </button>
        <div className="brand-lockup">
          <img className="brand-logo" src={BRAND_LOGO_SRC} alt="" />
          <span><b>易高</b><small>空间美学馆</small></span>
        </div>
        <nav aria-label="主导航">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <button
                type="button"
                key={item.id}
                className={view === item.id ? "active" : ""}
                aria-current={view === item.id ? "page" : undefined}
                onClick={() => navigate(item.id)}
              >
                <Icon aria-hidden="true" /><span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="rail-user">
          <span className="user-avatar">{user.display_name.slice(0, 1)}</span>
          <span><b>{user.display_name}</b><small>{user.account_type === "admin" ? "管理员" : user.account_type === "restricted" ? "受限员工" : "普通员工"}</small></span>
          <button type="button" onClick={logout} aria-label="退出登录"><LogOut aria-hidden="true" /></button>
        </div>
      </aside>
      {mobileNav && <button className="nav-scrim" type="button" aria-label="关闭导航" onClick={() => setMobileNav(false)} />}
      <main ref={appMainRef} className="app-main" aria-hidden={mobileNav || undefined}>
        <button ref={mobileMenuRef} className="mobile-menu" type="button" onClick={() => setMobileNav(true)} aria-label="打开导航">
          <Menu aria-hidden="true" /> <span>{BRAND_NAME}</span>
        </button>
        {view === "archive" && (
          <ArchiveView
            key={`${archiveCommunity ?? "all"}-${archiveScope}-${viewResetKey}`}
            initialCommunity={archiveCommunity}
            initialScope={archiveScope}
            isAdmin={user.role === "admin"}
            isRestricted={user.account_type === "restricted"}
            stats={stats}
            onStatsChanged={refreshStats}
          />
        )}
        {view === "map" && (
          <MapView
            key={viewResetKey}
            isAdmin={user.role === "admin"}
            isRestricted={user.account_type === "restricted"}
            goAdmin={() => navigate("admin", "locations")}
            openCommunity={openArchiveCommunity}
            stats={stats}
          />
        )}
        {view === "materials" && user.account_type !== "restricted" && (
          <ArchiveView
            key={viewResetKey}
            archiveKind="material"
            isAdmin={user.role === "admin"}
            stats={stats}
            onStatsChanged={refreshStats}
          />
        )}
        {view === "enterprise" && user.account_type !== "restricted" && <EnterpriseLibrary key={viewResetKey} />}
        {view === "floorplans" && user.account_type !== "restricted" && <FloorplanLibrary key={viewResetKey} />}
        {view === "social" && user.account_type !== "restricted" && <Suspense fallback={<LoadingState label="正在打开社媒" />}><DeferredSocialHub key={viewResetKey} /></Suspense>}
        {view === "admin" && user.role === "admin" && (
          <AdminView
            key={viewResetKey}
            initialTab={adminInitialTab}
            currentUserId={user.id}
            locationDirty={locationDirty}
            onLocationDirtyChange={setLocationDirty}
            onStatsChanged={refreshStats}
          />
        )}
      </main>
    </div>
  );
}

export default App;
